// src/navStack.ts
var STACK_KEY = "xone:navStack";
var NAV_DIR_KEY = "xone:navDir";
var MAX_DEPTH = 20;
function generateId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}
function setNavDirection(storage, dir) {
  if (!storage) return;
  try {
    storage.setItem(NAV_DIR_KEY, dir);
  } catch {
  }
}
function consumeNavDirection(storage) {
  if (!storage) return null;
  try {
    const v = storage.getItem(NAV_DIR_KEY);
    if (v) storage.removeItem(NAV_DIR_KEY);
    return v === "forward" || v === "back" ? v : null;
  } catch {
    return null;
  }
}
function createNavStack(storage) {
  function load() {
    if (!storage) return [];
    try {
      const raw = storage.getItem(STACK_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }
  function save(stack) {
    if (!storage) return;
    try {
      storage.setItem(STACK_KEY, JSON.stringify(stack));
    } catch {
      try {
        const last = stack[stack.length - 1];
        storage.setItem(STACK_KEY, JSON.stringify(last ? [last] : []));
      } catch {
      }
    }
  }
  function removeAssociatedState(entry) {
    if (!storage) return;
    try {
      if (entry.objectStateId) storage.removeItem(`xone:obj:${entry.objectStateId}`);
      if (entry.collectionStateId) storage.removeItem(`xone:coll:${entry.collectionStateId}`);
      storage.removeItem(`xone:pcq:${entry.id}`);
    } catch {
    }
  }
  return {
    push(entry) {
      const stack = load();
      while (stack.length >= MAX_DEPTH) {
        const evicted = stack.shift();
        if (evicted) removeAssociatedState(evicted);
      }
      stack.push(entry);
      save(stack);
    },
    pop() {
      const stack = load();
      const entry = stack.pop() ?? null;
      save(stack);
      if (entry) removeAssociatedState(entry);
      return entry;
    },
    peek() {
      const stack = load();
      return stack[stack.length - 1] ?? null;
    },
    peekPrevious() {
      const stack = load();
      return stack.length >= 2 ? stack[stack.length - 2] : null;
    },
    findByCollection(name) {
      const stack = load();
      for (let i = stack.length - 1; i >= 0; i--) {
        if (stack[i].collection === name) return stack[i];
      }
      return null;
    },
    updateTop(patch) {
      const stack = load();
      const top = stack[stack.length - 1];
      if (!top) return;
      if (patch.scrollY !== void 0) top.scrollY = patch.scrollY;
      save(stack);
    },
    truncateToRoot() {
      const stack = load();
      const evicted = stack.splice(1);
      for (const e of evicted) removeAssociatedState(e);
      save(stack);
    },
    clear() {
      const stack = load();
      for (const e of stack) removeAssociatedState(e);
      if (!storage) return;
      try {
        storage.removeItem(STACK_KEY);
        storage.removeItem("xone:macros");
        const toRemove = [];
        for (let i = 0; i < storage.length; i++) {
          const k = storage.key(i);
          if (k && (k.startsWith("xone:obj:") || k.startsWith("xone:coll:") || k.startsWith("xone:pcq:"))) {
            toRemove.push(k);
          }
        }
        for (const k of toRemove) storage.removeItem(k);
      } catch {
      }
    },
    size() {
      return load().length;
    },
    getStack() {
      return load();
    }
  };
}

// src/persistentGlobal.ts
var VARS_NS = "@vars";
function isSerializable(v) {
  if (v === null) return true;
  const t = typeof v;
  if (t === "string" || t === "number" || t === "boolean") return true;
  if (t !== "object") return false;
  try {
    JSON.stringify(v);
    return true;
  } catch {
    return false;
  }
}
function createPersistentGlobal(opts) {
  const { key, storage } = opts;
  function load() {
    if (!storage) return {};
    try {
      const raw = storage.getItem(key);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  let overlay = load();
  const volatile = /* @__PURE__ */ new Map();
  const volatileVars = /* @__PURE__ */ new Map();
  function flush() {
    if (!storage) return;
    let json;
    try {
      json = JSON.stringify(overlay);
    } catch {
      return;
    }
    try {
      storage.setItem(key, json);
    } catch {
      try {
        storage.removeItem(key);
        storage.setItem(key, json);
      } catch {
      }
    }
  }
  function readVar(name) {
    if (volatileVars.has(name)) return volatileVars.get(name);
    const bag = overlay[VARS_NS];
    if (bag && typeof bag === "object" && Object.prototype.hasOwnProperty.call(bag, name)) {
      return bag[name];
    }
    return null;
  }
  function writeVar(name, value) {
    if (isSerializable(value)) {
      volatileVars.delete(name);
      const cur = overlay[VARS_NS];
      const bag = cur && typeof cur === "object" ? cur : {};
      bag[name] = value;
      overlay[VARS_NS] = bag;
      flush();
    } else {
      const cur = overlay[VARS_NS];
      if (cur && typeof cur === "object" && Object.prototype.hasOwnProperty.call(cur, name)) {
        delete cur[name];
        flush();
      }
      volatileVars.set(name, value);
    }
  }
  const methods = {
    getVariable: (name) => readVar(name),
    setVariable: (name, value) => writeVar(name, value),
    getVariables: (name) => readVar(name),
    setVariables: (name, value) => writeVar(name, value)
  };
  const methodNames = new Set(Object.keys(methods));
  const isDataKey = (p) => p !== VARS_NS && Object.prototype.hasOwnProperty.call(overlay, p);
  function resolveRead(p) {
    if (volatile.has(p)) return volatile.get(p);
    if (isDataKey(p)) return overlay[p];
    const b = opts.base();
    return p in b ? b[p] : void 0;
  }
  const target = {};
  for (const [name, fn] of Object.entries(methods)) {
    Object.defineProperty(target, name, {
      value: fn,
      enumerable: false,
      writable: false,
      configurable: true
    });
  }
  const proxy = new Proxy(target, {
    get(t, p, r) {
      if (typeof p === "symbol") return Reflect.get(t, p, r);
      if (p in t) return Reflect.get(t, p, r);
      return resolveRead(p);
    },
    set(t, p, v) {
      if (typeof p === "symbol") return Reflect.set(t, p, v);
      if (methodNames.has(p)) return true;
      if (isSerializable(v)) {
        volatile.delete(p);
        overlay[p] = v;
        flush();
      } else {
        if (Object.prototype.hasOwnProperty.call(overlay, p)) {
          delete overlay[p];
          flush();
        }
        volatile.set(p, v);
      }
      return true;
    },
    deleteProperty(t, p) {
      if (typeof p === "symbol") return Reflect.deleteProperty(t, p);
      volatile.delete(p);
      if (isDataKey(p)) {
        delete overlay[p];
        flush();
      }
      return true;
    },
    has(t, p) {
      if (typeof p === "symbol") return Reflect.has(t, p);
      return p in t || volatile.has(p) || isDataKey(p) || p in opts.base();
    },
    ownKeys(t) {
      const keys = new Set(Reflect.ownKeys(t));
      for (const k of Object.keys(overlay)) if (k !== VARS_NS) keys.add(k);
      for (const k of volatile.keys()) keys.add(k);
      for (const k of Object.keys(opts.base())) keys.add(k);
      return [...keys];
    },
    getOwnPropertyDescriptor(t, p) {
      if (typeof p !== "symbol" && !(p in t) && (volatile.has(p) || isDataKey(p) || p in opts.base())) {
        return { enumerable: true, configurable: true, writable: true, value: resolveRead(p) };
      }
      return Reflect.getOwnPropertyDescriptor(t, p);
    }
  });
  return {
    proxy,
    clear() {
      overlay = {};
      volatile.clear();
      volatileVars.clear();
      if (storage) {
        try {
          storage.removeItem(key);
        } catch {
        }
      }
    }
  };
}

// src/pickFile.ts
function isObj(v) {
  return typeof v === "object" && v !== null;
}
function buildAccept(params) {
  if (typeof params === "string") return params.trim();
  if (!isObj(params)) return "";
  if (typeof params["accept"] === "string" && params["accept"].trim()) {
    return params["accept"].trim();
  }
  const ft = params["fileTypes"];
  if (typeof ft === "string" && ft.trim()) {
    return ft.split(",").map((s) => s.trim()).filter(Boolean).map((ext) => ext.startsWith(".") || ext.includes("/") ? ext : "." + ext).join(",");
  }
  return "";
}
function pickFileWeb(params) {
  if (typeof document === "undefined") return Promise.resolve(null);
  const accept = buildAccept(params);
  const multiple = isObj(params) && params["multiple"] === true;
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    if (accept) input.accept = accept;
    if (multiple) input.multiple = true;
    input.style.position = "fixed";
    input.style.left = "-9999px";
    input.style.top = "-9999px";
    let settled = false;
    const done = (file) => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(file);
    };
    input.addEventListener("change", () => done(input.files?.[0] ?? null), {
      once: true
    });
    input.addEventListener("cancel", () => done(null), { once: true });
    document.body.appendChild(input);
    input.click();
  });
}

// src/downloadGuard.ts
var DEDUP_WINDOW_MS = 700;
var installed = false;
function installDownloadDedup() {
  if (installed) return;
  try {
    let guardedClick2 = function(...args) {
      try {
        const download = this.download;
        if (download) {
          const key = download + "|" + (this.href || "");
          const now = Date.now();
          const prev = lastByKey.get(key);
          if (prev !== void 0 && now - prev < DEDUP_WINDOW_MS) {
            try {
              g["console"]?.warn?.(
                "[xone-download] descarga duplicada suprimida (WEAL-213): dos <a download> apuntando al mismo fichero se clicaron dentro de " + DEDUP_WINDOW_MS + "ms. Causa probable: un handler de proyecto dispar\xF3 a la vez su propio <a download> y ui.downloadFile() (ver openURL en documentos_functions.js).",
                { key }
              );
            } catch {
            }
            return;
          }
          lastByKey.set(key, now);
          if (lastByKey.size > 64) {
            for (const [k, t] of lastByKey) {
              if (now - t >= DEDUP_WINDOW_MS) lastByKey.delete(k);
            }
          }
        }
      } catch {
      }
      return nativeClick.apply(this, args);
    };
    var guardedClick = guardedClick2;
    if (typeof HTMLAnchorElement === "undefined" || !HTMLAnchorElement.prototype) return;
    const g = typeof globalThis !== "undefined" ? globalThis : {};
    if (g.__xoneDownloadDedupInstalled === true) {
      installed = true;
      return;
    }
    const proto = HTMLAnchorElement.prototype;
    const nativeClick = proto.click;
    if (typeof nativeClick !== "function") return;
    const lastByKey = /* @__PURE__ */ new Map();
    proto.click = guardedClick2;
    g.__xoneDownloadDedupInstalled = true;
    installed = true;
  } catch {
  }
}

// src/ui.ts
import { whenWritesIdle } from "@xone-web/data";
var navigating = false;
var navHealTimer = null;
var NAV_HEAL_MS = 1500;
function clearNavHeal() {
  if (navHealTimer !== null) {
    clearTimeout(navHealTimer);
    navHealTimer = null;
  }
}
function resetNavGuard() {
  navigating = false;
  clearNavHeal();
}
function consumeInRowEdit() {
  const g = globalThis;
  const entry = g.__xoneInRowEdit?.pop();
  if (!entry) return false;
  try {
    entry.collapse();
  } catch {
  }
  return true;
}
function beginNav() {
  if (navigating) return false;
  navigating = true;
  clearNavHeal();
  if (typeof window !== "undefined") {
    navHealTimer = setTimeout(() => {
      navigating = false;
      navHealTimer = null;
    }, NAV_HEAL_MS);
  }
  return true;
}
function drawerHandle(id) {
  if (typeof document === "undefined") return null;
  const host = document.querySelector(
    `.xone-drawer-host[data-group-id="${id}"]`
  );
  if (!host) return null;
  const h = host["__xoneDrawer"];
  return h && typeof h === "object" ? h : null;
}
function createUi(opts) {
  installDownloadDedup();
  function urlFor(name) {
    const direct = opts.routeMap.get(name);
    if (direct) return direct;
    const slug = slugify(name);
    return `/${slug}/`;
  }
  function snapshotCurrentState() {
    if (!opts.navStack || !opts.stateStore) return;
    const current = opts.navStack.peek();
    if (!current) return;
    const scrollY = typeof window !== "undefined" ? window.scrollY : 0;
    opts.navStack.updateTop({ scrollY });
    if (current.objectStateId && opts.snapshotSelf) {
      const objState = opts.snapshotSelf();
      if (objState) opts.stateStore.saveObjectState(current.objectStateId, objState);
    }
    if (current.collectionStateId && opts.snapshotCollection) {
      const collState = opts.snapshotCollection();
      if (collState) opts.stateStore.saveCollectionState(current.collectionStateId, collState);
    }
    opts.stateStore.saveMacros(opts.macros.snapshot());
  }
  function pushAndNavigate(collection, url, params, extra) {
    const entry = {
      id: generateId(),
      collection,
      url,
      params,
      parentEntryId: extra?.parentEntryId ?? null,
      contentName: extra?.contentName ?? null,
      objectStateId: extra?.objectStateId ?? generateId(),
      collectionStateId: generateId(),
      isNewObject: extra?.isNewObject ?? false,
      scrollY: 0,
      timestamp: Date.now(),
      ...extra?.rootSwitch ? { rootSwitch: true } : {}
    };
    opts.navStack?.push(entry);
    setNavDirection(opts.storage ?? null, "forward");
    navigateWithViewTransition(url);
  }
  function isLiveParentObject(v) {
    return typeof v === "object" && v !== null && !Array.isArray(v) && typeof v["getOwnerCollection"] === "function" && typeof v["toJson"] === "function";
  }
  function dehydrateParent(v, depth) {
    const oc = v["getOwnerCollection"]();
    const name = oc && typeof oc.getName === "function" ? oc.getName() : "";
    const inner = v["toJson"]();
    const fields = depth > 0 ? dehydrateParentFields(inner, depth - 1) : { ...inner };
    return { ...fields, _owner: { _name: name } };
  }
  function dehydrateParentFields(fields, depth = 4) {
    let out = null;
    for (const [k, v] of Object.entries(fields)) {
      if (isLiveParentObject(v)) {
        (out ??= { ...fields })[k] = dehydrateParent(v, depth);
      }
    }
    return out ?? fields;
  }
  function serializeDataObject(obj) {
    if (!opts.stateStore) return null;
    const id = generateId();
    const fields = dehydrateParentFields(
      typeof obj.toJson === "function" ? obj.toJson() : {}
    );
    const isNew = typeof obj.isNew === "function" ? obj.isNew() : true;
    const owner = typeof obj.getOwnerCollection === "function" ? obj.getOwnerCollection() : null;
    const collName = owner && typeof owner.getName === "function" ? owner.getName() : "";
    const seededFn = obj._unassignedTypeSeeded;
    const typeSeeded = isNew && typeof seededFn === "function" ? seededFn.call(obj) : [];
    const state = {
      collectionName: collName,
      fields,
      dirtyFields: [],
      isNew,
      index: 0,
      ...typeSeeded.length > 0 ? { typeSeeded } : {}
    };
    opts.stateStore.saveObjectState(id, state);
    return id;
  }
  function refreshNamed(namesCsv) {
    if (!namesCsv) return;
    const esc = (s) => typeof CSS !== "undefined" && CSS.escape ? CSS.escape(s) : s.replace(/"/g, '\\"');
    for (const raw of namesCsv.split(",")) {
      const name = raw.trim();
      if (!name) continue;
      if (typeof document !== "undefined") {
        const seen = /* @__PURE__ */ new Set();
        for (const sel of [
          `xone-island[data-contents-name="${esc(name)}"]`,
          `xone-island[data-contents-name="@${esc(name)}"]`,
          `xone-island[data-prop="${esc(name)}"]`
        ]) {
          for (const el of document.querySelectorAll(sel)) {
            if (seen.has(el)) continue;
            seen.add(el);
            const island = el;
            if (typeof island.rerender === "function") island.rerender();
            else if (typeof island.refresh === "function") island.refresh();
          }
        }
      }
      opts.domSync?.syncField(name);
    }
  }
  const _delayTimers = /* @__PURE__ */ new Set();
  if (typeof window !== "undefined") {
    window.addEventListener("pagehide", () => {
      for (const id of _delayTimers) clearTimeout(id);
      _delayTimers.clear();
    });
  }
  function backWithFallback() {
    setNavDirection(opts.storage ?? null, "back");
    const prevUrl = opts.navStack?.peekPrevious()?.url ?? null;
    void whenWritesIdle().then(() => {
      markProgrammaticNavigation();
      const hrefBefore = typeof window !== "undefined" ? window.location.href : "";
      history.back();
      if (typeof window !== "undefined" && prevUrl) {
        window.setTimeout(() => {
          if (window.location.href === hrefBefore) {
            setNavDirection(opts.storage ?? null, "back");
            markProgrammaticNavigation();
            window.location.assign(prevUrl);
          }
        }, 450);
      }
    });
  }
  function findContentsIsland(name) {
    if (typeof document === "undefined" || !name) return null;
    const esc = (s) => typeof CSS !== "undefined" && CSS.escape ? CSS.escape(s) : s.replace(/"/g, '\\"');
    const n = esc(String(name));
    return document.querySelector(`xone-island[data-prop="${n}"]`) ?? document.querySelector(`xone-island[data-contents-name="${n}"]`) ?? document.querySelector(`xone-island[data-contents-name="@${n}"]`);
  }
  return {
    openCollection(name, params, navOpts) {
      if (!beginNav()) return;
      snapshotCurrentState();
      if (navOpts?.rootSwitch) opts.navStack?.truncateToRoot();
      const strParams = {};
      if (params) {
        for (const [k, v] of Object.entries(params)) {
          if (v != null) strParams[k] = String(v);
        }
      }
      const url = withQuery(urlFor(name), strParams);
      pushAndNavigate(name, url, strParams, navOpts?.rootSwitch ? { rootSwitch: true } : void 0);
    },
    back() {
      if (opts.isSectionRoot?.() && opts.onRootBack) {
        opts.onRootBack();
        return;
      }
      if (!beginNav()) return;
      snapshotCurrentState();
      backWithFallback();
    },
    // #367 — "atrás" de N saltos, para el rastro de `breadcrumbview`.
    //
    // Existe porque la alternativa (que el rastro hiciera
    // `window.location.href = url` por su cuenta) se salta TRES cosas que
    // toda navegación de XOne respeta, y las tres fallan en silencio:
    //   · la dirección `back` → el destino entraría por la rama "sin
    //     dirección" de createAppContext y EMPUJARÍA una entrada nueva: el
    //     rastro A/B/C pasaría a A/B/C/A, creciendo en vez de acortarse, y
    //     a los 20 saltos (MAX_DEPTH) evicta las entradas raíz;
    //   · la marca de navegación programática → saldría el diálogo nativo
    //     "¿salir sin guardar?" con el formulario sucio, que además deja la
    //     página destino con el rendering congelado bajo View Transition
    //     cross-document (ver markProgrammaticNavigation);
    //   · la barrera de escrituras → aterrizarías en una lista que
    //     re-consulta el backend con los datos de ANTES del save.
    navigateToNavEntry(entryId) {
      const stack = opts.navStack?.getStack() ?? [];
      const idx = stack.findIndex((e) => e.id === entryId);
      if (idx < 0 || idx >= stack.length - 1) return;
      const destino = stack[idx];
      if (!destino || !destino.url) return;
      if (!beginNav()) return;
      opts.writeExitMarker?.();
      void whenWritesIdle().then(() => {
        const sigueViva = (opts.navStack?.getStack() ?? []).some((e) => e.id === entryId);
        if (sigueViva) {
          for (let i = 0; i < 64; i++) {
            const top = opts.navStack?.peek();
            if (!top || top.id === entryId) break;
            opts.navStack?.pop();
          }
        }
        setNavDirection(opts.storage ?? null, "back");
        markProgrammaticNavigation();
        window.location.assign(destino.url);
      });
    },
    showToast(msg) {
      if (typeof msg === "object" && msg !== null) {
        showToast(opts.macros.expand(msg.text ?? ""), {
          bgColor: msg.color,
          textColor: msg.textColor,
          rounded: msg.rounded
        });
      } else {
        showToast(opts.macros.expand(msg));
      }
    },
    async showDialog(o) {
      const message = typeof o === "string" ? o : o.message ?? "";
      const title = typeof o === "string" ? void 0 : o.title;
      await openNativeDialog({ title, message: opts.macros.expand(message) });
    },
    async confirm(msg) {
      return openNativeConfirm(opts.macros.expand(msg));
    },
    async prompt(msg, defaultValue) {
      return Promise.resolve(window.prompt(opts.macros.expand(msg), defaultValue ?? ""));
    },
    showWaitDialog(msg) {
      if (typeof document === "undefined") return;
      let overlay = document.querySelector("[data-xone-wait]");
      if (!overlay) {
        overlay = document.createElement("div");
        overlay.setAttribute("data-xone-wait", "");
        overlay.style.cssText = [
          "position:fixed",
          "inset:0",
          "z-index:10000",
          "display:flex",
          "align-items:center",
          "justify-content:center",
          "background:rgba(0,0,0,0.3)"
        ].join(";");
        const inner2 = document.createElement("div");
        inner2.style.cssText = "background:#fff;padding:16px 24px;border-radius:8px;font-size:14px;box-shadow:0 4px 16px rgba(0,0,0,0.2);";
        overlay.appendChild(inner2);
        document.body.appendChild(overlay);
      }
      const inner = overlay.firstElementChild;
      if (inner) inner.textContent = opts.macros.expand(msg ?? "Cargando...");
      overlay.style.display = "flex";
    },
    hideWaitDialog() {
      if (typeof document === "undefined") return;
      const overlay = document.querySelector("[data-xone-wait]");
      if (overlay) overlay.style.display = "none";
    },
    refreshView() {
      opts.domSync?.syncAll();
    },
    refresh(name) {
      if (name !== void 0 && name !== "") {
        refreshNamed(name);
      } else {
        opts.domSync?.syncAll();
      }
    },
    refreshValue(field) {
      opts.domSync?.syncField(field);
    },
    // Paridad Android `XoneBaseActivity.refreshContentRow(name, row)`
    // (XoneBaseActivity.java:4091 → `listView.refreshItem(nRow)`): re-renderiza
    // la fila `index` del contents `propName` desde su DataObject (ya mutado por
    // el script, p.ej. `item.MAP_BLOQUEO=1`). El MPA no tiene refresco por-fila;
    // re-renderiza el contents ENTERO desde la colección compartida ya cargada
    // (superset visualmente equivalente: refleja el valor mutado del item en su
    // fila, re-evaluando disablevisible/colores/locked). El índice se acepta por
    // firma pero no se usa. Soporta el prefijo multinivel `A->B` (toma `A`),
    // como Android. Sin esto, el proyecto Alivia rompía al pulsar "Mostrar
    // calculadora" (LeadEdicion, pestaña Deudas y Planes) con
    // `ui.refreshContentRow is not a function` → el bucle abortaba antes de
    // `CalculadoraObtenerCuotas`.
    refreshContentRow(propName, _index) {
      const name = String(propName ?? "");
      const base = name.indexOf("->") > 0 ? name.split("->")[0] : name;
      if (base) refreshNamed(base);
    },
    // Paridad Android `refreshContentSelectedRow(name)`: en el MPA no hay una
    // "fila seleccionada" persistente que refrescar aisladamente → re-render del
    // contents entero (mismo criterio que `refreshContentRow`).
    refreshContentSelectedRow(propName) {
      const name = String(propName ?? "");
      const base = name.indexOf("->") > 0 ? name.split("->")[0] : name;
      if (base) refreshNamed(base);
    },
    // Paridad Android `XoneBaseActivity.appendContentRows(name)`: inserta en el
    // adapter las filas NUEVAS ya añadidas a la colección por un `addItem` puntual
    // (sin recargar todo el listado, para evitar parpadeo). El MPA no tiene append
    // incremental real → re-render del contents ENTERO desde la colección compartida
    // (mismo criterio que `refreshContentRow`): como el proyecto llama a
    // `ui.appendContentRows` DESPUÉS de `cntMsj.addItem(obj)`, el re-render pinta las
    // filas nuevas desde los items YA en memoria (sin round-trip). Soporta el prefijo
    // multinivel `A->B` (toma `A`), como Android. Sin esto, el proyecto Alivia rompía
    // al enviar un mensaje de chat (`mensajes_functions.js::agregarMensajesNuevos`)
    // con `ui.appendContentRows is not a function` → el mensaje quedaba en memoria y no
    // se pintaba hasta un refresco completo (recargar, cambiar de conversación…).
    appendContentRows(propName) {
      const name = String(propName ?? "");
      const base = name.indexOf("->") > 0 ? name.split("->")[0] : name;
      if (base) refreshNamed(base);
    },
    // WEAL-142 — registra la acción de proyecto que dispara la carga de filas
    // más antiguas cuando el content fluid-load llega al top. Delega en el
    // `<xone-island>` (que posee el contenedor de scroll y su ventana de
    // paginación). El callback ejecuta el nodo de proyecto vía `__xoneRunNode`
    // (mismo puente que `executeActionAfterDelay`), que fija el `self` global.
    setContentsOnReachTop(name, callbackNode) {
      const island = findContentsIsland(name);
      if (!island) return;
      const cb = String(callbackNode ?? "").trim();
      island.setOnReachTop?.(cb === "" ? null : () => {
        try {
          window.__xoneRunNode?.(cb);
        } catch {
        }
      });
    },
    // WEAL-142 — carga la página anterior (más antigua) y la prepende.
    async loadContentsOlderRows(name) {
      const island = findContentsIsland(name);
      if (!island) return;
      await island.loadOlderRows?.();
    },
    /**
     * `ui.executeActionAfterDelay("<nodo>", <segundos>)` — ejecuta un NODO
     * (acción) tras un retardo. Paridad Android
     * `XoneBaseActivity.executeActionAfterDelay` (`EditViewExecuteNode`): el
     * 2º parámetro va en SEGUNDOS (double → `*1000` ms) y el nodo corre sobre
     * el objeto actual. Es el `setTimeout` idiomático de XOne (chat: scroll,
     * búsqueda, precarga, refresco). La ejecución del nodo la hace el runtime
     * de islas (`window.__xoneRunNode`, resuelve el handler y fija el `self`
     * global); leemos el puente en el disparo, no al programar.
     */
    executeActionAfterDelay(nodeName, delaySeconds) {
      if (typeof window === "undefined") return;
      const name = String(nodeName ?? "").trim();
      if (name === "") return;
      const ms = Math.max(0, Number(delaySeconds) * 1e3);
      const id = window.setTimeout(() => {
        _delayTimers.delete(id);
        try {
          window.__xoneRunNode?.(name);
        } catch {
        }
      }, ms);
      _delayTimers.add(id);
    },
    /**
     * `ui.pickFile(params)` — abre el selector de fichero del sistema y
     * resuelve con el `File` elegido (o `null` si se cancela). Paridad con el
     * objeto de script XOne (Android `XoneGlobalUI.pickFile`). Asíncrono; el
     * transpilador inserta `await` (pickFile ∈ ASYNC_API_METHODS). El `File`
     * devuelto es compatible con `FormData.append` para la subida posterior.
     * Ver `pickFile.ts` para las formas de `params` aceptadas.
     */
    pickFile(params) {
      return pickFileWeb(params);
    },
    /**
     * `ui.sleep(seconds)` — pausa `seconds` SEGUNDOS (paridad Android
     * `XoneGlobalUI.sleep`: `TimeUnit.MILLISECONDS.sleep(nSeconds*1000)`,
     * mismas unidades que `executeActionAfterDelay`). Asíncrono (Promise);
     * en XOne se usa además para marcar como async una función que envuelve
     * trabajo diferido (p.ej. el subidor `sendOnlineFilesApiNEW` hace
     * `ui.sleep(0)` — 0 s indistinguible de 0 ms).
     */
    sleep(seconds) {
      const ms = Math.max(0, (Number(seconds) || 0) * 1e3);
      return new Promise((resolve) => {
        if (typeof window !== "undefined") window.setTimeout(resolve, ms);
        else resolve();
      });
    },
    // ── API de grupos sobre el modelo de pestañas (F2 §4.5) + drawers (F4) ──
    showGroup(id) {
      if (typeof document === "undefined") return;
      const sid = String(id);
      const drawer = drawerHandle(sid);
      if (drawer) {
        drawer.open();
        return;
      }
      const panel = document.querySelector(
        `.xone-tabs-chassis .xone-group[data-group-id="${sid}"]`
      ) ?? document.querySelector(
        `.xone-group[data-group-id="${sid}"]`
      );
      const chassis = panel?.closest(".xone-tabs-chassis");
      const activate = chassis?.["__xoneActivateGroup"];
      if (typeof activate === "function") {
        activate(sid);
        return;
      }
      if (panel) panel.style.display = "";
    },
    getActiveGroup() {
      if (typeof document === "undefined") return null;
      const vis = document.querySelector(
        '.xone-groups > .xone-group[role="tabpanel"]:not([hidden])'
      );
      return vis?.getAttribute("data-group-id") ?? null;
    },
    isGroupActive(id) {
      if (typeof document === "undefined") return false;
      const vis = document.querySelector(
        '.xone-groups > .xone-group[role="tabpanel"]:not([hidden])'
      );
      return vis?.getAttribute("data-group-id") === String(id);
    },
    /**
     * `disablevisible` (paridad `refreshGroupVisibility`): saca/mete un grupo
     * normal y su pestaña del juego. Si se oculta el grupo activo, recoloca
     * en el siguiente/anterior visible. Si quedan <2 pestañas visibles,
     * oculta la barra (regla R1 dinámica).
     */
    setGroupVisible(id, visible) {
      if (typeof document === "undefined") return;
      const sid = String(id);
      const panel = document.querySelector(
        `.xone-group[data-group-id="${sid}"]`
      );
      if (!panel) return;
      const chassis = panel.closest(".xone-tabs-chassis");
      const tab = chassis?.querySelector(
        `[role="tab"][data-group-id="${sid}"]`
      );
      if (!chassis || !tab) {
        panel.style.display = visible ? "" : "none";
        return;
      }
      tab.hidden = !visible;
      if (!visible) {
        const wasActive = tab.getAttribute("aria-selected") === "true";
        if (wasActive) {
          const tabs = Array.from(
            chassis.querySelectorAll('[role="tab"]:not([hidden])')
          );
          const next = tabs.find((t) => t.dataset["groupId"] !== sid);
          const activate = chassis["__xoneActivateGroup"];
          if (next && typeof activate === "function") {
            activate(next.dataset["groupId"]);
          } else {
            panel.hidden = true;
          }
        }
      } else {
        tab.hidden = false;
      }
      const bar = chassis.querySelector('[role="tablist"]');
      if (bar) {
        const visibleTabs = bar.querySelectorAll('[role="tab"]:not([hidden])').length;
        bar.hidden = visibleTabs < 2;
      }
    },
    hideGroup(id) {
      const drawer = drawerHandle(String(id));
      if (drawer) {
        drawer.close();
        return;
      }
      this.setGroupVisible(id, false);
    },
    // ── API de drawers (F4 §4.3, paridad doc 01 §2.4.2) ──
    toggleGroup(id) {
      drawerHandle(String(id))?.toggle();
    },
    lockGroup(id, close) {
      drawerHandle(String(id))?.lock(close);
    },
    unlockGroup(id) {
      drawerHandle(String(id))?.unlock();
    },
    isGroupOpen(id) {
      return drawerHandle(String(id))?.isOpen() ?? false;
    },
    getView(_self) {
      return {
        exit() {
          if (consumeInRowEdit()) return;
          if (!beginNav()) return;
          opts.writeExitMarker?.();
          snapshotCurrentState();
          backWithFallback();
        },
        refresh(...fields) {
          if (fields.length === 0) {
            opts.domSync?.syncAll();
          } else {
            for (const f of fields) refreshNamed(f);
          }
        },
        /**
         * Paridad Android `IXoneView.getControl(name)` (MVP): localiza
         * el control por nombre de prop o alias del contents y expone
         * `refresh()`. Para islands de contents delega en el
         * `refresh()` del custom element (re-carga con el filtro
         * runtime — patrón mRed FiltrarListado). Devuelve null si no
         * existe (el script XOne canónico hace `if (control)`).
         */
        getControl(name) {
          if (typeof document === "undefined" || !name) return null;
          const esc = (s) => typeof CSS !== "undefined" && CSS.escape ? CSS.escape(s) : s.replace(/"/g, '\\"');
          const el = document.querySelector(`xone-island[data-prop="${esc(name)}"]`) ?? document.querySelector(`xone-island[data-contents-name="${esc(name)}"]`) ?? document.querySelector(`xone-island[data-contents-name="@${esc(name)}"]`) ?? document.querySelector(`[data-prop="${esc(name)}"]`);
          if (!el) return null;
          return {
            // Paridad Android `IXoneView.getControl(name).getContentView()`:
            // el elemento raíz del control (el `<xone-island>`). El chat lo
            // usa para `element.querySelector('.xone-contents.xone-reverse-column')`.
            element: el,
            refresh() {
              const island = el;
              if (typeof island.refresh === "function") {
                island.refresh();
              } else {
                opts.domSync?.syncField(name);
              }
            },
            // WEAL-142 — scroll al fondo (chat/start-from-bottom). Delega
            // en el island (que conoce su contenedor de scroll interno).
            scrollToBottom() {
              const island = el;
              if (typeof island.scrollToBottom === "function") {
                island.scrollToBottom();
              } else {
                const sc = el.querySelector(".xone-contents");
                if (sc) sc.scrollTop = sc.scrollHeight;
              }
            },
            // WEAL-622 — paridad Android `XOneContentRecyclerView.
            // setSelectedItem(int)` / `clearSelectedItem()` (@ScriptAllowed):
            // marca la fila seleccionada del contents SIN disparar
            // `<selecteditem>`. Delega en el island; no-op si no es contents.
            setSelectedItem(position) {
              const island = el;
              if (typeof island.setSelectedItem === "function") {
                island.setSelectedItem(Number(position));
              }
            },
            clearSelectedItem() {
              const island = el;
              if (typeof island.clearSelectedItem === "function") {
                island.clearSelectedItem();
              }
            }
          };
        }
      };
    },
    // `ui.msgBox(mensaje, titulo, nOptions)` — paridad Android
    // `XoneGlobalUI.msgBox` → `MessageBoxDialog`. `nOptions` selecciona el
    // juego de botones (0=Aceptar, 1=Aceptar+Cancelar, 3=Sí+No+Cancelar,
    // 4=Sí+No, 5=Cancelar) y devuelve el `tag` del pulsado (1/2/6/7; 0 si se
    // cierra con Esc). Antes solo distinguía `buttons===4`; el resto caía a un
    // único "OK" → los confirm de `nOptions=1` no funcionaban.
    async msgBox(message, title, buttons) {
      return openMessageBox({
        title,
        message: opts.macros.expand(message),
        nOptions: safeOptions(buttons)
      });
    },
    // Igual que `msgBox` pero con sonido/vibración en Android. En el canal web
    // no hay reproducción de sonido del sistema para el diálogo (best-effort:
    // se ignoran `sound/vibrate/repeat`); los botones y el retorno son idénticos.
    async msgBoxWithSound(message, title, buttons, _sound, _vibrate, _repeat) {
      return openMessageBox({
        title,
        message: opts.macros.expand(message),
        nOptions: safeOptions(buttons)
      });
    },
    openMenu(name, _self) {
      if (!beginNav()) return;
      snapshotCurrentState();
      opts.navStack?.truncateToRoot();
      const url = withQuery(urlFor(name));
      pushAndNavigate(name, url, {}, { rootSwitch: true });
    },
    /**
     * ¿`collName` tiene página de edición generada? Consulta DIRECTA al route
     * map — deliberadamente NO usa `urlFor`, que cae a un slug adivinado
     * (`/${slug}/`) y por tanto devolvería una URL para CUALQUIER nombre,
     * inservible como gate. El mapa se puebla desde `manifest.routes`, que
     * lista solo las páginas realmente emitidas por el generador.
     */
    hasEditView(collName) {
      if (typeof collName !== "string" || collName === "") return false;
      return opts.routeMap.has(collName);
    },
    openEditView(target, navOpts) {
      if (!beginNav()) return;
      snapshotCurrentState();
      const rootSwitch = navOpts?.rootSwitch === true;
      if (rootSwitch) opts.navStack?.truncateToRoot();
      if (typeof target === "string") {
        const url = withQuery(urlFor(target));
        pushAndNavigate(target, url, {}, rootSwitch ? { rootSwitch: true } : void 0);
        return;
      }
      const resolve = (obj) => {
        if (!obj || typeof obj !== "object") {
          resetNavGuard();
          return;
        }
        const o = obj;
        const owner = typeof o.getOwnerCollection === "function" ? o.getOwnerCollection() : o.ownerCollection ?? o._owner;
        const collName = (typeof owner?.getName === "function" ? owner.getName() : void 0) ?? (typeof owner?.name === "string" ? owner.name : void 0);
        if (typeof collName !== "string" || collName === "") {
          resetNavGuard();
          return;
        }
        const objectStateId = serializeDataObject(o);
        const url = withQuery(urlFor(collName));
        const parentEntryId = rootSwitch ? null : opts.navStack?.peek()?.id ?? null;
        let ownerObj = null;
        try {
          ownerObj = typeof owner?.getOwnerObject === "function" ? owner.getOwnerObject.call(owner) : null;
        } catch {
          ownerObj = null;
        }
        const pageSelf = opts.getPageSelf?.();
        let isNewObj = false;
        try {
          isNewObj = typeof o.isNew === "function" ? Boolean(o.isNew.call(o)) : false;
        } catch {
          isNewObj = false;
        }
        const fromPageContents = !!pageSelf && ownerObj === pageSelf && o !== pageSelf && !isNewObj;
        const contentName = !rootSwitch && parentEntryId && fromPageContents ? collName : null;
        pushAndNavigate(collName, url, {}, { objectStateId, parentEntryId, ...contentName ? { contentName } : {}, ...rootSwitch ? { rootSwitch: true } : {} });
      };
      if (target && typeof target.then === "function") {
        void target.then(resolve).catch(() => {
          resetNavGuard();
        });
      } else {
        resolve(target);
      }
    },
    openURL(url) {
      if (typeof window !== "undefined") window.open(url, "_blank");
    },
    // Alias en minúsculas: el proyecto Alivia llama `ui.openUrl(...)` (p.ej. tras
    // `$http.getFile` para abrir un PDF/blob). El runtime exponía solo `openURL`
    // (mayúsculas) → "openUrl is not a function". Mismo comportamiento.
    openUrl(url) {
      if (typeof window !== "undefined") window.open(url, "_blank");
    },
    // WEAL-151 — descarga `url` (object-URL blob: o URL directa) forzando `filename`
    // como nombre de fichero. Reproduce el patrón probado de `addCalendarItem`
    // (xone-ui/system.ts): crea `<a>`, fija `.download`/`.href`, LO AÑADE al DOM
    // (crítico: un `<a download>` desconectado NO honra el atributo en varios
    // navegadores → el UUID del blob se cuela como nombre), hace click y limpia
    // (removeChild + revokeObjectURL de object-URLs propios) en un setTimeout.
    // Complementa al 2º argumento `filename` que ahora entrega `$http.getFile`
    // (Content-Disposition), para que "ver documento" descargue con el nombre real
    // en vez del UUID. Para abrir inline (PDF) sigue usándose `openUrl`.
    downloadFile(url, filename) {
      if (typeof window === "undefined" || typeof document === "undefined") return;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        try {
          document.body.removeChild(a);
        } catch {
        }
        if (typeof url === "string" && url.startsWith("blob:") && typeof URL !== "undefined" && typeof URL.revokeObjectURL === "function") {
          try {
            URL.revokeObjectURL(url);
          } catch {
          }
        }
      }, 0);
    },
    isNetworkAvailable() {
      return typeof navigator !== "undefined" ? navigator.onLine : true;
    },
    getOrientation() {
      if (typeof screen !== "undefined" && screen.orientation) {
        return screen.orientation.type.includes("portrait") ? "portrait" : "landscape";
      }
      return "portrait";
    },
    getScreenWidth() {
      return typeof screen !== "undefined" ? screen.width : 0;
    },
    getScreenHeight() {
      return typeof screen !== "undefined" ? screen.height : 0;
    },
    // ---- Sesión por inactividad (web-only) ----
    // `ui.setSessionExpired(timeoutMs, callback)` — función EXCLUSIVA del
    // canal web (no existe en el `ui` de Android; el equivalente Android es
    // `ui.setInactivityTimer({timeout, callback})`). Implementación fiel al
    // sitio de referencia XOne (app.js): idle-timer que, ante CUALQUIER
    // actividad del usuario (mousedown/mousemove/keypress/scroll/touchstart)
    // y el `load` inicial, reinicia un `setTimeout(callback, timeoutMs)`. Si
    // el usuario permanece inactivo `timeoutMs`, se dispara el callback
    // (típicamente `logout`). Llamar con `callback` falsy cancela el timer.
    // Ref: web.xoneideintcore.com app.js (método setSessionExpired).
    setSessionExpired(timeoutMs, callback) {
      if (typeof window === "undefined") return;
      const w = window;
      const reset = () => {
        if (w.__xoneSessionTimerId !== void 0) {
          window.clearTimeout(w.__xoneSessionTimerId);
        }
        w.__xoneSessionTimerId = window.setTimeout(() => {
          try {
            if (typeof callback === "function") callback();
          } catch (err) {
            console.error("[xone] setSessionExpired callback error:", err);
          }
        }, timeoutMs);
      };
      const ACTIVITY = ["mousedown", "mousemove", "keypress", "scroll", "touchstart"];
      if (w.__xoneSessionReset) {
        window.removeEventListener("load", w.__xoneSessionReset, true);
        for (const ev of ACTIVITY) document.removeEventListener(ev, w.__xoneSessionReset, true);
        w.__xoneSessionReset = void 0;
      }
      if (callback) {
        w.__xoneSessionReset = reset;
        window.addEventListener("load", reset, true);
        for (const ev of ACTIVITY) document.addEventListener(ev, reset, true);
        reset();
      } else if (w.__xoneSessionTimerId !== void 0) {
        window.clearTimeout(w.__xoneSessionTimerId);
        w.__xoneSessionTimerId = void 0;
      }
    }
  };
}
function navigateWithViewTransition(url) {
  if (typeof document === "undefined") return;
  void whenWritesIdle().then(() => {
    markProgrammaticNavigation();
    window.location.href = url;
  });
}
function markProgrammaticNavigation() {
  try {
    window.__xoneProgrammaticNav = true;
    window.setTimeout(() => {
      window.__xoneProgrammaticNav = false;
    }, 5e3);
  } catch {
  }
}
function withQuery(url, params) {
  if (!params) return url;
  const entries = Object.entries(params).filter(([_, v]) => v != null);
  if (entries.length === 0) return url;
  const qs = new URLSearchParams();
  for (const [k, v] of entries) qs.append(k, String(v));
  return url + (url.includes("?") ? "&" : "?") + qs.toString();
}
function slugify(name) {
  return name.replace(/_/g, "-").replace(/([a-z0-9])([A-Z])/g, "$1-$2").replace(/([A-Z]+)([A-Z][a-z])/g, "$1-$2").toLowerCase().replace(/-+/g, "-").replace(/^-|-$/g, "");
}
function showToast(message, toastOpts) {
  if (typeof document === "undefined") return;
  let el = document.querySelector("[data-xone-toast]");
  if (!el) {
    el = document.createElement("div");
    el.setAttribute("data-xone-toast", "");
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    const portal = document.getElementById("xone-dialog-portal");
    (portal ?? document.body).appendChild(el);
  }
  const bg = toastOpts?.bgColor ?? "rgba(0,0,0,0.85)";
  const fg = toastOpts?.textColor ?? "#fff";
  const radius = toastOpts?.rounded ? "20px" : "6px";
  el.style.cssText = [
    "position:fixed",
    "left:50%",
    "bottom:24px",
    "transform:translateX(-50%)",
    `background:${bg}`,
    `color:${fg}`,
    "padding:8px 16px",
    `border-radius:${radius}`,
    "font-size:14px",
    "z-index:9999",
    "max-width:80vw",
    "opacity:0",
    "transition:opacity 200ms"
  ].join(";");
  el.textContent = message;
  requestAnimationFrame(() => {
    el.style.opacity = "1";
  });
  window.clearTimeout(el.__toastTimer);
  el.__toastTimer = window.setTimeout(() => {
    el.style.opacity = "0";
    window.setTimeout(() => {
      el.remove();
    }, 250);
  }, toastOpts?.durationMs ?? 3e3);
}
var modalDialogChain = Promise.resolve();
function enqueueModalDialog(run) {
  const result = modalDialogChain.then(run, run);
  modalDialogChain = result.then(() => void 0, () => void 0);
  return result;
}
async function openNativeDialog(opts) {
  if (typeof document === "undefined" || typeof HTMLDialogElement === "undefined") {
    window.alert([opts.title, opts.message].filter(Boolean).join("\n\n"));
    return;
  }
  return enqueueModalDialog(() => new Promise((resolve) => {
    const dialog = document.createElement("dialog");
    dialog.setAttribute("data-xone-dialog", "");
    dialog.style.cssText = "border:none;border-radius:8px;padding:0;max-width:90vw;box-shadow:0 8px 32px rgba(0,0,0,0.2);";
    const inner = document.createElement("div");
    inner.style.cssText = "padding:20px;min-width:280px;";
    if (opts.title) {
      const h = document.createElement("h3");
      h.textContent = opts.title;
      h.style.cssText = "margin:0 0 12px;font-size:16px;";
      inner.appendChild(h);
    }
    const p = document.createElement("p");
    p.textContent = opts.message;
    p.style.cssText = "margin:0 0 16px;font-size:14px;white-space:pre-wrap;";
    inner.appendChild(p);
    const ok = document.createElement("button");
    ok.type = "button";
    ok.textContent = "OK";
    ok.style.cssText = "padding:6px 16px;border:none;background:#0066cc;color:#fff;border-radius:4px;cursor:pointer;float:right;";
    ok.addEventListener("click", () => {
      dialog.close();
      dialog.remove();
      resolve();
    });
    dialog.addEventListener("cancel", () => {
      dialog.remove();
      resolve();
    });
    inner.appendChild(ok);
    dialog.appendChild(inner);
    document.body.appendChild(dialog);
    dialog.showModal();
  }));
}
async function openNativeConfirm(message) {
  if (typeof document === "undefined" || typeof HTMLDialogElement === "undefined") {
    return Promise.resolve(window.confirm(message));
  }
  return enqueueModalDialog(() => new Promise((resolve) => {
    const dialog = document.createElement("dialog");
    dialog.setAttribute("data-xone-confirm", "");
    dialog.style.cssText = "border:none;border-radius:8px;padding:0;max-width:90vw;box-shadow:0 8px 32px rgba(0,0,0,0.2);";
    const inner = document.createElement("div");
    inner.style.cssText = "padding:20px;min-width:280px;";
    const p = document.createElement("p");
    p.textContent = message;
    p.style.cssText = "margin:0 0 16px;font-size:14px;white-space:pre-wrap;";
    inner.appendChild(p);
    const actions = document.createElement("div");
    actions.style.cssText = "display:flex;justify-content:flex-end;gap:8px;";
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.textContent = "Cancelar";
    cancel.style.cssText = "padding:6px 16px;border:1px solid #ccc;background:#fff;border-radius:4px;cursor:pointer;";
    cancel.addEventListener("click", () => {
      dialog.close();
      dialog.remove();
      resolve(false);
    });
    const ok = document.createElement("button");
    ok.type = "button";
    ok.textContent = "Aceptar";
    ok.style.cssText = "padding:6px 16px;border:none;background:#0066cc;color:#fff;border-radius:4px;cursor:pointer;";
    ok.addEventListener("click", () => {
      dialog.close();
      dialog.remove();
      resolve(true);
    });
    dialog.addEventListener("cancel", () => {
      dialog.remove();
      resolve(false);
    });
    actions.appendChild(cancel);
    actions.appendChild(ok);
    inner.appendChild(actions);
    dialog.appendChild(inner);
    document.body.appendChild(dialog);
    dialog.showModal();
  }));
}
function safeOptions(buttons) {
  const n = typeof buttons === "number" ? buttons : Number(buttons);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
}
function messageBoxButtons(nOptions) {
  switch (nOptions) {
    case 1:
      return [{ label: "S\xED", tag: 1, primary: true }, { label: "No", tag: 2 }];
    case 3:
      return [
        { label: "Cancelar", tag: 2 },
        { label: "No", tag: 7 },
        { label: "S\xED", tag: 6, primary: true }
      ];
    case 4:
      return [{ label: "No", tag: 7 }, { label: "S\xED", tag: 6, primary: true }];
    case 5:
      return [{ label: "Cancelar", tag: 2, primary: true }];
    case 0:
    default:
      return [{ label: "Aceptar", tag: 1, primary: true }];
  }
}
async function openMessageBox(opts) {
  const buttons = opts.buttons ?? messageBoxButtons(opts.nOptions);
  if (typeof document === "undefined" || typeof HTMLDialogElement === "undefined") {
    const text = [opts.title, opts.message].filter(Boolean).join("\n\n");
    if (buttons.length <= 1) {
      if (typeof window !== "undefined") window.alert(text);
      return buttons[0]?.tag ?? 1;
    }
    const primary = buttons.find((b) => b.primary) ?? buttons[buttons.length - 1];
    const secondary = buttons.find((b) => !b.primary) ?? buttons[0];
    const yes = typeof window !== "undefined" ? window.confirm(text) : true;
    return yes ? primary.tag : secondary.tag;
  }
  const render = () => new Promise((resolve) => {
    const dialog = document.createElement("dialog");
    dialog.setAttribute("data-xone-msgbox", "");
    dialog.setAttribute("data-options", String(opts.nOptions));
    const idBase = `xone-msgbox-${++msgBoxSeq}`;
    if (opts.alert) dialog.setAttribute("role", "alertdialog");
    if (opts.title) dialog.setAttribute("aria-labelledby", `${idBase}-title`);
    dialog.setAttribute("aria-describedby", `${idBase}-body`);
    dialog.style.cssText = "border:none;border-radius:8px;padding:0;max-width:90vw;box-shadow:0 8px 32px rgba(0,0,0,0.2);";
    const inner = document.createElement("div");
    inner.style.cssText = "padding:20px;min-width:280px;";
    if (opts.title) {
      const h = document.createElement("h3");
      h.id = `${idBase}-title`;
      h.textContent = opts.title;
      h.style.cssText = "margin:0 0 12px;font-size:16px;";
      inner.appendChild(h);
    }
    const p = document.createElement("p");
    p.id = `${idBase}-body`;
    p.textContent = opts.message;
    p.style.cssText = "margin:0 0 16px;font-size:14px;white-space:pre-wrap;";
    inner.appendChild(p);
    const actions = document.createElement("div");
    actions.style.cssText = "display:flex;justify-content:flex-end;gap:8px;";
    let settled = false;
    const finish = (tag) => {
      if (settled) return;
      settled = true;
      try {
        dialog.close();
      } catch {
      }
      dialog.remove();
      resolve(tag);
    };
    for (const b of buttons) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.textContent = b.label;
      btn.setAttribute("data-tag", String(b.tag));
      btn.style.cssText = b.primary ? "padding:6px 16px;border:none;background:#0066cc;color:#fff;border-radius:4px;cursor:pointer;" : "padding:6px 16px;border:1px solid #ccc;background:#fff;border-radius:4px;cursor:pointer;";
      btn.addEventListener("click", () => finish(b.tag));
      actions.appendChild(btn);
    }
    inner.appendChild(actions);
    dialog.appendChild(inner);
    dialog.addEventListener("cancel", (e) => {
      e.preventDefault();
      finish(0);
    });
    document.body.appendChild(dialog);
    try {
      dialog.showModal();
    } catch (e) {
      dialog.remove();
      throw e;
    }
  });
  return opts.queue === false ? render() : enqueueModalDialog(render);
}
var msgBoxSeq = 0;
var SESSION_EXPIRED_TITLE = "Sesi\xF3n caducada";
var SESSION_EXPIRED_DIRTY_MESSAGE = "Su sesi\xF3n ha caducado. Si ten\xEDa cambios sin guardar, no se han podido guardar.";
var SESSION_EXPIRED_BUTTON = "Iniciar sesi\xF3n";
var SESSION_EXPIRED_LOGIN_MESSAGE = "Su sesi\xF3n ha caducado. Vuelva a iniciar sesi\xF3n.";
function showSessionExpiredNotice() {
  const notice = openMessageBox({
    title: SESSION_EXPIRED_TITLE,
    message: SESSION_EXPIRED_DIRTY_MESSAGE,
    nOptions: 0,
    buttons: [{ label: SESSION_EXPIRED_BUTTON, tag: 1, primary: true }],
    queue: false,
    alert: true
  });
  const settle = () => void 0;
  modalDialogChain = modalDialogChain.then(() => notice).then(settle, settle);
  return notice.then(() => void 0);
}
function isNavigationInProgress() {
  return navigating;
}
function buildRouteMap(manifest) {
  const map = /* @__PURE__ */ new Map();
  if (!manifest) return map;
  for (const r of manifest.routes) {
    map.set(r.collection, r.url);
  }
  return map;
}

// src/appData.ts
var LOGIN_NOTICE_KEY = "xone:loginNotice";
function loginCollectionPath(loginColl) {
  if (!loginColl) return "/";
  return `/${loginColl.replace(/_/g, "-").replace(/([a-z0-9])([A-Z])/g, "$1-$2").replace(/([A-Z]+)([A-Z][a-z])/g, "$1-$2").toLowerCase()}/`;
}
function resolveSessionExpiresAt(expiresAt, expiresIn, nowMs = Date.now()) {
  if (typeof expiresAt === "string" && expiresAt.trim() !== "") {
    const parsed = Date.parse(expiresAt);
    if (Number.isFinite(parsed)) return parsed;
    const asNum = Number(expiresAt);
    if (Number.isFinite(asNum) && asNum > 0) return asNum < 1e11 ? asNum * 1e3 : asNum;
  } else if (typeof expiresAt === "number" && Number.isFinite(expiresAt) && expiresAt > 0) {
    return expiresAt < 1e11 ? expiresAt * 1e3 : expiresAt;
  }
  const secs = typeof expiresIn === "number" ? expiresIn : Number(expiresIn);
  if (expiresIn != null && expiresIn !== "" && Number.isFinite(secs) && secs > 0) {
    return nowMs + secs * 1e3;
  }
  return void 0;
}
function createAppData(opts) {
  const liveValueStack = [];
  const userGlobal = createPersistentGlobal({
    key: "xone:user",
    storage: opts.storage ?? null,
    base: () => {
      const s = opts.session.get();
      if (!s) return { CODIGO: "", ID: "", NOMBRE: "", LOGIN: "" };
      return {
        CODIGO: s.userId,
        ID: s.userId,
        NOMBRE: s.nombre ?? s.login ?? "",
        LOGIN: s.login ?? "",
        ID_EMPRESA: s.empresa,
        EMPRESA: s.empresa,
        ROL: s.rol,
        // gen_usuarios.USERTYPE — referenciado como user.USERTYPE en scripts.
        USERTYPE: s.userType ?? "",
        // ROWID del usuario — usado junto a LOGIN en filtros XOne.
        ROWID: s.rowid ?? ""
      };
    }
  });
  const entGlobal = createPersistentGlobal({
    key: "xone:ent",
    storage: opts.storage ?? null,
    base: () => {
      const empresa = opts.session.get()?.empresa ?? null;
      return { CODIGO: empresa, ID: empresa, ID_EMPRESA: empresa };
    }
  });
  function navigateToEntryPoint(entryPoint) {
    if (!entryPoint || typeof window === "undefined") return;
    const ui = window.ui;
    if (ui?.openCollection) ui.openCollection(entryPoint, void 0, { rootSwitch: true });
    else if (ui?.openMenu) ui.openMenu(entryPoint);
  }
  function navigateToLoginCollection(replace = false, programmatic = false) {
    if (typeof window === "undefined") return;
    const url = loginCollectionPath(opts.macros.get("LOGIN_COLLECTION"));
    if (programmatic) markProgrammaticNavigation();
    if (replace) window.location.replace(url);
    else window.location.href = url;
  }
  function clearLoginNotice() {
    try {
      opts.storage?.removeItem(LOGIN_NOTICE_KEY);
    } catch {
    }
  }
  const appDataObj = {
    getCurrentEnterprise() {
      return entGlobal.proxy;
    },
    getCurrentUser() {
      return userGlobal.proxy;
    },
    async login(payload) {
      try {
        userGlobal.clear();
        entGlobal.clear();
        opts.clearShellOverlay?.();
        opts.session.clear();
        const p = payload;
        const deviceId = opts.macros.get("DEVICE_ID") ?? "aaaabbbbccccddddaaaabbbbccccdddd";
        const userName = p.userName ?? p.user ?? p.USUARIO ?? "";
        const password = p.password ?? p.PASSWORD ?? "";
        const noAuth = p.noAuth === true || password === "";
        const fireLoginCb = async (name, ...args) => {
          const cb = p[name];
          if (typeof cb === "function") {
            try {
              await cb(...args);
            } catch (e) {
              console.error(`[appData.login ${name}]`, e);
            }
          }
        };
        if (opts.backend === "webcore") {
          const apiBase = (opts.apiBase ?? "/api/itf").replace(/\/$/, "");
          const loginColl = p.loginCollection ?? opts.macros.get("LOGIN_COLLECTION") ?? "Usuarios";
          const res2 = await opts.http.post(`${apiBase}/Auth`, {
            coll: loginColl,
            action: "Auth",
            loadall: true,
            filter: noAuth ? { LOGIN: userName } : { LOGIN: userName, PASSWORD: password }
          });
          if (!res2 || res2.hasError === true) {
            if (res2?.err) console.warn("[appData.login webcore]", res2.err);
            void fireLoginCb("onLoginFailed", { err: res2?.err, status: 200 });
            return null;
          }
          const rows = Array.isArray(res2.data) ? res2.data : res2.data ? [res2.data] : [];
          const row = rows[0];
          if (!row) {
            void fireLoginCb("onLoginFailed", { err: void 0, status: 200 });
            return null;
          }
          const pick = (...keys) => {
            for (const k of keys) if (row[k] != null) return row[k];
            return void 0;
          };
          const envelope = res2;
          const pickAuth = (...keys) => {
            const fromRow = pick(...keys);
            if (fromRow != null) return fromRow;
            for (const k of keys) if (envelope[k] != null) return envelope[k];
            return void 0;
          };
          const expiresAt = resolveSessionExpiresAt(
            pickAuth("expiresAt", "EXPIRES_AT", "expires_at"),
            pickAuth("expires_in", "EXPIRES_IN", "expiresIn")
          );
          const userType2 = String(pick("USERTYPE", "USER_USERTYPE") ?? "");
          const wcSession = {
            // Token opaco del IDS emitido por WebCore en el `Auth`
            // (IDS-AUTH). `buildHeaders` de `$http` lo inyecta como
            // `Authorization: Bearer` en TODAS las llamadas siguientes
            // (DoAction/DoBatch/Upload/Download) salvo que el proyecto
            // traiga su propio Authorization. Vacío = sin IDS (legacy).
            token: String(pickAuth("ACCESS_TOKEN", "TOKEN") ?? ""),
            userId: pick("ID", "CODIGO", "ID_USUARIO"),
            empresa: pick("ID_EMPRESA", "EMPRESA"),
            rol: pick("ROL"),
            userType: userType2,
            nombre: pick("NOMBRE"),
            login: String(userName),
            rowid: pick("ROWID") != null ? String(pick("ROWID")) : void 0,
            ...expiresAt !== void 0 ? { expiresAt } : {}
          };
          opts.session.set(wcSession);
          clearLoginNotice();
          opts.macros.set("USERID", String(wcSession.userId ?? ""));
          opts.macros.set("EMPRESA", String(wcSession.empresa ?? ""));
          opts.macros.set("ROL", wcSession.rol ?? "");
          opts.macros.set("USERTYPE", userType2);
          opts.macros.set("USER_USERTYPE", userType2);
          if (wcSession.token) opts.macros.set("TOKEN", wcSession.token);
          await fireLoginCb("onLoginSuccessful");
          navigateToEntryPoint(
            p.entryPoint ?? opts.macros.get("ENTRY_POINT")
          );
          return wcSession;
        }
        const body = {
          USUARIO: userName,
          PASSWORD: password,
          DEVICE_ID: p.deviceId ?? p.DEVICE_ID ?? deviceId,
          PLATFORM: p.platform ?? p.PLATFORM ?? "Web",
          APP_VERSION: opts.macros.get("APP_VERSION") ?? "1.0.0",
          ...p.enterprise || p.ID_EMPRESA ? { ID_EMPRESA: p.enterprise ?? p.ID_EMPRESA } : {},
          ...noAuth ? { NO_AUTH: true } : {}
        };
        const entryPointOverride = p.entryPoint;
        const res = await opts.http.post("/api/v1/auth/login", body);
        if (!res?.ACCESS_TOKEN) {
          void fireLoginCb("onLoginFailed", { err: void 0, status: 200 });
          return null;
        }
        const userType = res.MACROS_SESION?.USERTYPE ?? res.USERTYPE ?? "";
        const session = {
          token: res.ACCESS_TOKEN,
          refreshToken: res.REFRESH_TOKEN,
          userId: res.ID_USUARIO,
          empresa: res.ID_EMPRESA,
          rol: res.ROL,
          userType,
          nombre: res.NOMBRE,
          login: String(userName),
          expiresAt: res.EXPIRES_AT
        };
        opts.session.set(session);
        clearLoginNotice();
        opts.macros.set("TOKEN", res.ACCESS_TOKEN ?? "");
        opts.macros.set("REFRESH_TOKEN", res.REFRESH_TOKEN ?? "");
        opts.macros.set("USERID", String(res.ID_USUARIO ?? ""));
        opts.macros.set("EMPRESA", String(res.ID_EMPRESA ?? ""));
        opts.macros.set("ROL", res.ROL ?? "");
        if (res.MACROS_SESION) {
          for (const [k, v] of Object.entries(res.MACROS_SESION)) {
            opts.macros.set(k, v);
          }
        }
        opts.macros.set("USER_USERTYPE", userType);
        opts.macros.set("USERTYPE", userType);
        await fireLoginCb("onLoginSuccessful");
        navigateToEntryPoint(entryPointOverride ?? opts.macros.get("ENTRY_POINT"));
        return session;
      } catch (err) {
        console.error("[appData.login]", err);
        const cb = payload?.onLoginFailed;
        if (typeof cb === "function") {
          const status = err?.status;
          try {
            cb({
              err,
              status: typeof status === "number" ? status : void 0
            });
          } catch (e) {
            console.error("[appData.login onLoginFailed]", e);
          }
        }
        return null;
      }
    },
    async logout() {
      const cleanup = () => {
        if (opts.onLogout) {
          try {
            void Promise.resolve(opts.onLogout(opts.session.get())).catch((e) => {
              console.error("[appData.logout onLogout]", e);
            });
          } catch (e) {
            console.error("[appData.logout onLogout]", e);
          }
        }
        opts.navStack?.clear();
        opts.session.clear();
        userGlobal.clear();
        entGlobal.clear();
        opts.clearShellOverlay?.();
        queueMicrotask(() => {
          try {
            opts.storage?.removeItem("xone:macros");
          } catch {
          }
        });
      };
      if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
        window.addEventListener("pagehide", cleanup, { once: true });
      } else {
        cleanup();
      }
      navigateToLoginCollection();
    },
    getCollection(name, sourceName) {
      if (opts.dataLayerGetCollection) {
        return opts.dataLayerGetCollection(name, sourceName);
      }
      return {
        getName() {
          return name;
        },
        async loadAll(_filter, _sort) {
        },
        getCurrentItem() {
          return null;
        },
        async createObject(_initial) {
          return null;
        }
      };
    },
    setGlobalMacro(name, value) {
      const key = name.replace(/^##|##$/g, "");
      opts.macros.set(key, value == null ? null : String(value));
      try {
        if (typeof window !== "undefined" && typeof window.dispatchEvent === "function" && typeof CustomEvent === "function") {
          window.dispatchEvent(new CustomEvent("xone:global-macro", { detail: { name: key } }));
        }
      } catch {
      }
    },
    getGlobalMacro(name) {
      const key = name.replace(/^##|##$/g, "");
      return opts.macros.get(key) ?? "";
    },
    expandMacros(text) {
      return opts.macros.expand(text);
    },
    failWithMessage(code, msg) {
      if (msg === "##EXIT##" && opts.onExit?.()) {
        return;
      }
      console.error(`[appData.failWithMessage] code=${code} msg=${msg}`);
    },
    exit(exitOpts) {
      opts.navStack?.clear();
      opts.session.clear();
      userGlobal.clear();
      entGlobal.clear();
      opts.clearShellOverlay?.();
      queueMicrotask(() => {
        try {
          opts.storage?.removeItem("xone:macros");
        } catch {
        }
      });
      navigateToLoginCollection(exitOpts?.replace === true, exitOpts?.programmatic === true);
    },
    // Paridad Android `XoneApplication.PushValue/PopValue`
    // (XoneApplication.java:1821/1792): pila de parámetros con
    // REFERENCIAS VIVAS dentro de la página (un DataObject pusheado debe
    // poder recuperarse como objeto, no como JSON — el framework consume
    // el tope tras ejecutar un nodo para abrir su edit view,
    // ExecuteNodeTask.java:137-151). La persistencia en sessionStorage
    // (JSON, best-effort) solo cubre el pop tras una navegación MPA.
    pushValue(value) {
      liveValueStack.push(value);
      if (typeof sessionStorage === "undefined") return;
      try {
        const raw = sessionStorage.getItem("xone:valueStack");
        const stack = raw ? JSON.parse(raw) : [];
        const serializable = value && typeof value.toJson === "function" ? value.toJson() : value;
        stack.push(serializable);
        sessionStorage.setItem("xone:valueStack", JSON.stringify(stack));
      } catch {
      }
    },
    popValue() {
      let persisted;
      if (typeof sessionStorage !== "undefined") {
        try {
          const raw = sessionStorage.getItem("xone:valueStack");
          if (raw) {
            const stack = JSON.parse(raw);
            persisted = stack.pop();
            sessionStorage.setItem("xone:valueStack", JSON.stringify(stack));
          }
        } catch {
        }
      }
      if (liveValueStack.length > 0) return liveValueStack.pop();
      return persisted;
    },
    pushValueAndExit(value) {
      appDataObj.pushValue?.(value);
      setNavDirection(opts.storage ?? null, "forward");
    },
    isPhone() {
      if (typeof window === "undefined") return false;
      return window.matchMedia("(max-width: 767px)").matches;
    },
    isTablet() {
      if (typeof window === "undefined") return false;
      return window.matchMedia("(min-width: 768px) and (max-width: 1024px)").matches;
    },
    isVertical() {
      if (typeof window === "undefined") return true;
      return window.matchMedia("(orientation: portrait)").matches;
    },
    isHorizontal() {
      if (typeof window === "undefined") return false;
      return window.matchMedia("(orientation: landscape)").matches;
    },
    getAppPath() {
      return "/";
    },
    getFilesPath() {
      return "/files/";
    },
    writeConsoleString(msg) {
      console.log(msg);
    },
    executeSql(_sql) {
      console.warn("[appData.executeSql] No soportado en el canal web \u2014 los datos vienen del backend.");
      return null;
    }
  };
  return appDataObj;
}

// src/bundle.ts
function createBundleCtor() {
  class XoneBundle {
    data;
    constructor(initial) {
      this.data = initial ? { ...initial } : {};
    }
    get(key) {
      return this.data[key];
    }
    set(key, value) {
      this.data[key] = value;
    }
    has(key) {
      return key in this.data;
    }
    keys() {
      return Object.keys(this.data);
    }
  }
  return XoneBundle;
}

// src/crypto.ts
var _nativeCrypto = typeof globalThis !== "undefined" && globalThis.crypto ? globalThis.crypto : void 0;
function createCryptoApi() {
  return {
    async sha256(input) {
      return digestHex("SHA-256", input);
    },
    async sha1(input) {
      return digestHex("SHA-1", input);
    },
    async sha384(input) {
      return digestHex("SHA-384", input);
    },
    async sha512(input) {
      return digestHex("SHA-512", input);
    },
    async md5(input) {
      return md5Hash(input);
    },
    async hmacSha256(key, input) {
      const keyData = new TextEncoder().encode(key);
      const cryptoKey = await _nativeCrypto.subtle.importKey(
        "raw",
        keyData,
        { name: "HMAC", hash: "SHA-256" },
        false,
        ["sign"]
      );
      const sig = await _nativeCrypto.subtle.sign(
        "HMAC",
        cryptoKey,
        new TextEncoder().encode(input)
      );
      return toHex(new Uint8Array(sig));
    },
    getRandomValues(array) {
      return _nativeCrypto.getRandomValues(array);
    },
    randomBytes(length) {
      const arr = new Uint8Array(length);
      _nativeCrypto.getRandomValues(arr);
      return arr;
    },
    base64Encode(input) {
      return btoa(unescape(encodeURIComponent(input)));
    },
    base64Decode(input) {
      return decodeURIComponent(escape(atob(input)));
    },
    toBase64(input) {
      return btoa(unescape(encodeURIComponent(input)));
    },
    fromBase64(input) {
      return decodeURIComponent(escape(atob(input)));
    },
    getNewUuid() {
      if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID().replace(/-/g, "");
      }
      return Array.from(
        { length: 32 },
        () => Math.floor(Math.random() * 16).toString(16)
      ).join("");
    },
    getRandomString(length) {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      const arr = new Uint8Array(length);
      window.crypto.getRandomValues(arr);
      return Array.from(arr, (b) => chars[b % chars.length]).join("");
    },
    getRandomInt(min, max) {
      return Math.floor(Math.random() * (max - min + 1)) + min;
    },
    getRandomDouble() {
      return Math.random();
    },
    getChecksum(input) {
      let hash = 0;
      for (let i = 0; i < input.length; i++) {
        hash = (hash << 5) - hash + input.charCodeAt(i) | 0;
      }
      return Math.abs(hash).toString(16).padStart(8, "0");
    }
  };
}
async function digestHex(algo, input) {
  const data = new TextEncoder().encode(String(input));
  const buf = await _nativeCrypto.subtle.digest(algo, data);
  return toHex(new Uint8Array(buf));
}
function toHex(bytes) {
  let out = "";
  for (const b of bytes) out += b.toString(16).padStart(2, "0");
  return out;
}
function md5Hash(input) {
  const bytes = new TextEncoder().encode(input);
  const len = bytes.length;
  const words = new Uint32Array(len + 72 >>> 6 << 4);
  const view = new DataView(words.buffer);
  for (let i = 0; i < len; i++) view.setUint8(i, bytes[i]);
  view.setUint8(len, 128);
  view.setUint32(words.length - 2 << 2, len << 3, true);
  const S = [7, 12, 17, 22, 5, 9, 14, 20, 4, 11, 16, 23, 6, 10, 15, 21];
  const K = new Uint32Array(64);
  for (let i = 0; i < 64; i++) K[i] = Math.floor(2 ** 32 * Math.abs(Math.sin(i + 1)));
  let a0 = 1732584193, b0 = 4023233417, c0 = 2562383102, d0 = 271733878;
  for (let off = 0; off < words.length; off += 16) {
    let a = a0, b = b0, c = c0, d = d0;
    for (let i = 0; i < 64; i++) {
      let f, g;
      if (i < 16) {
        f = b & c | ~b & d;
        g = i;
      } else if (i < 32) {
        f = d & b | ~d & c;
        g = (5 * i + 1) % 16;
      } else if (i < 48) {
        f = b ^ c ^ d;
        g = (3 * i + 5) % 16;
      } else {
        f = c ^ (b | ~d);
        g = 7 * i % 16;
      }
      const m = view.getUint32(off + g << 2, true);
      const temp = d;
      d = c;
      c = b;
      const sum = a + f + K[i] + m >>> 0;
      const s = S[(i >>> 4 << 2) + (i & 3)];
      b = b + ((sum << s | sum >>> 32 - s) >>> 0) >>> 0;
      a = temp;
    }
    a0 = a0 + a >>> 0;
    b0 = b0 + b >>> 0;
    c0 = c0 + c >>> 0;
    d0 = d0 + d >>> 0;
  }
  const result = new DataView(new ArrayBuffer(16));
  result.setUint32(0, a0, true);
  result.setUint32(4, b0, true);
  result.setUint32(8, c0, true);
  result.setUint32(12, d0, true);
  return toHex(new Uint8Array(result.buffer));
}

// src/clipboard.ts
function writeTextBestEffort(text) {
  const nav = typeof navigator !== "undefined" ? navigator : void 0;
  if (nav?.clipboard?.writeText) {
    nav.clipboard.writeText(text).catch(() => {
      legacyCopy(text);
    });
    return;
  }
  legacyCopy(text);
}
function legacyCopy(text) {
  if (typeof document === "undefined") return;
  const ta = document.createElement("textarea");
  try {
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.top = "-9999px";
    ta.style.left = "-9999px";
    ta.setAttribute("readonly", "");
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, ta.value.length);
    document.execCommand("copy");
  } catch {
  } finally {
    ta.remove();
  }
}
function notApplicable(method) {
  throw new Error(
    `clipboard.${method}() no est\xE1 disponible en el canal web (espec\xEDfico de Android: ficheros/intents). Usa setText/getText.`
  );
}
function createClipboardApi() {
  const api = {
    setText(text, _sensitive) {
      writeTextBestEffort(text == null ? "" : String(text));
      return api;
    },
    async getText() {
      const nav = typeof navigator !== "undefined" ? navigator : void 0;
      if (nav?.clipboard?.readText) {
        try {
          return await nav.clipboard.readText();
        } catch {
          return "";
        }
      }
      return "";
    },
    clear() {
      writeTextBestEffort("");
      return api;
    },
    async hasSomething() {
      const text = await api.getText();
      return text.length > 0;
    },
    setFile() {
      return notApplicable("setFile");
    },
    setIntent() {
      return notApplicable("setIntent");
    },
    getFile() {
      return notApplicable("getFile");
    },
    getIntent() {
      return notApplicable("getIntent");
    }
  };
  return api;
}

// src/dataLayer.ts
import { createBackendClient } from "@xone-web/backend-client";
import {
  createDataCollection,
  createRemoteStorage,
  XoneStructuredDataClient
} from "@xone-web/data";
import { create$Http } from "@xone-web/http";
import { MemoryStorageAdapter } from "@xone-web/data";
import { CollectionMacroStore } from "@xone-web/macros";

// src/webcore/WebCoreClient.ts
var ROWID = "ROWID";
function createWebCoreBackend(opts) {
  const apiBase = (opts.apiBase ?? "/api/itf").replace(/\/$/, "");
  const logger = opts.logger ?? console;
  const endpoint = `${apiBase}/DoAction`;
  const batchEndpoint = `${apiBase}/DoBatch`;
  const batchEnabled = opts.batch !== false;
  function normalizeRows(res, msg) {
    if (res && res.hasError === true) {
      throw new WebCoreError(res.err || "WebCore error", msg.coll, msg.action);
    }
    const data = res?.data;
    if (Array.isArray(data)) return data;
    if (data == null) return [];
    return [data];
  }
  async function doActionDirect(msg) {
    const res = await opts.http.post(endpoint, msg);
    return normalizeRows(res, msg);
  }
  const readTtlMs = opts.readDedupTtlMs ?? 400;
  const readCache = /* @__PURE__ */ new Map();
  function readKey(msg) {
    return [
      msg.coll,
      msg.action,
      msg.loadall ? "1" : "0",
      msg.where ?? "",
      msg.sort ?? "",
      msg.page ? `${msg.page.start},${msg.page.length}` : "",
      msg.macros ? JSON.stringify(msg.macros) : ""
    ].join("|");
  }
  function invalidateColl(coll) {
    for (const k of readCache.keys()) {
      if (k.startsWith(coll + "|")) readCache.delete(k);
    }
  }
  let pending = [];
  let flushScheduled = false;
  function scheduleFlush() {
    if (flushScheduled) return;
    flushScheduled = true;
    setTimeout(() => void flushBatch(), 0);
  }
  async function flushBatch() {
    flushScheduled = false;
    if (readTtlMs > 0 && readCache.size > 0) {
      const cutoff = Date.now() - readTtlMs;
      for (const [k, e] of readCache) {
        if (e.ts < cutoff) readCache.delete(k);
      }
    }
    const batch = pending;
    pending = [];
    if (batch.length === 0) return;
    if (batch.length === 1) {
      const p = batch[0];
      try {
        p.resolve(await doActionDirect(p.msg));
      } catch (e) {
        p.reject(e);
      }
      return;
    }
    try {
      const res = await opts.http.post(
        batchEndpoint,
        { actions: batch.map((b) => b.msg) }
      );
      const arr = Array.isArray(res) && res.length === batch.length ? res : null;
      if (!arr) {
        logger.warn?.("[webcore] DoBatch respuesta inesperada (len mismatch), reintentando individual");
        for (const p of batch) {
          try {
            p.resolve(await doActionDirect(p.msg));
          } catch (err) {
            p.reject(err);
          }
        }
        return;
      }
      batch.forEach((p, i) => {
        try {
          p.resolve(normalizeRows(arr[i], p.msg));
        } catch (e) {
          p.reject(e);
        }
      });
    } catch (e) {
      if (isAuthFailure(e)) {
        for (const p of batch) p.reject(e);
        return;
      }
      logger.warn?.("[webcore] DoBatch fall\xF3, reintentando individual", e);
      for (const p of batch) {
        try {
          p.resolve(await doActionDirect(p.msg));
        } catch (err) {
          p.reject(err);
        }
      }
    }
  }
  function doActionBatched(msg) {
    if (readTtlMs > 0) {
      const key = readKey(msg);
      const now = Date.now();
      const hit = readCache.get(key);
      if (hit && now - hit.ts < readTtlMs) {
        return hit.rows;
      }
      const p = batchEnabled ? enqueueRead(msg) : doActionDirect(msg);
      readCache.set(key, { ts: now, rows: p });
      void p.catch(() => {
        const cur = readCache.get(key);
        if (cur && cur.rows === p) readCache.delete(key);
      });
      return p;
    }
    return batchEnabled ? enqueueRead(msg) : doActionDirect(msg);
  }
  function enqueueRead(msg) {
    return new Promise((resolve, reject) => {
      pending.push({
        msg,
        resolve,
        reject
      });
      scheduleFlush();
    });
  }
  const collection = {
    // WebCore no expone metadata de esquema; el runtime sólo usa describe
    // de forma defensiva — devolvemos un stub permisivo.
    async describe(_collection) {
      return {};
    },
    async list(coll, dsl) {
      const whereFilter = dsl?.filter ? filterToSql(dsl.filter) : "";
      const whereRaw = typeof dsl?.rawWhere === "string" ? dsl.rawWhere.trim() : "";
      const where = sanitizeUndefinedSql(
        [whereFilter, whereRaw].filter((w) => w !== "").map((w) => whereFilter && whereRaw ? `(${w})` : w).join(" AND ")
      );
      const sort = dsl?.order ? orderToSql(dsl.order) : "";
      const page = dsl?.page;
      const usePage = page != null && (typeof page.size === "number" || typeof page.offset === "number");
      const msg = {
        coll,
        action: "select",
        loadall: !usePage
      };
      if (where) msg.where = where;
      if (sort) msg.sort = sort;
      if (usePage) {
        msg.page = {
          start: page.offset ?? 0,
          length: page.size ?? 1e3
        };
      }
      const macros = paramsToMacros(dsl?.macros);
      if (macros) msg.macros = macros;
      const rows = await doActionBatched(msg);
      return {
        ROWS: rows,
        PAGE_SIZE: usePage ? msg.page.length : rows.length,
        OFFSET: usePage ? msg.page.start : 0,
        TRUNCATED: false,
        NEXT_CURSOR: null,
        TIEMPO_MS: 0
      };
    },
    async count(coll, filter) {
      const raw = filter && typeof filter === "object" && ("rawWhere" in filter || "macros" in filter) ? filter : null;
      const where = raw ? raw.rawWhere ?? "" : filter ? filterToSql(filter) : "";
      const msg = { coll, action: "count" };
      if (where) msg.where = where;
      if (raw) {
        const macros = paramsToMacros(raw.macros);
        if (macros) msg.macros = macros;
      }
      const rows = await doActionBatched(msg);
      const first = rows[0];
      let total = 0;
      if (typeof first === "number") total = first;
      else if (first && typeof first === "object") {
        const v = Object.values(first)[0];
        total = typeof v === "number" ? v : Number(v) || 0;
      }
      return { TOTAL: total };
    },
    // WebCore no tiene get-by-rowid: lo emulamos con un select filtrado.
    // `keyField` = columna de identidad de la coll (default ROWID). Para colls
    // cuyo backend MySQL usa `ID` como PK (p.ej. listas basadas en un JOIN sin
    // ROWID materializado, caso real Alivia `Lead`), el filtro por ROWID no casa
    // ninguna fila → el llamador pasa el `idFieldName` real.
    async get(coll, rowid, keyField = ROWID) {
      const rows = await doActionDirect({
        coll,
        action: "select",
        loadall: true,
        where: `${keyField}=${sqlQuote(rowid)}`
      });
      return { ROW: rows[0] ?? null };
    },
    async create(coll, row) {
      const rowid = String(row[ROWID] ?? generateRowId());
      const rows = await doActionDirect({
        coll,
        action: "write",
        data: { ...row, [ROWID]: rowid },
        keys: [ROWID]
      });
      invalidateColl(coll);
      const first = rows[0];
      const raw = first?.data?.ID;
      const newId = typeof raw === "number" && raw !== 0 ? raw : typeof raw === "string" && raw !== "" && raw !== "0" ? raw : null;
      return { ROWID: rowid, newId };
    },
    async update(coll, rowid, row, keyField = ROWID) {
      await doActionDirect({
        coll,
        action: "write",
        data: { ...row, [keyField]: rowid },
        keys: [keyField]
      });
      invalidateColl(coll);
    },
    async remove(coll, rowid, keyField = ROWID) {
      await doActionDirect({
        coll,
        action: "drop",
        data: { [keyField]: rowid },
        keys: [keyField]
      });
      invalidateColl(coll);
    }
  };
  const dataQuery = {
    async execute(name, req) {
      const sort = (req.ORDEN ?? []).map((o) => `${o.CAMPO} ${o.DIRECCION === "DESC" ? "DESC" : "ASC"}`).join(", ");
      const pag = req.PAGINACION;
      const usePage = pag != null && (pag.OFFSET != null || pag.LIMIT != null);
      const macros = paramsToMacros(req.PARAMETROS);
      const msg = { coll: name, action: "select", loadall: !usePage };
      if (sort) msg.sort = sort;
      if (usePage) msg.page = { start: pag.OFFSET ?? 0, length: pag.LIMIT ?? 1e3 };
      if (macros) msg.macros = macros;
      const rows = await doActionDirect(msg);
      return { REGISTROS: rows, TOTAL: rows.length };
    }
  };
  void logger;
  return { collection, dataQuery, supportsRawWhere: true };
}
function isAuthFailure(e) {
  if (!e || typeof e !== "object") return false;
  const status = e.status;
  return status === 401 || status === 403;
}
var WebCoreError = class extends Error {
  constructor(message, collection, action) {
    super(message);
    this.collection = collection;
    this.action = action;
    this.name = "WebCoreError";
  }
  collection;
  action;
};
var OP_SQL = {
  Eq: "=",
  Ne: "<>",
  Lt: "<",
  Le: "<=",
  Gt: ">",
  Ge: ">=",
  Like: "LIKE",
  ILike: "LIKE",
  In: "IN",
  Nin: "NOT IN",
  IsNull: "IS NULL",
  IsNotNull: "IS NOT NULL",
  Between: "BETWEEN"
};
function filterToSql(node) {
  if (!node) return "";
  if (node.and && node.and.length > 0) {
    const parts = node.and.map(filterToSql).filter(Boolean);
    return parts.length > 0 ? `(${parts.join(" AND ")})` : "";
  }
  if (node.or && node.or.length > 0) {
    const parts = node.or.map(filterToSql).filter(Boolean);
    return parts.length > 0 ? `(${parts.join(" OR ")})` : "";
  }
  if (node.not) {
    const inner = filterToSql(node.not);
    return inner ? `NOT (${inner})` : "";
  }
  if (!node.field || !node.op) return "";
  const field = node.field;
  const op = OP_SQL[node.op] ?? "=";
  switch (node.op) {
    case "IsNull":
      return `${field} IS NULL`;
    case "IsNotNull":
      return `${field} IS NOT NULL`;
    case "In":
    case "Nin": {
      const arr = Array.isArray(node.value) ? node.value : [node.value];
      const list = arr.map(sqlQuote).join(", ");
      return `${field} ${op} (${list})`;
    }
    case "Between": {
      const arr = Array.isArray(node.value) ? node.value : [];
      if (arr.length !== 2) return "";
      return `${field} BETWEEN ${sqlQuote(arr[0])} AND ${sqlQuote(arr[1])}`;
    }
    default:
      return `${field} ${op} ${sqlQuote(node.value)}`;
  }
}
function orderToSql(order) {
  return order.map((o) => `${o.field} ${o.dir === "Desc" ? "DESC" : "ASC"}`).join(", ");
}
function sqlQuote(value) {
  if (value == null) return "NULL";
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? "1" : "0";
  return `'${String(value).replace(/'/g, "''")}'`;
}
function sanitizeUndefinedSql(sql) {
  return sql.replace(/(<=|>=|<>|!=|=|<|>)\s*undefined\b/gi, "$1 NULL");
}
function paramsToMacros(params) {
  if (!params) return void 0;
  const out = {};
  for (const [k, v] of Object.entries(params)) {
    const key = k.startsWith("##") ? k : `##${k}##`;
    out[key] = v == null ? "" : sanitizeUndefinedSql(String(v));
  }
  return Object.keys(out).length > 0 ? out : void 0;
}
function generateRowId() {
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  return Date.now().toString(16).padStart(32, "0").slice(0, 32);
}

// src/http.ts
function xoneCaptureSelf() {
  try {
    const w = typeof window !== "undefined" ? window : globalThis;
    const getter = w["__xoneGetSelf"];
    return typeof getter === "function" ? getter() : void 0;
  } catch {
    return void 0;
  }
}
function xoneWithSelf(self, fn) {
  const w = typeof window !== "undefined" ? window : globalThis;
  const setter = w["__xoneSetSelf"];
  const getter = w["__xoneGetSelf"];
  if (typeof setter !== "function") return fn();
  const prev = typeof getter === "function" ? getter() : void 0;
  try {
    setter(self);
    return fn();
  } finally {
    try {
      setter(prev);
    } catch {
    }
  }
}
function isUsableAuthorization(value) {
  if (value === void 0 || value === null) return false;
  const v = String(value).trim();
  if (v === "" || v === "undefined" || v === "null") return false;
  if (/^(bearer|basic|token)\s*(undefined|null)?\s*$/i.test(v)) return false;
  if (isExpiredBearerJwt(v)) return false;
  return true;
}
var JWT_EXPIRY_SKEW_MS = 5e3;
function base64UrlDecode(seg) {
  try {
    let s = seg.replace(/-/g, "+").replace(/_/g, "/");
    const pad = s.length % 4;
    if (pad) s += "=".repeat(4 - pad);
    if (typeof atob === "function") return atob(s);
    const g = globalThis;
    if (g.Buffer) return g.Buffer.from(s, "base64").toString("binary");
    return void 0;
  } catch {
    return void 0;
  }
}
function isExpiredBearerJwt(value, nowMs = Date.now()) {
  if (value === void 0 || value === null) return false;
  const m = /^bearer\s+(.+)$/i.exec(String(value).trim());
  const token = m?.[1]?.trim();
  if (!token) return false;
  const parts = token.split(".");
  const payloadSeg = parts[1];
  if (!payloadSeg) return false;
  const json = base64UrlDecode(payloadSeg);
  if (!json) return false;
  let payload;
  try {
    payload = JSON.parse(json);
  } catch {
    return false;
  }
  const exp = payload?.exp;
  if (typeof exp !== "number" || !isFinite(exp)) return false;
  return exp * 1e3 <= nowMs + JWT_EXPIRY_SKEW_MS;
}
function coerceHeaderValue(name, value) {
  if (typeof value === "string") return value;
  if (value === void 0 || value === null) {
    if (typeof console !== "undefined" && typeof console.warn === "function") {
      console.warn(
        `[$http] cabecera '${name}' con valor ${value === null ? "null" : "undefined"}; se env\xEDa como "[]" (probable JSON.stringify(undefined) en el proyecto).`
      );
    }
    return "[]";
  }
  return void 0;
}
function normalizeQuerySeparators(url) {
  const i = url.indexOf("?");
  if (i < 0) return url;
  const head = url.slice(0, i + 1);
  const tail = url.slice(i + 1);
  return head + tail.replace(/&{2,}/g, "&").replace(/^&+/, "");
}
function parseContentDispositionFilename(value) {
  if (value === void 0 || value === null) return void 0;
  const header = String(value);
  const extended = /filename\*\s*=\s*(?:[^']*'[^']*')?([^;]+)/i.exec(header);
  if (extended && extended[1]) {
    let v = extended[1].trim().replace(/^["']|["']$/g, "");
    try {
      v = decodeURIComponent(v);
    } catch {
    }
    v = v.trim();
    if (v) return v;
  }
  const legacy = /filename\s*=\s*(?:"([^"]*)"|([^;]+))/i.exec(header);
  if (legacy) {
    const v = (legacy[1] ?? legacy[2] ?? "").trim();
    if (v) return v;
  }
  return void 0;
}
var SessionLostError = class extends Error {
  status = 401;
  code = "session_lost";
  body = "";
  constructor(method, url) {
    super(
      `[$http] ${method} ${url} \u2192 401 (session_lost): la sesi\xF3n de esta p\xE1gina ya no es v\xE1lida; la petici\xF3n no se ha enviado. Vuelva a iniciar sesi\xF3n.`
    );
    this.name = "SessionLostError";
  }
};
function isAuthActionUrl(url) {
  const path = url.split(/[?#]/, 1)[0] ?? "";
  const last = path.replace(/\/+$/, "").split("/").pop() ?? "";
  return last.toLowerCase() === "auth";
}
function pageOrigin() {
  const origin = typeof location !== "undefined" && typeof location.origin === "string" ? location.origin : void 0;
  return !origin || origin === "null" ? void 0 : origin;
}
function pageOriginIsOpaque() {
  return typeof location !== "undefined" && location.origin === "null";
}
function parseAgainstPage(url) {
  const origin = pageOrigin();
  if (!origin) return void 0;
  try {
    return new URL(url, origin);
  } catch {
    return void 0;
  }
}
var LOOKS_ABSOLUTE_RE = /^(?:[a-z][a-z0-9+.-]*:|[/\\]{2})/i;
function looksAbsolute(url) {
  const cleaned = url.replace(/[\t\n\r]/g, "").replace(/^[\u0000- ]+/, "");
  return LOOKS_ABSOLUTE_RE.test(cleaned);
}
var warnedForeignApiBase = false;
function warnForeignApiBaseOnce(apiBase) {
  if (warnedForeignApiBase || !pageOrigin()) return;
  warnedForeignApiBase = true;
  console.warn(
    `[$http] bearer de sesi\xF3n retenido: apiBase de otro origen (${apiBase}) \u2014 no se inyecta el token del IDS ni un 401 suyo cierra la sesi\xF3n`
  );
}
function isSessionBearerTarget(url) {
  if (pageOriginIsOpaque()) return false;
  const origin = pageOrigin();
  if (origin) {
    const parsed = parseAgainstPage(url);
    return parsed !== void 0 && parsed.origin === origin;
  }
  return !looksAbsolute(url);
}
function apiBaseSessionScope(apiBase) {
  const baseIsAbsolute = looksAbsolute(apiBase);
  const relativeBase = apiBase.replace(/\/+$/, "");
  return (url) => {
    let base = relativeBase;
    if (baseIsAbsolute) {
      if (!isSessionBearerTarget(apiBase)) {
        warnForeignApiBaseOnce(apiBase);
        return false;
      }
      const parsedBase = parseAgainstPage(apiBase);
      if (!parsedBase) return false;
      base = parsedBase.pathname.replace(/\/+$/, "");
    }
    let path;
    const origin = pageOrigin();
    if (origin) {
      const parsed = parseAgainstPage(url);
      if (!parsed || parsed.origin !== origin) return false;
      path = parsed.pathname;
    } else {
      if (looksAbsolute(url)) return false;
      path = url;
    }
    if (base === "") return true;
    const p = path.toLowerCase();
    const b = base.toLowerCase();
    return p === b || p.startsWith(`${b}/`) || p.startsWith(`${b}?`);
  };
}
function createHttp(opts) {
  function classifyAuth(headers) {
    const sent = headers.get("Authorization");
    if (!sent) return "none";
    const tok = opts.session.get()?.token;
    return tok && sent === `Bearer ${tok}` ? "session" : "caller";
  }
  function sentSessionToken(authSource) {
    return authSource === "session" ? opts.session.get()?.token || void 0 : void 0;
  }
  function handleUnauthorized(url, status, authSource, sentToken) {
    if (status !== 401 || authSource === "caller" || !opts.onUnauthorized) return;
    if (opts.sessionScope && !opts.sessionScope(url)) return;
    const currentToken = opts.session.get()?.token || void 0;
    if (currentToken !== sentToken) return;
    if (authSource === "session" && (opts.canRevokeSession?.() ?? true)) {
      try {
        opts.session.markRevoked?.();
      } catch {
      }
    }
    try {
      opts.session.clear?.();
    } catch {
    }
    notifyUnauthorized({ url, status, authSource });
  }
  function notifyUnauthorized(info) {
    if (!opts.onUnauthorized) return;
    try {
      opts.onUnauthorized(info);
    } catch (e) {
      console.error("[$http onUnauthorized]", e);
    }
  }
  function sessionLostFor(url, authSource) {
    if (authSource === "caller" || !opts.onUnauthorized || !opts.sessionScope) return false;
    if (!opts.session.isRevoked?.()) return false;
    if (!opts.sessionScope(url) || isAuthActionUrl(url)) return false;
    return true;
  }
  function isForbiddenInScope(url, status) {
    return status === 403 && !!opts.sessionScope && opts.sessionScope(url);
  }
  function forbiddenError(method, url, text, body) {
    let detail = text;
    try {
      const j = JSON.parse(text);
      if (j && typeof j.err === "string" && j.err.trim() !== "") detail = j.err;
    } catch {
    }
    let coll = "";
    let action = "";
    try {
      const msg = typeof body === "string" ? JSON.parse(body) : body;
      if (msg && typeof msg === "object") {
        const m = msg;
        if (typeof m.coll === "string") coll = m.coll;
        if (typeof m.action === "string") action = m.action;
      }
    } catch {
    }
    const message = `[$http] ${method} ${url} \u2192 403: autenticado pero sin autorizaci\xF3n` + (coll ? ` (${coll}/${action || "?"})` : "") + (detail ? `: ${detail.slice(0, 200)}` : "") + ". La sesi\xF3n se conserva; revisa los scopes/cliente del token en el IDS.";
    console.error(message);
    const err = new WebCoreError(message, coll, action);
    err.status = 403;
    err.body = text;
    return err;
  }
  function resolveUrl(url) {
    const expanded = normalizeQuerySeparators(opts.macros.expand(url));
    if (/^https?:\/\//i.test(expanded)) return expanded;
    if (expanded.startsWith("/")) return expanded;
    const base = opts.apiBase ?? "";
    return `${base.replace(/\/$/, "")}/${expanded.replace(/^\//, "")}`;
  }
  function sessionBearerFor(finalUrl) {
    const s = opts.session.get();
    if (!s?.token) return void 0;
    if (!isSessionBearerTarget(finalUrl)) {
      const base = opts.apiBase;
      if (base && looksAbsolute(base) && !isSessionBearerTarget(base)) warnForeignApiBaseOnce(base);
      return void 0;
    }
    return `Bearer ${s.token}`;
  }
  function buildHeaders(finalUrl, init) {
    const h = new Headers(init?.headers ?? {});
    if (!h.has("Accept")) h.set("Accept", "application/json");
    if (h.has("Authorization") && !isUsableAuthorization(h.get("Authorization")))
      h.delete("Authorization");
    if (!h.has("Authorization")) {
      const bearer = sessionBearerFor(finalUrl);
      if (bearer) h.set("Authorization", bearer);
    }
    return h;
  }
  async function request(method, url, body, init) {
    const finalUrl = resolveUrl(url);
    const headers = buildHeaders(finalUrl, init);
    const authSource = classifyAuth(headers);
    const sentToken = sentSessionToken(authSource);
    if (sessionLostFor(finalUrl, authSource)) {
      notifyUnauthorized({ url: finalUrl, status: 401, authSource: "session", sessionLost: true });
      throw new SessionLostError(method, finalUrl);
    }
    let payload;
    if (body !== void 0 && body !== null) {
      if (body instanceof Blob || body instanceof FormData || body instanceof URLSearchParams || typeof body === "string") {
        payload = body;
      } else {
        payload = JSON.stringify(body);
        if (!headers.has("Content-Type"))
          headers.set("Content-Type", "application/json");
      }
    }
    const res = await fetch(finalUrl, {
      method,
      ...init,
      headers,
      body: payload
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      if (isForbiddenInScope(finalUrl, res.status)) {
        throw forbiddenError(method, finalUrl, text, body);
      }
      handleUnauthorized(finalUrl, res.status, authSource, sentToken);
      const err = new Error(
        `[$http] ${method} ${finalUrl} \u2192 ${res.status}: ${text.slice(0, 200)}`
      );
      err.status = res.status;
      err.body = text;
      throw err;
    }
    const ct = res.headers.get("content-type") ?? "";
    if (ct.includes("application/json")) {
      return await res.json();
    }
    return await res.text();
  }
  const noop = () => {
  };
  async function dispatchCallback(method, url, reqRaw, onOk, onErr) {
    const callSelf = xoneCaptureSelf();
    const req = reqRaw && typeof reqRaw === "object" ? reqRaw : {};
    const hasBody = method !== "GET" && method !== "DELETE";
    const init = {};
    if (req.headers && typeof req.headers === "object") {
      const h = {};
      for (const [k, v] of Object.entries(req.headers)) {
        const hv = coerceHeaderValue(k, v);
        if (hv !== void 0) h[k] = hv;
      }
      init.headers = h;
    }
    const readTimeout = typeof req.parameters?.readTimeout === "number" ? req.parameters.readTimeout : void 0;
    let timer;
    if (readTimeout && readTimeout > 0 && typeof AbortController !== "undefined") {
      const ac = new AbortController();
      init.signal = ac.signal;
      timer = setTimeout(() => ac.abort(), readTimeout);
    }
    try {
      const result = await request(method, url, hasBody ? req.data : void 0, init);
      xoneWithSelf(callSelf, () => onOk(result, []));
    } catch (e) {
      const anyErr = e;
      if (anyErr?.name === "AbortError") {
        xoneWithSelf(callSelf, () => onErr(-1, "timeout", ""));
      } else {
        xoneWithSelf(callSelf, () => onErr(anyErr?.status ?? 0, anyErr?.message ?? String(e), anyErr?.body ?? ""));
      }
    } finally {
      if (timer) clearTimeout(timer);
    }
  }
  function dual(method, url, args) {
    if (typeof args[1] === "function") {
      return dispatchCallback(
        method,
        url,
        args[0],
        args[1],
        args[2] ?? noop
      );
    }
    const hasBody = method !== "GET" && method !== "DELETE";
    return hasBody ? request(method, url, args[0], args[1]) : request(method, url, void 0, args[0]);
  }
  return {
    // Firma dual: `get(url, init?)` → Promise (uso interno); o
    // `get(url, request, onOk, onErr)` → callbacks (paridad Android). El tipo
    // público (overloads) vive en `HttpApi`; la impl usa `any` porque una sola
    // firma no puede satisfacer los dos overloads (Promise<T> vs void).
    /* eslint-disable @typescript-eslint/no-explicit-any */
    get(url, ...args) {
      return dual("GET", url, args);
    },
    post(url, ...args) {
      return dual("POST", url, args);
    },
    put(url, ...args) {
      return dual("PUT", url, args);
    },
    delete(url, ...args) {
      return dual("DELETE", url, args);
    },
    /* eslint-enable @typescript-eslint/no-explicit-any */
    // Descarga binaria con callbacks (paridad Android `$http.getFile`). El
    // proyecto lo usa para "ver documento/PDF": descarga el fichero y en el
    // callback de éxito hace `ui.openUrl(data)` para abrirlo. Devolvemos un
    // OBJECT URL (blob:) que `ui.openUrl`/`ui.openURL` abre en pestaña nueva.
    // Estilo callback (no Promise) para casar con la firma del proyecto; los
    // errores van al errback `(code, msg, extended)`. `request.headers` del
    // proyecto puede traer flags no-cabecera (processData/contentType=false):
    // solo se copian los valores string (p.ej. Authorization Bearer).
    getFile(url, req, onSuccess, onError) {
      const finalUrl = resolveUrl(url);
      const headers = new Headers();
      const reqHeaders = req?.headers;
      if (reqHeaders && typeof reqHeaders === "object") {
        for (const [k, v] of Object.entries(reqHeaders)) {
          const hv = coerceHeaderValue(k, v);
          if (hv !== void 0) headers.set(k, hv);
        }
      }
      let unusableAuth = false;
      if (headers.has("Authorization") && !isUsableAuthorization(headers.get("Authorization"))) {
        headers.delete("Authorization");
        unusableAuth = true;
      }
      if (!headers.has("Authorization")) {
        const bearer = sessionBearerFor(finalUrl);
        if (bearer) headers.set("Authorization", bearer);
      }
      const authless = unusableAuth && !headers.has("Authorization");
      const authSource = classifyAuth(headers);
      const sentToken = sentSessionToken(authSource);
      if (sessionLostFor(finalUrl, authSource)) {
        notifyUnauthorized({ url: finalUrl, status: 401, authSource: "session", sessionLost: true });
        const lost = new SessionLostError("GET", finalUrl);
        void Promise.resolve().then(() => onError?.(401, lost.message, ""));
        return;
      }
      void (async () => {
        try {
          const res = await fetch(finalUrl, { method: "GET", headers });
          if (!res.ok) {
            const text = await res.text().catch(() => "");
            if (isForbiddenInScope(finalUrl, res.status)) {
              void forbiddenError("GET", finalUrl, text, void 0);
            }
            handleUnauthorized(finalUrl, res.status, authSource, sentToken);
            const msg = authless && res.status === 401 ? `HTTP 401 \u2014 sesi\xF3n no v\xE1lida: el token de usuario no est\xE1 disponible o ha expirado (Authorization vac\xEDo/'undefined'/JWT vencido). Vuelva a iniciar sesi\xF3n.` : authless && res.status === 403 ? `HTTP 403 \u2014 autenticado pero sin autorizaci\xF3n: el servidor deniega la operaci\xF3n (permisos/scopes); un nuevo inicio de sesi\xF3n no lo resuelve. La petici\xF3n sali\xF3 sin credenciales utilizables (Authorization vac\xEDo/'undefined'/JWT vencido y sin token de sesi\xF3n).` : `HTTP ${res.status}`;
            onError?.(res.status, msg, text.slice(0, 200));
            return;
          }
          const blob = await res.blob();
          const objectUrl = typeof URL !== "undefined" && typeof URL.createObjectURL === "function" ? URL.createObjectURL(blob) : finalUrl;
          const suggestedFilename = parseContentDispositionFilename(
            res.headers.get("content-disposition")
          );
          onSuccess?.(objectUrl, suggestedFilename);
        } catch (e) {
          onError?.(-1, e instanceof Error ? e.message : String(e), "");
        }
      })();
    }
  };
}

// src/shellOverlay.ts
var STORAGE_KEY = "xone:shellOverlay";
function clearShellOverlayStorage(storage) {
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
  }
}
function collectShellRenderFields(doc, coll) {
  if (!doc) return null;
  let hosts;
  try {
    hosts = doc.querySelectorAll(`[data-src="${coll.replace(/["\\]/g, "\\$&")}"]`);
  } catch {
    return null;
  }
  const out = /* @__PURE__ */ new Set();
  let found = false;
  for (const host of Array.from(hosts)) {
    const tpl = host.querySelector("template[data-item-template]");
    if (!tpl) continue;
    found = true;
    const root = tpl.content ?? tpl;
    for (const el of Array.from(root.querySelectorAll("*"))) {
      const prop = el.getAttribute("data-prop");
      if (prop) out.add(prop);
      for (const attr of Array.from(el.attributes)) {
        collectFieldRefs(attr.value, out);
      }
    }
    collectFldMacros(root.textContent ?? "", out);
  }
  return found ? out : null;
}
function collectFieldRefs(text, out) {
  if (!text) return;
  for (const m of text.matchAll(/row\[\s*["']([A-Za-z0-9_]+)["']\s*\]/g)) out.add(m[1]);
  collectFldMacros(text, out);
}
function collectFldMacros(text, out) {
  if (!text || !text.includes("##FLD_")) return;
  for (const m of text.matchAll(/##FLD_([A-Za-z0-9_]+)##/g)) out.add(m[1]);
}
var EMPTY_CLEARED = Object.freeze([]);
function isPrimitive(v) {
  return v === null || typeof v === "string" || typeof v === "number" || typeof v === "boolean";
}
function safeParse(raw) {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed;
    }
  } catch {
  }
  return {};
}
function deepClone(bag) {
  return safeParse(JSON.stringify(bag));
}
function createShellOverlay(storage) {
  const incoming = storage ? safeParse(storage.getItem(STORAGE_KEY)) : {};
  let live = deepClone(incoming);
  let applying = false;
  let selectionDepth = 0;
  let resetPending = false;
  const fieldSources = /* @__PURE__ */ new Map();
  const fieldCache = /* @__PURE__ */ new Map();
  function persist() {
    if (!storage) return;
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(live));
    } catch {
    }
  }
  function allowedFields(coll) {
    const cached = fieldCache.get(coll);
    if (cached) return cached;
    const source = fieldSources.get(coll);
    if (!source) return null;
    let resolved = null;
    try {
      resolved = source();
    } catch {
    }
    if (!resolved) return null;
    fieldCache.set(coll, resolved);
    const collBag = live[coll];
    if (collBag) {
      let dirty = false;
      for (const rowKey of Object.keys(collBag)) {
        const row = collBag[rowKey];
        for (const f of Object.keys(row)) {
          if (!resolved.has(f)) {
            delete row[f];
            dirty = true;
          }
        }
        if (Object.keys(row).length === 0) delete collBag[rowKey];
      }
      if (Object.keys(collBag).length === 0) delete live[coll];
      if (dirty) persist();
    }
    return resolved;
  }
  return {
    record(coll, rowKey, field, value) {
      if (applying) return EMPTY_CLEARED;
      if (!isPrimitive(value)) return EMPTY_CLEARED;
      const allowed = allowedFields(coll);
      if (allowed && !allowed.has(field)) return EMPTY_CLEARED;
      let cleared = null;
      if (selectionDepth > 0 && resetPending) {
        live = {};
        resetPending = false;
      } else if (selectionDepth === 0) {
        const collBag = live[coll];
        if (collBag) {
          for (const otherKey of Object.keys(collBag)) {
            if (otherKey === rowKey) continue;
            const row = collBag[otherKey];
            if (Object.prototype.hasOwnProperty.call(row, field) && row[field] === value) {
              delete row[field];
              (cleared ??= []).push({ rowKey: otherKey, field });
              if (Object.keys(row).length === 0) delete collBag[otherKey];
            }
          }
        }
      }
      live[coll] ??= {};
      live[coll][rowKey] ??= {};
      live[coll][rowKey][field] = value;
      persist();
      return cleared ?? EMPTY_CLEARED;
    },
    getIncoming(coll) {
      const bag = incoming[coll] ?? null;
      if (!bag) return null;
      const allowed = allowedFields(coll);
      if (!allowed) return bag;
      const filtered = {};
      for (const [rowKey, row] of Object.entries(bag)) {
        const keep = {};
        for (const [f, v] of Object.entries(row)) {
          if (allowed.has(f)) keep[f] = v;
        }
        if (Object.keys(keep).length > 0) filtered[rowKey] = keep;
      }
      return Object.keys(filtered).length > 0 ? filtered : null;
    },
    runApplying(fn) {
      const prev = applying;
      applying = true;
      try {
        return fn();
      } finally {
        applying = prev;
      }
    },
    beginSelection() {
      if (selectionDepth === 0) resetPending = true;
      selectionDepth++;
    },
    endSelection() {
      selectionDepth = Math.max(0, selectionDepth - 1);
      if (selectionDepth === 0) resetPending = false;
    },
    clear() {
      live = {};
      for (const k of Object.keys(incoming)) delete incoming[k];
      clearShellOverlayStorage(storage);
    },
    setFieldSource(coll, source) {
      fieldSources.set(coll, source);
      fieldCache.delete(coll);
    }
  };
}

// src/dataLayer.ts
var CONTENTS_ITEM_CHANGED_EVENT = "xone:contents-item-changed";
var _pendingContentsChanged = /* @__PURE__ */ new Map();
var _contentsFlushScheduled = false;
function notifyContentsItemChanged(src, change) {
  if (typeof document === "undefined") return;
  const prev = _pendingContentsChanged.get(src);
  if (!change || prev === null) {
    _pendingContentsChanged.set(src, null);
  } else if (prev === void 0) {
    _pendingContentsChanged.set(src, [change]);
  } else {
    prev.push(change);
  }
  if (_contentsFlushScheduled) return;
  _contentsFlushScheduled = true;
  queueMicrotask(() => {
    _contentsFlushScheduled = false;
    const batch = [..._pendingContentsChanged];
    _pendingContentsChanged.clear();
    for (const [s, changes] of batch) {
      document.dispatchEvent(new CustomEvent(CONTENTS_ITEM_CHANGED_EVENT, {
        detail: changes ? { src: s, changes } : { src: s }
      }));
    }
  });
}
function primitiveFields(row) {
  const out = {};
  for (const [k, v] of Object.entries(row)) {
    const t = typeof v;
    if (v === null || t === "string" || t === "number" || t === "boolean") out[k] = v;
  }
  return out;
}
var MAX_PERSISTED_ROWS = 200;
function createDataLayer(opts) {
  const cache = /* @__PURE__ */ new Map();
  let bundlePromise = null;
  const shellCollsPromise = (async () => {
    if (!opts.shellOverlay || !opts.layoutShellColl) return /* @__PURE__ */ new Set();
    const manifest = await opts.manifestPromise;
    const shellDef = manifest?.collections?.[opts.layoutShellColl];
    const srcs = (shellDef?.contents ?? []).map((c) => c.src).filter((s) => typeof s === "string" && s.length > 0);
    return new Set(srcs);
  })();
  function ensureBundle() {
    if (bundlePromise) return bundlePromise;
    bundlePromise = (async () => {
      const $http = create$Http({
        macroReader: {
          getGlobalMacro: (n) => opts.macros.get(n)
        }
      });
      if (opts.backend === "webcore") {
        const webcoreHttp = createHttp({
          macros: opts.macros,
          session: opts.session,
          // IDS-AUTH: 401 sobre el bearer de sesión → limpiar + re-login,
          // acotado al apiBase de WebCore (`sessionScope`).
          onUnauthorized: opts.onUnauthorized,
          sessionScope: opts.sessionScope,
          canRevokeSession: opts.canRevokeSession
        });
        const webcoreBackend = createWebCoreBackend({
          http: webcoreHttp,
          apiBase: opts.apiBase
        });
        const structuredClient2 = new XoneStructuredDataClient(webcoreBackend);
        return { adapter: new MemoryStorageAdapter(), structuredClient: structuredClient2 };
      }
      const backendClient = createBackendClient({
        apiBase: opts.apiBase,
        $http,
        macros: {
          getGlobalMacro: (n) => opts.macros.get(n)
        },
        projectId: opts.projectId,
        // Opt-in por proyecto: sin declararlo, `@xone-web/data` usa el parser clásico.
        filterDialect: opts.filterParser === "full" ? "full" : null
      });
      const remote = createRemoteStorage({
        backend: backendClient
      });
      const structuredClient = new XoneStructuredDataClient(
        backendClient
      );
      return { adapter: remote.adapter, structuredClient };
    })();
    return bundlePromise;
  }
  function getCollection(name, sourceName) {
    const src = sourceName ?? name;
    const cacheKey = src === name ? name : `@contents:${src}#${name}`;
    const existing = cache.get(cacheKey);
    if (existing) return existing;
    const wrapper = createWrapper(
      src,
      ensureBundle,
      opts.manifestPromise,
      opts.macros,
      opts.shellOverlay ?? null,
      shellCollsPromise
    );
    cache.set(cacheKey, wrapper);
    return wrapper;
  }
  return {
    getCollection,
    getStorage: () => ensureBundle().then((b) => b.adapter),
    snapshotQueryStates() {
      const out = {};
      for (const [collName, wrapper] of cache) {
        if (collName.startsWith("@contents:")) continue;
        const st = wrapper.getQueryState();
        if (st) out[collName] = st;
      }
      return out;
    },
    restoreQueryStates(states) {
      for (const [collName, st] of Object.entries(states)) {
        if (!st || typeof st !== "object") continue;
        getCollection(collName).applyQueryState(st);
      }
    },
    discardNewItem(collName, rowid) {
      const wrapper = cache.get(collName);
      return wrapper ? wrapper.discardNewInMemory(rowid) : false;
    },
    upsertNewItem(collName, rowid, fields) {
      const wrapper = cache.get(collName);
      return wrapper ? wrapper.upsertNewInMemory(rowid, fields) : false;
    },
    async refetchLockedCollections(exceptName) {
      const tasks = [];
      for (const [collName, wrapper] of cache) {
        if (collName.startsWith("@contents:")) continue;
        if (exceptName && collName === exceptName) continue;
        tasks.push(wrapper.refetchLocked());
      }
      await Promise.all(tasks);
    }
  };
}
function createWrapper(name, ensureBundle, manifestPromise, macros, shellOverlay, shellCollsPromise) {
  let inner = null;
  let initPromise = null;
  let ghostWarnLogged = false;
  let isShellScoped = false;
  const overlayBaseValues = /* @__PURE__ */ new Map();
  let revertingOverlay = false;
  const macroBuffer = new CollectionMacroStore();
  let pendingFilter = null;
  let pendingSort = null;
  let pendingLinkFilter = null;
  let locked = false;
  let loadedSig = null;
  const currentSig = () => {
    const macros2 = inner ? inner.getAllMacros() : macroBuffer.getAllMacros();
    const keys = Object.keys(macros2).sort();
    const mStr = keys.map((k) => `${k}=${String(macros2[k] ?? "")}`).join("&");
    const filter = inner ? inner.getFilter() : pendingFilter ?? "";
    const sort = inner ? inner.getSort() : pendingSort ?? "";
    const linkFilter = pendingLinkFilter ?? "";
    return `${mStr}|f:${filter}|s:${sort}|l:${linkFilter}`;
  };
  let pendingOwner = null;
  let pendingSeed = null;
  let restoredPage = null;
  let lastLoadIncremental = false;
  const refetchPage = () => {
    const c = inner;
    const live = c ? c.getPageInfo() : null;
    if (c && live) {
      if (lastLoadIncremental) return null;
      if (loadedSig !== null && currentSig() !== loadedSig) return null;
      return { offset: live.offset, size: live.size };
    }
    if (loadedSig !== null) return null;
    return restoredPage;
  };
  function ensure() {
    if (inner) return Promise.resolve(inner);
    if (initPromise) return initPromise;
    initPromise = (async () => {
      const manifest = await manifestPromise;
      if (shellOverlay) {
        isShellScoped = (await shellCollsPromise).has(name);
        if (isShellScoped) {
          shellOverlay.setFieldSource(
            name,
            () => collectShellRenderFields(
              typeof document !== "undefined" ? document : null,
              name
            )
          );
        }
      }
      const def = manifest?.collections?.[name];
      if (!def) {
        if (!ghostWarnLogged) {
          ghostWarnLogged = true;
          console.warn(
            `[xone] appData.getCollection("${name}"): no definida en manifest. Devolvendo ghost (sin datos remotos).`
          );
        }
        return null;
      }
      const bundle = await ensureBundle();
      const xneDef = def;
      inner = createDataCollection({
        name,
        definition: xneDef,
        storage: bundle.adapter,
        structuredClient: bundle.structuredClient,
        // Duck-typing: DataCollection espera GlobalMacroStore (clase de @xone-web/macros)
        // pero solo usa getGlobalMacro/getAllGlobalMacros — nuestro adapter es compatible.
        macros: {
          getGlobalMacro: (n) => macros.get(n),
          getAllGlobalMacros: () => macros.snapshot()
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        },
        // Resolver de definiciones para `<contents>` de row-items
        // (item.getContents(...)): la colección hija se construye
        // con la definición completa de su `src` desde el manifest.
        definitionResolver: async (collName) => {
          const m = await manifestPromise;
          const childDef = m?.collections?.[collName];
          return childDef ? childDef : null;
        },
        // Nodo `<load>` por fila (WEAL-45). `loadAll` llama aquí con las
        // filas recién hidratadas y el runtime de islas resuelve el
        // handler `<load>` del módulo de eventos YA compilado de esa coll
        // (`EVENTS_BY_COLL[collName].load`) — sin duplicar `scriptSource`
        // en el manifest ni acoplar `@xone-web/data` al islands-runtime.
        // Mismo puente global que `window.__xoneRunNode` usa `ui`.
        onRowsLoaded: async (collName, items) => {
          const w = globalThis;
          let ranLoad = false;
          if (typeof w.__xoneRunCollLoad === "function") {
            ranLoad = await w.__xoneRunCollLoad(collName, items) === true;
          }
          if (typeof w.__xoneRunCollOnchangeOnLoad === "function") {
            try {
              await w.__xoneRunCollOnchangeOnLoad(collName, items);
            } catch {
            }
          }
          return ranLoad;
        }
      });
      inner.setOnItemFieldSet((item, field, kind) => {
        if (kind === "property") {
          notifyContentsItemChanged(name, { field, kind, item });
          return;
        }
        if (shellOverlay && isShellScoped && inner && !revertingOverlay) {
          try {
            const idField = inner.getIdFieldName();
            const key = item.getValue(idField);
            if (key != null) {
              const cleared = shellOverlay.record(
                name,
                String(key),
                field,
                item.getValue(field)
              );
              if (cleared.length > 0) revertClearedOverlay(cleared);
            }
          } catch {
          }
        }
        notifyContentsItemChanged(name, { field, kind, item });
      });
      for (const [k, v] of Object.entries(macroBuffer.getAllMacros())) {
        inner.setMacro(k, v);
      }
      if (pendingFilter !== null) inner.setFilter(pendingFilter);
      if (pendingSort !== null) inner.setSort(pendingSort);
      if (pendingLinkFilter !== null && typeof inner.setLinkFilter === "function") {
        inner.setLinkFilter(pendingLinkFilter);
      }
      if (pendingOwner !== null && typeof inner.setOwnerObject === "function") {
        inner.setOwnerObject(pendingOwner);
      }
      if (locked) inner.lock();
      return inner;
    })();
    return initPromise;
  }
  function applyOverlay(c) {
    if (!shellOverlay || !isShellScoped) return;
    const inc = shellOverlay.getIncoming(name);
    if (!inc) return;
    const idField = c.getIdFieldName();
    shellOverlay.runApplying(() => {
      const count = c.getCount();
      for (let i = 0; i < count; i++) {
        const item = c.get(i);
        if (!item) continue;
        let key;
        try {
          key = item.getValue(idField);
        } catch {
          continue;
        }
        if (key == null) continue;
        const fields = inc[String(key)];
        if (!fields) continue;
        for (const [f, v] of Object.entries(fields)) {
          let base;
          try {
            base = item.getValue(f);
          } catch {
            base = void 0;
          }
          if (base === void 0) base = null;
          try {
            item.setValue(f, v);
          } catch {
            continue;
          }
          let row = overlayBaseValues.get(String(key));
          if (!row) overlayBaseValues.set(String(key), row = /* @__PURE__ */ new Map());
          if (!row.has(f)) row.set(f, base);
        }
      }
    });
  }
  function revertClearedOverlay(cleared) {
    const c = inner;
    if (!c || !shellOverlay) return;
    const pending = cleared.filter((e) => overlayBaseValues.get(e.rowKey)?.has(e.field));
    if (pending.length === 0) return;
    const idField = c.getIdFieldName();
    revertingOverlay = true;
    try {
      shellOverlay.runApplying(() => {
        const count = c.getCount();
        for (let i = 0; i < count; i++) {
          const item = c.get(i);
          if (!item) continue;
          let key;
          try {
            key = item.getValue(idField);
          } catch {
            continue;
          }
          if (key == null) continue;
          const rowKey = String(key);
          const row = overlayBaseValues.get(rowKey);
          if (!row) continue;
          for (const entry of pending) {
            if (entry.rowKey !== rowKey) continue;
            if (!row.has(entry.field)) continue;
            try {
              item.setValue(entry.field, row.get(entry.field));
            } catch {
            }
            row.delete(entry.field);
          }
          if (row.size === 0) overlayBaseValues.delete(rowKey);
        }
      });
    } finally {
      revertingOverlay = false;
    }
    notifyContentsItemChanged(name);
  }
  let inFlightLoad = null;
  const doLoadAll = async (filterOrPageOrShow, sortOrPage, page) => {
    if (pendingSeed) {
      try {
        await pendingSeed;
      } catch {
      }
    }
    if (locked) return;
    const isPageObj = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
    let filter;
    let sort;
    let pageOpt = page;
    if (typeof filterOrPageOrShow === "boolean") {
      if (isPageObj(sortOrPage)) pageOpt = sortOrPage;
    } else if (isPageObj(filterOrPageOrShow)) {
      pageOpt = filterOrPageOrShow;
    } else if (typeof filterOrPageOrShow === "string") {
      filter = filterOrPageOrShow;
      if (typeof sortOrPage === "string") sort = sortOrPage;
      else if (isPageObj(sortOrPage)) pageOpt = sortOrPage;
    }
    const c = await ensure();
    if (!c) return;
    if (filter !== void 0) c.setFilter(filter);
    if (sort !== void 0) c.setSort(sort);
    const raceLocked = locked && typeof c.unlock === "function" && typeof c.lock === "function";
    if (raceLocked) c.unlock();
    try {
      await c.loadAll(pageOpt ?? void 0);
    } finally {
      if (raceLocked) c.lock();
    }
    applyOverlay(c);
    loadedSig = currentSig();
    lastLoadIncremental = pageOpt?.append === true || pageOpt?.prepend === true;
  };
  return {
    getName() {
      return name;
    },
    loadAll(filterOrPageOrShow, sortOrPage, page) {
      const p = doLoadAll(filterOrPageOrShow, sortOrPage, page);
      inFlightLoad = p;
      void p.catch(() => void 0).finally(() => {
        if (inFlightLoad === p) inFlightLoad = null;
      });
      return p;
    },
    isLoading() {
      return inFlightLoad !== null;
    },
    whenLoaded() {
      return (inFlightLoad ?? Promise.resolve()).catch(() => void 0);
    },
    getPageInfo() {
      return inner && typeof inner.getPageInfo === "function" ? inner.getPageInfo() : null;
    },
    hasMorePages() {
      return inner && typeof inner.hasMorePages === "function" ? inner.hasMorePages() : false;
    },
    async reload() {
      const c = await ensure();
      if (!c) return;
      await c.reload();
    },
    getCurrentItem() {
      if (!inner) return null;
      return inner.getCurrentItem();
    },
    async findAllObjects(...args) {
      const c = await ensure();
      if (!c) return [];
      return c.findAllObjects(...args);
    },
    async createObject(initial) {
      const c = await ensure();
      if (!c) {
        throw new Error(
          `[xone] No se puede createObject("${name}"): colecci\xF3n no definida en el manifest.`
        );
      }
      return c.createObject(initial);
    },
    async createClone() {
      const c = await ensure();
      if (!c) {
        throw new Error(
          `[xone] No se puede createClone("${name}"): colecci\xF3n no definida en el manifest.`
        );
      }
      return c.createClone();
    },
    async ready() {
      const c = await ensure();
      if (!c) {
        throw new Error(
          `[xone] Colecci\xF3n "${name}" no definida en el manifest.`
        );
      }
      return c;
    },
    async startBrowse(_returnCount) {
      const c = await ensure();
      if (!c) return 0;
      await c.startBrowse();
      return c.browseLength();
    },
    isEOF() {
      if (!inner) return true;
      return inner.isEOF();
    },
    async moveNext() {
      if (!inner) return false;
      return inner.moveNext();
    },
    endBrowse() {
      if (!inner) return;
      void inner.endBrowse();
    },
    setFilter(filter) {
      pendingFilter = filter;
      if (inner) inner.setFilter(filter);
    },
    getFilter() {
      if (inner) return inner.getFilter();
      return pendingFilter ?? "";
    },
    setSort(sort) {
      pendingSort = sort;
      if (inner) inner.setSort(sort);
    },
    setOwnerObject(obj) {
      pendingOwner = obj ?? null;
      if (inner && typeof inner.setOwnerObject === "function") {
        inner.setOwnerObject(obj ?? null);
      }
    },
    getOwnerObject() {
      const innerAny = inner;
      if (innerAny && typeof innerAny.getOwnerObject === "function") {
        return innerAny.getOwnerObject();
      }
      return pendingOwner;
    },
    setLinkFilter(value) {
      pendingLinkFilter = value ?? "";
      if (inner && typeof inner.setLinkFilter === "function") {
        inner.setLinkFilter(value ?? "");
      }
    },
    getSort() {
      if (inner) return inner.getSort();
      return pendingSort ?? "";
    },
    getCount() {
      if (!inner) return 0;
      return inner.getCount();
    },
    // WEAL-142 — cuenta TOTAL en backend para el filtro/macros actuales
    // (arranque `start-from-bottom`: offset de la última página). Async.
    async countRemote() {
      const c = await ensure();
      if (!c || typeof c.countRemote !== "function") return c ? c.getCount() : 0;
      return c.countRemote();
    },
    getObject(indexOrKey) {
      if (!inner) return null;
      return inner.get(indexOrKey);
    },
    get(indexOrKey) {
      if (!inner) return null;
      return inner.get(indexOrKey);
    },
    getItem(...args) {
      if (!inner) return null;
      return inner.getItem(...args);
    },
    async findObject(field, value, ...rest) {
      const c = await ensure();
      if (!c) return null;
      return c.findObject(field, value, ...rest);
    },
    async deleteItem(obj) {
      const c = await ensure();
      if (!c) return false;
      return c.deleteItem(obj);
    },
    async saveAll() {
      const c = await ensure();
      if (!c) return false;
      return c.saveAll();
    },
    async addItem(obj) {
      const c = await ensure();
      if (!c) return false;
      return c.addItem(obj);
    },
    clear() {
      if (locked) return;
      if (!inner) return;
      inner.clear();
    },
    lock() {
      locked = true;
      if (inner) inner.lock();
    },
    unlock() {
      locked = false;
      if (inner) inner.unlock();
    },
    isLocked() {
      return locked;
    },
    isFull() {
      return inner ? inner.isFull() : false;
    },
    isStale() {
      return loadedSig === null ? true : currentSig() !== loadedSig;
    },
    count() {
      return inner ? inner.getCount() : 0;
    },
    async loadFromJson(arrayOrJson, params) {
      const c = await ensure();
      if (!c) return null;
      return c.loadFromJson(arrayOrJson, params);
    },
    setMacro(name2, value) {
      macroBuffer.setMacro(name2, value);
      if (inner) inner.setMacro(name2, value);
    },
    getQueryState() {
      if (isShellScoped) return null;
      const macros2 = macroBuffer.getAllMacros();
      const filter = inner ? inner.getFilter() : pendingFilter ?? "";
      const sort = inner ? inner.getSort() : pendingSort ?? "";
      const linkFilter = pendingLinkFilter ?? "";
      const isLocked = locked;
      const macroKeys = Object.keys(macros2);
      if (!filter && !sort && !linkFilter && !isLocked && macroKeys.length === 0) {
        return null;
      }
      const page = refetchPage() ?? void 0;
      const m = {};
      for (const k of macroKeys) m[k] = String(macros2[k] ?? "");
      let rows;
      let rowFieldProperties;
      let anyRowProps = false;
      if (isLocked && inner && inner.getCount() <= MAX_PERSISTED_ROWS) {
        rows = [];
        rowFieldProperties = [];
        const count = inner.getCount();
        for (let i = 0; i < count; i++) {
          const it = inner.get(i);
          if (!it || typeof it.toJson !== "function") continue;
          if (typeof it.isNew === "function" && it.isNew()) {
            const userAdded = typeof it.wasUserAdded === "function" && it.wasUserAdded() === true;
            if (!userAdded) continue;
          }
          try {
            rows.push(primitiveFields(it.toJson()));
            let props = [];
            if (typeof it.getCachedFieldAttributes === "function") {
              try {
                props = it.getCachedFieldAttributes() ?? [];
              } catch {
                props = [];
              }
            }
            rowFieldProperties.push(props);
            if (props.length > 0) anyRowProps = true;
          } catch {
          }
        }
      }
      return {
        macros: m,
        filter,
        sort,
        linkFilter,
        locked: isLocked,
        ...page ? { page } : {},
        ...rows ? { rows } : {},
        // Solo persistir si alguna fila tiene overrides (evita ruido).
        ...anyRowProps && rowFieldProperties ? { rowFieldProperties } : {}
      };
    },
    applyQueryState(state) {
      for (const [k, v] of Object.entries(state.macros ?? {})) {
        macroBuffer.setMacro(k, v);
        if (inner) inner.setMacro(k, v);
      }
      const sp = state.page;
      restoredPage = sp && typeof sp.size === "number" && sp.size > 0 ? { offset: typeof sp.offset === "number" && sp.offset > 0 ? sp.offset : 0, size: sp.size } : null;
      if (state.filter) {
        pendingFilter = state.filter;
        if (inner) inner.setFilter(state.filter);
      }
      if (state.sort) {
        pendingSort = state.sort;
        if (inner) inner.setSort(state.sort);
      }
      if (state.linkFilter) {
        pendingLinkFilter = state.linkFilter;
        if (inner && typeof inner.setLinkFilter === "function") {
          inner.setLinkFilter(state.linkFilter);
        }
      }
      if (state.locked) {
        locked = true;
        if (inner) inner.lock();
      }
      const rows = state.rows;
      if (state.locked && state.refetchRows) {
        pendingSeed = (async () => {
          try {
            const c = await ensure();
            if (!c || c.getCount() > 0) return;
            try {
              c.unlock();
              await c.loadAll(refetchPage());
            } catch {
            } finally {
              c.lock();
            }
            if (c.getCount() === 0 && rows && Array.isArray(rows) && rows.length > 0) {
              await c.loadFromJson(rows);
            }
          } catch {
          } finally {
            pendingSeed = null;
          }
        })();
      } else if (rows && Array.isArray(rows)) {
        const rowProps = state.rowFieldProperties;
        pendingSeed = (async () => {
          try {
            const c = await ensure();
            if (c && c.getCount() === 0) {
              await c.loadFromJson(rows);
              if (rowProps && Array.isArray(rowProps)) {
                const cnt = c.getCount();
                for (let i = 0; i < cnt && i < rowProps.length; i++) {
                  const props = rowProps[i];
                  if (!props || props.length === 0) continue;
                  const item = c.get(i);
                  if (!item || typeof item.setFieldPropertyValue !== "function") {
                    continue;
                  }
                  for (const p of props) {
                    try {
                      item.setFieldPropertyValue(p.field, p.attribute, p.value);
                    } catch {
                    }
                  }
                }
              }
            }
          } catch {
          } finally {
            pendingSeed = null;
          }
        })();
      }
    },
    upsertNewInMemory(rowid, fields) {
      if (!inner || !rowid) return false;
      let merged = false;
      const count = inner.getCount();
      for (let i = 0; i < count; i++) {
        const it = inner.get(i);
        if (!it || typeof it.getValue !== "function") continue;
        if (String(it.getValue("ROWID") ?? "") !== rowid) continue;
        for (const [k, v] of Object.entries(fields)) {
          try {
            it.setValue(k, v);
          } catch {
          }
        }
        merged = true;
        break;
      }
      const c = inner;
      const wasLocked = locked;
      void (async () => {
        try {
          if (wasLocked) c.unlock();
          try {
            await c.loadAll(refetchPage());
          } finally {
            if (wasLocked) c.lock();
          }
          notifyContentsItemChanged(name);
        } catch {
        }
      })();
      return merged;
    },
    discardNewInMemory(rowid) {
      if (!inner || !rowid) return false;
      const count = inner.getCount();
      for (let i = 0; i < count; i++) {
        const it = inner.get(i);
        if (!it || typeof it.getValue !== "function") continue;
        if (String(it.getValue("ROWID") ?? "") !== rowid) continue;
        if (typeof it.isNew === "function" && !it.isNew()) return false;
        const removed = inner.removeFromMemory(it);
        if (removed) notifyContentsItemChanged(name);
        return removed;
      }
      return false;
    },
    async refetchLocked() {
      if (!inner || !locked || isShellScoped) return false;
      const c = inner;
      try {
        c.unlock();
        try {
          await c.loadAll(refetchPage());
        } finally {
          c.lock();
        }
        notifyContentsItemChanged(name);
        return true;
      } catch {
        return false;
      }
    },
    getMacro(name2) {
      return inner ? inner.getMacro(name2) : macroBuffer.getMacro(name2);
    },
    getMacroCount() {
      return inner ? inner.getMacroCount() : macroBuffer.getMacroCount();
    },
    getAllMacros() {
      return inner ? inner.getAllMacros() : macroBuffer.getAllMacros();
    }
  };
}

// src/dvSafeEval.ts
var DV_TOKEN = /\s+|_coerce|row|==|!=|>=|<=|&&|\|\||[()[\]!<>]|'[^']*'|"[^"]*"|\d+(?:\.\d+)?|[A-Za-z_$][\w$]*/g;
function coerceDv(v) {
  return v === null || v === void 0 ? 0 : v;
}
function compileTranslatedDv(js) {
  const toks = [];
  DV_TOKEN.lastIndex = 0;
  let m;
  let cur = 0;
  while ((m = DV_TOKEN.exec(js)) !== null) {
    if (m.index !== cur) {
      throw new Error(`dvSafeEval: caracter no soportado en pos ${cur}: '${js.slice(cur, m.index)}'`);
    }
    cur = m.index + m[0].length;
    if (!/^\s+$/.test(m[0])) toks.push(m[0]);
  }
  if (cur !== js.length) {
    throw new Error(`dvSafeEval: caracteres sobrantes al final: '${js.slice(cur)}'`);
  }
  let i = 0;
  const peek = () => toks[i];
  const eat = (expected) => {
    const x = toks[i++];
    if (x === void 0) {
      throw new Error(`dvSafeEval: fin inesperado de expresi\xF3n (se esperaba '${expected ?? "token"}')`);
    }
    if (expected !== void 0 && x !== expected) {
      throw new Error(`dvSafeEval: se esperaba '${expected}' pero lleg\xF3 '${x}'`);
    }
    return x;
  };
  const strLit = (t) => t[0] === "'" || t[0] === '"' ? t.slice(1, -1) : t;
  function primary() {
    const t = eat();
    if (t === "(") {
      const e = orExpr();
      eat(")");
      return e;
    }
    if (t === "_coerce") {
      eat("(");
      eat("row");
      eat("[");
      const k = strLit(eat());
      eat("]");
      eat(")");
      return (row) => coerceDv(row[k]);
    }
    if (t === "row") {
      eat("[");
      const k = strLit(eat());
      eat("]");
      return (row) => row[k];
    }
    if (t === "null") return () => null;
    if (t === "undefined") return () => void 0;
    if (t === "true") return () => true;
    if (t === "false") return () => false;
    if (t[0] === "'" || t[0] === '"') {
      const s = t.slice(1, -1);
      return () => s;
    }
    if (/^\d/.test(t)) {
      const n = Number(t);
      return () => n;
    }
    return () => void 0;
  }
  function unary() {
    if (peek() === "!") {
      eat("!");
      const e = unary();
      return (row) => !e(row);
    }
    return primary();
  }
  function cmp() {
    let left = unary();
    for (; ; ) {
      const op = peek();
      if (op !== "==" && op !== "!=" && op !== ">" && op !== "<" && op !== ">=" && op !== "<=") break;
      eat();
      const right = unary();
      const l = left;
      switch (op) {
        // eslint-disable-next-line eqeqeq
        case "==":
          left = (row) => l(row) == right(row);
          break;
        // eslint-disable-next-line eqeqeq
        case "!=":
          left = (row) => l(row) != right(row);
          break;
        case ">":
          left = (row) => l(row) > right(row);
          break;
        case "<":
          left = (row) => l(row) < right(row);
          break;
        case ">=":
          left = (row) => l(row) >= right(row);
          break;
        default:
          left = (row) => l(row) <= right(row);
          break;
      }
    }
    return left;
  }
  function andExpr() {
    let left = cmp();
    while (peek() === "&&") {
      eat("&&");
      const right = cmp();
      const l = left;
      left = (row) => Boolean(l(row)) && Boolean(right(row));
    }
    return left;
  }
  function orExpr() {
    let left = andExpr();
    while (peek() === "||") {
      eat("||");
      const right = andExpr();
      const l = left;
      left = (row) => Boolean(l(row)) || Boolean(right(row));
    }
    return left;
  }
  const fn = orExpr();
  if (i !== toks.length) {
    throw new Error(`dvSafeEval: token inesperado '${peek()}'`);
  }
  return fn;
}

// src/rebindInlineHandlers.ts
var CALL_RE = /^\s*([A-Za-z_$][\w$]*)\s*\(([^]*?)\)\s*;?\s*$/;
function parseLiteralArg(tok) {
  const t = tok.trim();
  if (t === "") return { ok: false };
  if (t === "true") return { ok: true, value: true };
  if (t === "false") return { ok: true, value: false };
  if (t === "null") return { ok: true, value: null };
  if (/^-?\d+(?:\.\d+)?$/.test(t)) return { ok: true, value: Number(t) };
  const q = t[0];
  if ((q === "'" || q === '"') && t.length >= 2 && t[t.length - 1] === q) {
    const inner = t.slice(1, -1);
    if (!inner.includes(q)) return { ok: true, value: inner };
  }
  return { ok: false };
}
function rebindOne(el, win) {
  const code = el.getAttribute("onclick");
  if (code == null) return;
  const m = CALL_RE.exec(code);
  if (!m) return;
  const fnName = m[1];
  const argsStr = m[2];
  if (fnName === void 0 || argsStr === void 0) return;
  const fn = win[fnName];
  if (typeof fn !== "function") return;
  const args = [];
  if (argsStr.trim() !== "") {
    for (const part of argsStr.split(",")) {
      const p = parseLiteralArg(part);
      if (!p.ok) return;
      args.push(p.value);
    }
  }
  el.removeAttribute("onclick");
  el.addEventListener("click", function() {
    try {
      fn.apply(this, args);
    } catch {
    }
  });
}
function rebindInlineHandlers(root) {
  if (!root || typeof root.querySelectorAll !== "function") return;
  const win = typeof window !== "undefined" ? window : void 0;
  if (!win) return;
  if (typeof root.hasAttribute === "function" && root.hasAttribute("onclick")) rebindOne(root, win);
  const nodes = root.querySelectorAll("[onclick]");
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (node) rebindOne(node, win);
  }
}

// src/domSync.ts
import { formatXoneDisplayDate, formatXoneInputDate } from "@xone-web/types";
function dateFieldTypeOf(el) {
  const holder = el.hasAttribute("data-xone-fieldtype") ? el : el.closest("[data-xone-fieldtype]");
  const ft = holder?.getAttribute("data-xone-fieldtype");
  return ft === "D" || ft === "DT" || ft === "H" ? ft : null;
}
var ISO_DATE_INPUT = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/;
function normalizeDateInputValue(inp, str) {
  if (!(inp instanceof HTMLInputElement)) return str;
  const type = inp.type;
  if (type !== "date" && type !== "datetime-local" && type !== "time") return str;
  if (str === "") return "";
  const m = ISO_DATE_INPUT.exec(str);
  if (!m) return str;
  const [, y, mo, d, h = "00", mi = "00"] = m;
  if (type === "date") return `${y}-${mo}-${d}`;
  if (type === "time") return `${h}:${mi}`;
  return `${y}-${mo}-${d}T${h}:${mi}`;
}
var BUTTON_VISUAL_EXCLUDE_ATTRS = /* @__PURE__ */ new Set([
  "bgcolor",
  "forecolor",
  "border-color",
  "border-width"
]);
var STYLE_PATCH = {
  bgcolor: "backgroundColor",
  forecolor: "color",
  fontsize: "fontSize",
  tmargin: "marginTop",
  bmargin: "marginBottom",
  lmargin: "marginLeft",
  rmargin: "marginRight",
  tpadding: "paddingTop",
  bpadding: "paddingBottom",
  lpadding: "paddingLeft",
  rpadding: "paddingRight",
  width: "width",
  height: "height"
};
var SIZE_PATCH_ATTRS = /* @__PURE__ */ new Set([
  "width",
  "height",
  "tmargin",
  "bmargin",
  "lmargin",
  "rmargin",
  "tpadding",
  "bpadding",
  "lpadding",
  "rpadding"
]);
var RT_SIZE_TOKEN = /^(-?\d+(?:\.\d+)?)(p|%)?$/;
function toCssSizeRuntime(raw) {
  const trimmed = String(raw ?? "").trim();
  if (trimmed.length === 0) return trimmed;
  const m = RT_SIZE_TOKEN.exec(trimmed);
  if (!m) return trimmed;
  const n = Number(m[1]);
  const unit = m[2];
  if (unit === "%") return `${n}%`;
  if (unit === "p") return `calc(${n} * var(--xone-p-unit, 1px))`;
  if (n === -1) return "100%";
  if (n === -2) return "auto";
  if (n < 0) return "auto";
  return `calc(${n} * var(--xone-p-unit, 1px))`;
}
function dataColorToCss(raw) {
  return raw;
}
function resolveResourceUrl(value) {
  const v = (value ?? "").trim();
  if (v === "") return v;
  if (/^(?:https?:|data:|blob:|\/\/)/i.test(v)) return v;
  if (v.startsWith("/")) return v;
  return `/icons/${v.replace(/^\.?\/*/, "")}`;
}
var FLD_MACRO_RE = /##FLD_([A-Za-z0-9_]+)##/g;
function normalizeImgSrc(raw) {
  if (!raw || /^(?:https?:|data:|blob:|\/\/)/i.test(raw) || raw.includes("##")) return raw;
  let n = raw.replace(/\\/g, "/");
  if (n.startsWith("./")) n = n.slice(2);
  if (n.startsWith("/")) return n;
  if (n.includes("/")) return `/${n}`;
  return `/icons/${n}`;
}
function hideBrokenImage(img) {
  img.removeAttribute("src");
  img.setAttribute("alt", "");
  img.style.visibility = "hidden";
  img.classList.add("xone-img-empty");
}
function resolveFldImage(img, fields) {
  const tpl = img.getAttribute("data-xone-img-src");
  if (tpl === null) return;
  const resolved = tpl.replace(FLD_MACRO_RE, (_m, f) => {
    const v = fields[f];
    return v == null ? "" : String(v);
  }).trim();
  if (resolved === "" || resolved.includes("##")) {
    hideBrokenImage(img);
  } else {
    img.src = normalizeImgSrc(resolved);
    img.style.removeProperty("visibility");
    img.classList.remove("xone-img-empty");
  }
}
function syncImgFieldValue(el, value) {
  const v = (value ?? "").trim();
  for (const n of Array.from(el.childNodes)) {
    if (n.nodeType === 3) el.removeChild(n);
  }
  let img = el.tagName === "IMG" ? el : el.querySelector("img");
  if (v === "" || v.includes("##")) {
    if (img) hideBrokenImage(img);
    el.classList.add("xone-img-empty");
    return;
  }
  if (img === null) {
    img = document.createElement("img");
    img.alt = "";
    img.className = "xone-prop-img-value";
    const hasW = el.style.width !== "";
    const hasH = el.style.height !== "";
    img.style.display = "block";
    img.style.objectFit = "contain";
    img.style.objectPosition = "center";
    img.style.maxWidth = "100%";
    img.style.width = hasW ? "100%" : "auto";
    img.style.height = hasH ? "100%" : "auto";
    el.appendChild(img);
  }
  const src = resolveResourceUrl(v);
  if (img.getAttribute("src") !== src) img.src = src;
  img.style.removeProperty("visibility");
  img.classList.remove("xone-img-empty");
  el.classList.remove("xone-img-empty");
}
function patchPropAttributeDOM(el, attrName, value) {
  const styleProp = STYLE_PATCH[attrName];
  if (styleProp) {
    let cssVal;
    if (attrName === "bgcolor" || attrName === "forecolor") cssVal = dataColorToCss(value);
    else if (SIZE_PATCH_ATTRS.has(attrName)) cssVal = toCssSizeRuntime(value);
    else cssVal = value;
    el.style[styleProp] = cssVal;
    return;
  }
  if (attrName === "text-bgcolor") {
    const sub = el.querySelector("input, textarea");
    (sub ?? el).style.backgroundColor = dataColorToCss(value);
    return;
  }
  if (attrName === "text-forecolor") {
    el.style.setProperty("--xone-text-forecolor", dataColorToCss(value));
    return;
  }
  if (attrName === "border-color") {
    el.style.borderColor = dataColorToCss(value);
    if (!el.style.borderStyle || el.style.borderStyle === "none") {
      el.style.borderStyle = "solid";
      const w = el.style.borderWidth;
      if (!w || w === "0" || w === "0px") {
        el.style.borderWidth = "1px";
      }
    }
    return;
  }
  if (attrName === "border-width") {
    el.style.borderWidth = toCssSizeRuntime(value);
    el.style.borderStyle = "solid";
    return;
  }
  if (attrName === "fontbold" || attrName === "textfont-bold") {
    el.style.fontWeight = value === "true" || value === "1" ? "bold" : "normal";
    return;
  }
  if (attrName === "img" || attrName === "src" || attrName === "path") {
    const img = el.querySelector("img");
    if (img) img.src = resolveResourceUrl(value);
    return;
  }
  if (attrName === "imgbk") {
    const url = resolveResourceUrl(value);
    el.style.backgroundImage = `url("${url.replace(/"/g, '\\"')}")`;
    return;
  }
  if (attrName === "title") {
    if (el.hasAttribute("data-xone-breadcrumb")) return;
    const span = el.querySelector(".xone-text-value, .xone-label-text, .xone-btn-text");
    if (span) span.textContent = value;
    else el.textContent = value;
    return;
  }
  if (attrName === "visible") {
    el.style.display = value === "0" || value === "false" ? "none" : "";
    return;
  }
  if (attrName === "locked") {
    const locked = value === "true" || value === "1";
    const inputs = el.querySelectorAll("input, button, textarea, select");
    for (const inp of inputs) inp.disabled = locked;
    return;
  }
}
var DV_FN_CACHE = /* @__PURE__ */ new Map();
function compileDv(js) {
  let fn = DV_FN_CACHE.get(js);
  if (!fn) {
    try {
      fn = compileTranslatedDv(js);
    } catch {
      fn = () => false;
    }
    DV_FN_CACHE.set(js, fn);
  }
  return fn;
}
function applyDisableVisible(el, fields) {
  const js = el.getAttribute("data-disablevisible");
  if (js === null) return;
  let hidden = false;
  try {
    hidden = Boolean(compileDv(js)(fields));
  } catch {
    hidden = false;
  }
  if (hidden) el.setAttribute("hidden", "");
  else el.removeAttribute("hidden");
}
function ownTabs(chassis) {
  return Array.from(chassis.querySelectorAll('[role="tab"][data-group-id]')).filter((t) => t.closest(".xone-tabs-chassis") === chassis);
}
function chassisTabs(box) {
  const chassis = box.closest(".xone-tabs-chassis");
  return chassis ? ownTabs(chassis) : [];
}
function syncTabBar(chassis) {
  const bar = Array.from(chassis.querySelectorAll('[role="tablist"]')).find((b) => b.closest(".xone-tabs-chassis") === chassis);
  if (bar) bar.hidden = ownTabs(chassis).filter((t) => !t.hidden).length < 2;
}
function syncGroupVisibility(fields) {
  if (typeof document === "undefined") return;
  const marked = document.querySelectorAll(".xone-group[data-group-disablevisible]");
  if (marked.length === 0) return;
  const containers = /* @__PURE__ */ new Set();
  for (const panel of marked) {
    if (panel.closest("[data-row-index]")) continue;
    const js = panel.getAttribute("data-group-disablevisible") ?? "";
    let hidden = false;
    try {
      hidden = Boolean(compileDv(js)(fields));
    } catch {
      hidden = false;
    }
    if (hidden) panel.setAttribute("data-group-dv-hidden", "");
    else panel.removeAttribute("data-group-dv-hidden");
    const chassis = panel.closest(".xone-tabs-chassis");
    const gid = panel.getAttribute("data-group-id");
    const tab = chassis && gid ? ownTabs(chassis).find((t) => t.getAttribute("data-group-id") === gid) ?? null : null;
    if (tab) tab.hidden = hidden;
    if (chassis) syncTabBar(chassis);
    if (panel.parentElement) containers.add(panel.parentElement);
  }
  for (const box of containers) {
    const panels = Array.from(box.children).filter(
      (c) => c instanceof HTMLElement && c.matches('.xone-group[role="tabpanel"]')
    );
    const active = panels.find((p) => !p.hidden) ?? null;
    if (active && !active.hasAttribute("data-group-dv-hidden")) continue;
    const target = panels.find((p) => !p.hasAttribute("data-group-dv-hidden")) ?? null;
    if (!target) {
      if (active) active.hidden = true;
      continue;
    }
    const chassis = box.closest(".xone-tabs-chassis");
    const activate = chassis ? chassis["__xoneActivateGroup"] : void 0;
    const tid = target.getAttribute("data-group-id");
    if (typeof activate === "function" && tid) {
      activate(tid, { fireEvents: false });
      continue;
    }
    for (const p of panels) p.hidden = p !== target;
    const tabs = chassisTabs(box);
    for (const t of tabs) {
      const sel = t.getAttribute("data-group-id") === tid;
      t.setAttribute("aria-selected", String(sel));
      t.tabIndex = sel ? 0 : -1;
    }
  }
}
var NON_READONLY_INPUT_TYPES = /* @__PURE__ */ new Set([
  "checkbox",
  "radio",
  "button",
  "submit",
  "reset",
  "file",
  "range",
  "color",
  "image",
  "hidden"
]);
function setEditDisabledState(el, disabled) {
  const tag = el.tagName;
  const type = (el.type ?? "").toLowerCase();
  const useReadonly = tag === "TEXTAREA" || tag === "INPUT" && !NON_READONLY_INPUT_TYPES.has(type);
  if (useReadonly) {
    if (disabled) {
      el.setAttribute("readonly", "");
    } else {
      el.removeAttribute("readonly");
      el.removeAttribute("inert");
    }
  } else {
    if (disabled) el.setAttribute("disabled", "");
    else el.removeAttribute("disabled");
  }
}
function applyDisableEdit(el, fields) {
  const js = el.getAttribute("data-disableedit");
  if (js === null) return;
  let disabled = false;
  try {
    disabled = Boolean(compileDv(js)(fields));
  } catch {
    disabled = false;
  }
  setEditDisabledState(el, disabled);
}
var CONTENTS_EDITOR_SELECTOR = "input:not([type=hidden]), textarea, select, xone-combo, [contenteditable]";
function setContentsHostEditLock(el, locked) {
  const tag = el.tagName;
  const type = (el.type ?? "").toLowerCase();
  const useReadonly = tag === "TEXTAREA" || tag === "INPUT" && !NON_READONLY_INPUT_TYPES.has(type) || el.hasAttribute("contenteditable");
  if (locked) {
    el.setAttribute("data-host-edit-locked", "1");
    if (useReadonly) {
      if (!el.hasAttribute("readonly")) {
        el.setAttribute("readonly", "");
        el.setAttribute("data-host-set-readonly", "1");
      }
    } else if (!el.hasAttribute("disabled")) {
      el.setAttribute("disabled", "");
      el.setAttribute("data-host-set-disabled", "1");
    }
  } else {
    if (!el.hasAttribute("data-host-edit-locked")) return;
    el.removeAttribute("data-host-edit-locked");
    if (el.hasAttribute("data-host-set-readonly")) {
      el.removeAttribute("readonly");
      el.removeAttribute("data-host-set-readonly");
    }
    if (el.hasAttribute("data-host-set-inert")) {
      el.removeAttribute("inert");
      el.removeAttribute("data-host-set-inert");
    }
    if (el.hasAttribute("data-host-set-disabled")) {
      el.removeAttribute("disabled");
      el.removeAttribute("data-host-set-disabled");
    }
  }
}
function applyContentsDisableEdit(container, fields) {
  const js = container.getAttribute("data-contents-disableedit");
  if (js === null) return;
  let locked = false;
  try {
    locked = Boolean(compileDv(js)(fields));
  } catch {
    locked = false;
  }
  if (locked) container.setAttribute("data-contents-edit-locked", "1");
  else container.removeAttribute("data-contents-edit-locked");
  for (const ed of container.querySelectorAll(CONTENTS_EDITOR_SELECTOR)) {
    if (ed.hasAttribute("data-disableedit")) continue;
    if (ed.getAttribute("data-xone-type") === "NC" && ed.closest('[data-stop-propagation="true"]') !== null)
      continue;
    setContentsHostEditLock(ed, locked);
  }
}
function createDomSync(opts) {
  let suppressRestore = false;
  function syncDisableVisibleForField(field, fields) {
    if (typeof document === "undefined") return;
    const needle = `["${field}"]`;
    for (const el of document.querySelectorAll("[data-disablevisible]")) {
      if (el.closest("[data-row-index]")) continue;
      if ((el.getAttribute("data-disablevisible") ?? "").includes(needle)) {
        applyDisableVisible(el, fields);
      }
    }
    for (const g of document.querySelectorAll(".xone-group[data-group-disablevisible]")) {
      if ((g.getAttribute("data-group-disablevisible") ?? "").includes(needle)) {
        syncGroupVisibility(fields);
        break;
      }
    }
  }
  function syncDisableEditForField(field, fields) {
    if (typeof document === "undefined") return;
    const needle = `["${field}"]`;
    for (const el of document.querySelectorAll("[data-disableedit]")) {
      if (el.closest("[data-row-index]")) continue;
      if ((el.getAttribute("data-disableedit") ?? "").includes(needle)) {
        applyDisableEdit(el, fields);
      }
    }
  }
  function syncFldImagesForField(field, fields) {
    if (typeof document === "undefined") return;
    const needle = `##FLD_${field}##`;
    for (const img of document.querySelectorAll("img[data-xone-img-src]")) {
      if (img.closest("[data-row-index]")) continue;
      if ((img.getAttribute("data-xone-img-src") ?? "").includes(needle)) {
        resolveFldImage(img, fields);
      }
    }
  }
  function syncContentsDisableEditForField(field, fields) {
    if (typeof document === "undefined") return;
    const needle = `["${field}"]`;
    for (const el of document.querySelectorAll("[data-contents-disableedit]")) {
      if ((el.getAttribute("data-contents-disableedit") ?? "").includes(needle)) {
        applyContentsDisableEdit(el, fields);
      }
    }
  }
  function syncConditionalsForField(field) {
    if (typeof document === "undefined") return;
    const allFields = opts.getSelf().toJson();
    syncDisableVisibleForField(field, allFields);
    syncDisableEditForField(field, allFields);
    syncContentsDisableEditForField(field, allFields);
  }
  function syncField(field) {
    if (typeof document === "undefined") return;
    const allFields = opts.getSelf().toJson();
    syncDisableVisibleForField(field, allFields);
    syncDisableEditForField(field, allFields);
    syncContentsDisableEditForField(field, allFields);
    restoreMappedFieldsFor(field);
    syncFldImagesForField(field, allFields);
    const value = allFields[field];
    if (value === void 0) return;
    const raw = value === null ? "" : String(value);
    let str = raw;
    if (field.startsWith("MAP_")) {
      str = str.replace(/\bundefined\b/g, "").replace(/\bnull\b/g, "");
      str = str.replace(/[,\s]+$/, "").trim();
    }
    const inputStr = raw;
    const displayEls = document.querySelectorAll(
      `[data-prop="${field}"] .xone-text-value, span[data-prop="${field}"], div[data-prop="${field}"]`
    );
    for (const el of displayEls) {
      if (el.closest("[data-row-index]")) continue;
      if (el.hasAttribute("data-prop") && el.querySelector(".xone-text-value")) continue;
      if (el.hasAttribute("data-xone-breadcrumb")) continue;
      if (el.classList.contains("xone-prop-img") || el.classList.contains("xone-prop-ph")) {
        syncImgFieldValue(el, raw);
        continue;
      }
      if (el.getAttribute("data-xone-type") === "THTML") {
        if (el.hasAttribute("data-xone-static") && raw === "" && el.innerHTML !== "") continue;
        if (el.innerHTML !== raw) {
          el.innerHTML = raw;
          rebindInlineHandlers(el);
        }
        continue;
      }
      const dateFieldType = dateFieldTypeOf(el);
      const outStr = dateFieldType ? formatXoneDisplayDate(value === null ? "" : value, dateFieldType) : str;
      if (el.hasAttribute("data-xone-static") && outStr === "" && el.textContent !== "") continue;
      if (el.textContent !== outStr) el.textContent = outStr;
    }
    const inputs = document.querySelectorAll(
      `[data-prop="${field}"] input, [data-prop="${field}"] textarea`
    );
    for (const inp of inputs) {
      if (inp.closest("[data-row-index]")) continue;
      const isCheckLike = inp instanceof HTMLInputElement && (inp.type === "checkbox" || inp.type === "radio" || inp.getAttribute("data-xone-type") === "NC");
      if (!isCheckLike && typeof document !== "undefined" && document.activeElement === inp) continue;
      if (inp instanceof HTMLInputElement && (inp.type === "checkbox" || inp.type === "radio" || inp.getAttribute("data-xone-type") === "NC")) {
        const checked = str !== "" && str !== "0" && str.toLowerCase() !== "false";
        if (inp.checked !== checked) inp.checked = checked;
        continue;
      }
      if (inp instanceof HTMLInputElement && (inp.type === "date" || inp.type === "datetime-local" || inp.type === "time")) {
        const inpVal2 = formatXoneInputDate(value, inp.type);
        if (inp.value !== inpVal2) inp.value = inpVal2;
        continue;
      }
      const inpVal = normalizeDateInputValue(inp, inputStr);
      if (inp.value !== inpVal) inp.value = inpVal;
    }
    const selects = document.querySelectorAll(
      `select[data-prop="${field}"]`
    );
    for (const sel of selects) {
      if (sel.closest("[data-row-index]")) continue;
      if (sel.value !== str) sel.value = str;
    }
    const reactiveEls = document.querySelectorAll("[data-reactive-attrs]");
    if (reactiveEls.length > 0) {
      const fields = opts.getSelf().toJson();
      for (const el of reactiveEls) {
        const rawJson = el.getAttribute("data-reactive-attrs");
        if (!rawJson || !rawJson.includes(field)) continue;
        try {
          const bindings = JSON.parse(rawJson);
          for (const b of bindings) {
            if (!b.deps.includes(field)) continue;
            const resolved = b.tpl.replace(
              /##FLD_([A-Za-z0-9_]+)##/g,
              (_m, f) => {
                const v = fields[f];
                return v == null ? "" : String(v);
              }
            );
            patchPropAttributeDOM(el, b.attr, resolved);
          }
        } catch {
        }
      }
    }
  }
  function restoreMappedFieldsFor(field) {
    if (suppressRestore) return;
    for (const el of document.querySelectorAll(`xone-combo[data-combo-idprop="${field}"]`)) {
      if (el.closest("[data-row-index]")) continue;
      try {
        el.restoreMappedFields?.();
      } catch {
      }
    }
  }
  function syncAll() {
    if (typeof document === "undefined") return;
    const fields = opts.getSelf().toJson();
    suppressRestore = true;
    try {
      for (const field of Object.keys(fields)) {
        syncField(field);
      }
    } finally {
      suppressRestore = false;
    }
    for (const el of document.querySelectorAll("[data-disablevisible]")) {
      if (el.closest("[data-row-index]")) continue;
      applyDisableVisible(el, fields);
    }
    syncGroupVisibility(fields);
    for (const el of document.querySelectorAll("[data-disableedit]")) {
      if (el.closest("[data-row-index]")) continue;
      applyDisableEdit(el, fields);
    }
    for (const el of document.querySelectorAll("[data-contents-disableedit]")) {
      applyContentsDisableEdit(el, fields);
    }
    for (const img of document.querySelectorAll("img[data-xone-img-src]")) {
      if (img.closest("[data-row-index]")) continue;
      resolveFldImage(img, fields);
    }
  }
  function patchPropAttribute(propName, attrName, value) {
    if (typeof document === "undefined") return;
    const els = document.querySelectorAll(`[data-prop="${propName}"]`);
    for (const el of els) {
      if (el.closest("[data-row-index]")) continue;
      if (el.closest('xone-island[data-type="Z"]')) continue;
      if (BUTTON_VISUAL_EXCLUDE_ATTRS.has(attrName) && el.tagName === "BUTTON" && el.closest('xone-island[data-type="B"]')) {
        continue;
      }
      patchPropAttributeDOM(el, attrName, value);
    }
  }
  function syncReactiveAttrs(field, selfFields, reactiveDeps, macroExpand) {
    if (typeof document === "undefined" || !reactiveDeps) return;
    const bindings = reactiveDeps[field];
    if (!bindings) return;
    for (const { attr, tpl, deps: _deps } of bindings) {
      let resolved = tpl;
      resolved = resolved.replace(/##FLD_([A-Za-z0-9_]+)##/g, (_m, f) => {
        const val = selfFields.get(f);
        return val == null ? "" : String(val);
      });
      if (macroExpand && resolved.includes("##")) {
        resolved = macroExpand(resolved);
      }
      const els = document.querySelectorAll(`[data-prop]`);
      for (const el of els) {
        if (el.dataset["prop"] !== _deps[0]) continue;
      }
      const islands = document.querySelectorAll(`[data-reactive-attrs]`);
      for (const island of islands) {
        const raw = island.getAttribute("data-reactive-attrs");
        if (!raw || !raw.includes(field)) continue;
        try {
          const islandBindings = JSON.parse(raw);
          for (const ib of islandBindings) {
            if (ib.attr === attr && ib.tpl === tpl) {
              patchPropAttributeDOM(island, attr, resolved);
            }
          }
        } catch {
        }
      }
    }
  }
  return {
    syncAll,
    syncField,
    syncConditionalsForField,
    patchPropAttribute,
    syncReactiveAttrs,
    dispose() {
    }
  };
}

// src/macros.ts
var MACRO_RE = /##([A-Za-z0-9_]+)##/g;
function createMacroStore(options) {
  const store = /* @__PURE__ */ new Map();
  let changePending = false;
  function scheduleNotify() {
    if (!options?.onChanged || changePending) return;
    changePending = true;
    queueMicrotask(() => {
      changePending = false;
      options.onChanged();
    });
  }
  const get = (name) => {
    const v = store.get(name);
    return v ?? null;
  };
  const set = (name, value) => {
    if (value == null) {
      store.delete(name);
    } else {
      store.set(name, String(value));
    }
    scheduleNotify();
  };
  const expand = (text) => {
    if (typeof text !== "string") return text;
    if (!text.includes("##")) return text;
    return text.replace(MACRO_RE, (full, name) => {
      const v = store.get(name);
      return v ?? full;
    });
  };
  const snapshot = () => {
    return Object.fromEntries(store);
  };
  return { get, set, expand, snapshot };
}

// src/session.ts
var KEY = "xone:auth";
function createSession(opts) {
  let cached = null;
  let revoked = false;
  if (opts.storage) {
    try {
      const raw = opts.storage.getItem(KEY);
      if (raw) {
        cached = JSON.parse(raw);
      }
    } catch {
      cached = null;
    }
  }
  function persist(s) {
    if (!opts.storage) return;
    try {
      if (s === null) opts.storage.removeItem(KEY);
      else opts.storage.setItem(KEY, JSON.stringify(s));
    } catch {
    }
  }
  function syncMacros(s) {
    opts.macros.set("USERID", s?.userId !== void 0 ? String(s.userId) : null);
    opts.macros.set("EMPRESA", s?.empresa !== void 0 ? String(s.empresa) : null);
    opts.macros.set("TOKEN", s?.token ?? null);
    opts.macros.set("ROL", s?.rol ?? null);
  }
  syncMacros(cached);
  return {
    get() {
      return cached;
    },
    set(s) {
      if (s) revoked = false;
      cached = s;
      persist(s);
      syncMacros(s);
    },
    clear() {
      cached = null;
      persist(null);
      syncMacros(null);
    },
    markRevoked() {
      revoked = true;
    },
    isRevoked() {
      return revoked;
    },
    isAuthenticated() {
      return Boolean(cached?.token);
    }
  };
}

// src/stateStore.ts
var OBJ_PREFIX = "xone:obj:";
var COLL_PREFIX = "xone:coll:";
var MACROS_KEY = "xone:macros";
function safeStateStringify(value) {
  const seen = /* @__PURE__ */ new WeakSet();
  let dropped = 0;
  const json = JSON.stringify(value, (_key, val) => {
    if (typeof val === "object" && val !== null) {
      if (seen.has(val)) {
        dropped++;
        return void 0;
      }
      seen.add(val);
    }
    return val;
  });
  if (dropped > 0) {
    try {
      console.warn(
        "[xone-state] " + dropped + " referencia(s) circular(es)/compartida(s) descartada(s) al serializar el estado de navegaci\xF3n (WEAL-127). Causa probable: un campo tipo O del self contiene un DataObject vivo con auto-referencia (p.ej. self.MAP_CLIENTE tras 'Pagar transferencia'). La navegaci\xF3n contin\xFAa; ese campo no se restaura al volver."
      );
    } catch {
    }
  }
  return json;
}
var SESSION_DERIVED_MACROS = /* @__PURE__ */ new Set([
  "TOKEN",
  "REFRESH_TOKEN",
  "USERID",
  "EMPRESA",
  "ROL",
  "CURRENT_LANGUAGE",
  "API_BASE",
  "PREF",
  "APPNAME",
  "ENTRY_POINT",
  "LOGIN_COLLECTION",
  "APP_VERSION",
  "DEVICE_ID"
]);
function createStateStore(storage) {
  function safeSet(key, value) {
    if (!storage) return;
    try {
      storage.setItem(key, value);
    } catch {
      emergencyCleanup();
      try {
        storage.setItem(key, value);
      } catch {
      }
    }
  }
  function safeGet(key) {
    if (!storage) return null;
    try {
      return storage.getItem(key);
    } catch {
      return null;
    }
  }
  function safeRemove(key) {
    if (!storage) return;
    try {
      storage.removeItem(key);
    } catch {
    }
  }
  function emergencyCleanup() {
    if (!storage) return;
    const toRemove = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k && (k.startsWith(OBJ_PREFIX) || k.startsWith(COLL_PREFIX))) {
        toRemove.push(k);
      }
    }
    const half = Math.ceil(toRemove.length / 2);
    for (let i = 0; i < half; i++) {
      try {
        storage.removeItem(toRemove[i]);
      } catch {
      }
    }
  }
  function filterSessionMacros(macros) {
    const filtered = {};
    for (const [k, v] of Object.entries(macros)) {
      if (!SESSION_DERIVED_MACROS.has(k)) {
        filtered[k] = v;
      }
    }
    return filtered;
  }
  return {
    saveObjectState(id, state) {
      const compactFields = {};
      for (const [k, v] of Object.entries(state.fields)) {
        if (v != null) compactFields[k] = v;
      }
      const compact = { ...state, fields: compactFields };
      safeSet(OBJ_PREFIX + id, safeStateStringify(compact));
    },
    loadObjectState(id) {
      const raw = safeGet(OBJ_PREFIX + id);
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    },
    removeObjectState(id) {
      safeRemove(OBJ_PREFIX + id);
    },
    saveCollectionState(id, state) {
      safeSet(COLL_PREFIX + id, safeStateStringify(state));
    },
    loadCollectionState(id) {
      const raw = safeGet(COLL_PREFIX + id);
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    },
    removeCollectionState(id) {
      safeRemove(COLL_PREFIX + id);
    },
    saveMacros(macros) {
      const filtered = filterSessionMacros(macros);
      if (Object.keys(filtered).length === 0) {
        safeRemove(MACROS_KEY);
        return;
      }
      safeSet(MACROS_KEY, JSON.stringify(filtered));
    },
    loadMacros() {
      const raw = safeGet(MACROS_KEY);
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    },
    getUsage() {
      if (!storage) return 0;
      let bytes = 0;
      try {
        for (let i = 0; i < storage.length; i++) {
          const k = storage.key(i);
          if (k && k.startsWith("xone:")) {
            bytes += k.length + (storage.getItem(k)?.length ?? 0);
          }
        }
      } catch {
      }
      return bytes * 2;
    },
    pruneOrphans(activeIds) {
      if (!storage) return;
      const toRemove = [];
      try {
        for (let i = 0; i < storage.length; i++) {
          const k = storage.key(i);
          if (!k) continue;
          let id = null;
          if (k.startsWith(OBJ_PREFIX)) id = k.slice(OBJ_PREFIX.length);
          else if (k.startsWith(COLL_PREFIX)) id = k.slice(COLL_PREFIX.length);
          if (id && !activeIds.has(id)) toRemove.push(k);
        }
        for (const k of toRemove) storage.removeItem(k);
      } catch {
      }
    }
  };
}

// src/createAppContext.ts
import {
  createDataCollection as createDataCollection2,
  createDataObject,
  isPrivateOrReservedProp,
  MemoryStorageAdapter as MemoryStorageAdapter2,
  trackPendingWrite,
  whenPendingLoadsSettled
} from "@xone-web/data";
function isDehydratedDataObject(v) {
  if (!v || typeof v !== "object" || Array.isArray(v)) return false;
  const owner = v["_owner"];
  return typeof owner === "object" && owner !== null && typeof owner["_name"] === "string";
}
function runProjectNodeByName(name, ...args) {
  const w = typeof window !== "undefined" ? window : globalThis;
  return typeof w.__xoneRunNode === "function" ? Promise.resolve(w.__xoneRunNode(String(name), args)) : Promise.resolve(void 0);
}
function createDetachedParentObject(snapshot, resolveClone, resolveOwnerObject) {
  const owner = snapshot["_owner"];
  const ownerName = owner?._name ?? "";
  const ownerObject = () => {
    if (!resolveOwnerObject) return null;
    try {
      return resolveOwnerObject() ?? null;
    } catch {
      return null;
    }
  };
  const detachedOwnerColl = {
    getName: () => ownerName,
    getOwnerObject: ownerObject,
    ownerObject
  };
  const makeDetachedContents = (name) => ({
    unlock: () => {
    },
    lock: () => {
    },
    clear: () => {
    },
    loadAll: async () => {
    },
    setMacro: () => {
    },
    setFilter: () => {
    },
    setSort: () => {
    },
    getFilter: () => "",
    getSort: () => "",
    count: () => 0,
    getCount: () => 0,
    getItem: () => null,
    getCurrentItem: () => null,
    refresh: async () => {
    },
    createClone: () => resolveClone ? resolveClone(name) : makeDetachedContents(name)
  });
  const api = {
    getOwnerCollection: () => detachedOwnerColl,
    getContents: (name) => makeDetachedContents(String(name ?? "")),
    contents: (name) => makeDetachedContents(String(name ?? "")),
    getParent: () => {
      const p = snapshot["_parent"];
      return isDehydratedDataObject(p) ? createDetachedParentObject(p, resolveClone) : null;
    },
    getOwnerObject: () => null,
    getValue: (field) => snapshot[field],
    setValue: (field, value) => {
      snapshot[field] = value;
    },
    getFieldPropertyValue: () => void 0,
    setFieldPropertyValue: (_field, _attr, value) => value,
    getVariable: () => void 0,
    setVariable: () => {
    },
    isNew: () => Boolean(snapshot["_isNew"]),
    getDirty: () => false,
    save: async () => true,
    deleteObject: async () => true,
    refresh: async () => true,
    toJson: () => ({ ...snapshot })
  };
  return new Proxy(snapshot, {
    get(target, prop) {
      if (typeof prop === "string" && prop in api) return api[prop];
      return Reflect.get(target, prop);
    },
    set(target, prop, value) {
      return Reflect.set(target, prop, value);
    }
  });
}
function createAppContext(opts = {}) {
  const storage = opts.storage ?? (typeof window !== "undefined" && window.sessionStorage ? window.sessionStorage : null);
  const navStack = createNavStack(storage);
  const stateStore = createStateStore(storage);
  const navDirection = consumeNavDirection(storage);
  const currentCollName = opts.collectionName ?? "";
  let currentNavEntry = null;
  if (navDirection === "back") {
    const top = navStack.peek();
    if (top && top.collection !== currentCollName) {
      navStack.pop();
    }
    currentNavEntry = navStack.peek();
  } else if (navDirection === "forward") {
    currentNavEntry = navStack.peek();
  } else {
    const top = navStack.peek();
    if (top && top.collection === currentCollName) {
      currentNavEntry = top;
    } else {
      const prev = navStack.peekPrevious();
      if (prev && prev.collection === currentCollName) {
        navStack.pop();
        currentNavEntry = navStack.peek();
      }
    }
  }
  if (!currentNavEntry && currentCollName) {
    const rootEntry = {
      id: generateId(),
      collection: currentCollName,
      url: typeof window !== "undefined" ? window.location.pathname : "",
      params: {},
      parentEntryId: null,
      contentName: null,
      objectStateId: generateId(),
      collectionStateId: generateId(),
      isNewObject: false,
      scrollY: 0,
      timestamp: Date.now()
    };
    navStack.push(rootEntry);
    currentNavEntry = rootEntry;
  }
  const macros = createMacroStore({
    onChanged: () => {
      stateStore.saveMacros(macros.snapshot());
    }
  });
  const savedMacros = stateStore.loadMacros();
  if (savedMacros) {
    for (const [k, v] of Object.entries(savedMacros)) {
      macros.set(k, v);
    }
  }
  const session = createSession({ storage, macros });
  if (typeof navigator !== "undefined") {
    macros.set("CURRENT_LANGUAGE", navigator.language ?? "es");
  }
  macros.set("API_BASE", opts.apiBase ?? "/api/v1");
  const routeMap = /* @__PURE__ */ new Map();
  if (opts.routeMap) {
    for (const [k, v] of Object.entries(opts.routeMap)) {
      routeMap.set(k, v);
    }
  }
  if (opts.tablePrefix) {
    macros.set("PREF", opts.tablePrefix);
  }
  if (opts.appName) {
    macros.set("APPNAME", opts.appName);
  }
  if (opts.appVersion) {
    macros.set("VERSION", opts.appVersion);
    macros.set("APP_VERSION", opts.appVersion);
  }
  const backend = opts.backend ?? "generated";
  const effectiveApiBase = opts.apiBase ?? (backend === "webcore" ? "/api/itf" : "/api/v1");
  let forceRelogin = null;
  let unauthorizedHandled = false;
  const isOnLoginPage = () => {
    const loginColl = (macros.get("LOGIN_COLLECTION") ?? "").trim();
    return loginColl !== "" && currentCollName === loginColl || typeof window !== "undefined" && window.location.pathname === loginCollectionPath(loginColl || null);
  };
  const sessionScope = backend === "webcore" ? apiBaseSessionScope(effectiveApiBase) : void 0;
  const onUnauthorized = backend === "webcore" ? (info) => {
    if (unauthorizedHandled) return;
    if (isOnLoginPage()) return;
    if (isNavigationInProgress()) return;
    if (!forceRelogin) return;
    unauthorizedHandled = true;
    console.warn(
      `[xone] HTTP ${info.status} en ${info.url} con el token de sesi\xF3n (${info.authSource}${info.sessionLost ? ", sesi\xF3n ya perdida: no enviada" : ""}): sesi\xF3n no v\xE1lida \u2192 re-login.`
    );
    forceRelogin();
  } : void 0;
  const canRevokeSession = () => !isOnLoginPage();
  const http = createHttp({
    macros,
    session,
    apiBase: opts.apiBase ?? "/api/v1",
    sessionScope,
    onUnauthorized,
    canRevokeSession
  });
  const cryptoApi = createCryptoApi();
  const clipboardApi = createClipboardApi();
  const Bundle = createBundleCtor();
  const hasMpaRoutes = opts.routeMap !== void 0 && Object.keys(opts.routeMap).length > 0;
  const manifestUrl = opts.manifestUrl ?? "/xone-static-manifest.json";
  const manifestPromise = typeof fetch === "function" ? fetch(manifestUrl).then((r) => r.ok ? r.json() : null).then((m) => {
    if (!m) return null;
    if (!hasMpaRoutes) {
      const map = buildRouteMap(m);
      for (const [k, v] of map) routeMap.set(k, v);
      macros.set("PREF", m.project.tablePrefix);
      macros.set("APPNAME", m.project.title ?? m.project.name);
    }
    return m;
  }).catch((err) => {
    console.warn("[xone] manifest no cargado:", err);
    return null;
  }) : Promise.resolve(null);
  const resolvedProjectId = opts.projectId ?? opts.appName?.toLowerCase().replace(/\s+/g, "") ?? void 0;
  const shellOverlay = opts.layoutShellColl ? createShellOverlay(storage) : null;
  const dataLayer = createDataLayer({
    apiBase: effectiveApiBase,
    macros,
    session,
    manifestPromise,
    projectId: resolvedProjectId,
    backend,
    filterParser: opts.filterParser,
    // IDS-AUTH: mismo hook y mismo ámbito para el `$http` interno del backend WebCore.
    onUnauthorized,
    sessionScope,
    canRevokeSession,
    shellOverlay,
    layoutShellColl: opts.layoutShellColl
  });
  const PCQ_PREFIX = "xone:pcq:";
  const DISCARD_KEY = "xone:discardNew";
  const UPSERT_KEY = "xone:upsertNew";
  const takeMarker = (key) => {
    try {
      const raw = storage?.getItem(key);
      if (!raw) return null;
      storage?.removeItem(key);
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };
  {
    const discardM = takeMarker(DISCARD_KEY);
    const upsertM = takeMarker(UPSERT_KEY);
    if (currentNavEntry) {
      try {
        const raw = storage?.getItem(PCQ_PREFIX + currentNavEntry.id);
        if (raw) {
          const states = JSON.parse(raw);
          if (discardM?.coll && discardM.rowid && states[discardM.coll]?.rows) {
            states[discardM.coll].rows = states[discardM.coll].rows.filter(
              (r) => String(r["ROWID"] ?? "") !== discardM.rowid
            );
          }
          if (upsertM?.coll && upsertM.rowid && upsertM.fields && states[upsertM.coll]) {
            const st = states[upsertM.coll];
            const rows = [...st.rows ?? []];
            const idx = rows.findIndex((r) => String(r["ROWID"] ?? "") === upsertM.rowid);
            if (idx >= 0) rows[idx] = { ...rows[idx], ...upsertM.fields };
            else rows.push(upsertM.fields);
            st.rows = rows;
            st.refetchRows = true;
          }
          dataLayer.restoreQueryStates(states);
        }
      } catch {
      }
    }
  }
  if (typeof window !== "undefined") {
    const pcqEntryId = currentNavEntry?.id ?? null;
    window.addEventListener("pagehide", () => {
      if (!pcqEntryId) return;
      try {
        const states = dataLayer.snapshotQueryStates();
        if (Object.keys(states).length > 0) {
          storage?.setItem(PCQ_PREFIX + pcqEntryId, JSON.stringify(states));
        } else {
          storage?.removeItem(PCQ_PREFIX + pcqEntryId);
        }
      } catch {
      }
    });
  }
  const cloneResolver = (name) => dataLayer.getCollection(name).createClone();
  const emptyDetachedParent = () => createDetachedParentObject({ _owner: { _name: "" }, _isNew: false }, cloneResolver);
  const resolvePageParent = () => {
    try {
      const cur = navStack.peek();
      let parentEntry = null;
      if (cur?.parentEntryId) {
        parentEntry = navStack.getStack().find((e) => e.id === cur.parentEntryId) ?? null;
      }
      if (!parentEntry) parentEntry = navStack.peekPrevious();
      if (!parentEntry?.objectStateId) return emptyDetachedParent();
      const pState = stateStore.loadObjectState(parentEntry.objectStateId);
      if (!pState) return emptyDetachedParent();
      const snap = {
        ...pState.fields,
        _owner: { _name: parentEntry.collection },
        _isNew: pState.isNew
      };
      return createDetachedParentObject(snap, cloneResolver);
    } catch {
      return emptyDetachedParent();
    }
  };
  const MAX_OWNER_DEPTH = 32;
  const openerOwnerOf = (entry, depth) => {
    if (!entry?.contentName || !entry.parentEntryId || depth > MAX_OWNER_DEPTH) return null;
    const parent = navStack.getStack().find((e) => e.id === entry.parentEntryId) ?? null;
    if (!parent?.objectStateId) return null;
    const pState = stateStore.loadObjectState(parent.objectStateId);
    if (!pState) return null;
    const snap = {
      ...pState.fields,
      _owner: { _name: parent.collection },
      _isNew: pState.isNew
    };
    return createDetachedParentObject(snap, cloneResolver, () => openerOwnerOf(parent, depth + 1));
  };
  const resolvePageOwnerObject = () => {
    try {
      return openerOwnerOf(currentNavEntry ?? navStack.peek(), 0);
    } catch {
      return null;
    }
  };
  const withPageOwner = (coll) => {
    if (!coll || typeof coll !== "object") return coll;
    return new Proxy(coll, {
      get(t, p) {
        if (p === "getOwnerObject" || p === "ownerObject") {
          return () => {
            let own = Reflect.get(t, p);
            if (typeof own !== "function") own = Reflect.get(t, "getOwnerObject");
            const v2 = typeof own === "function" ? own.call(t) : null;
            return v2 ?? resolvePageOwnerObject();
          };
        }
        const v = Reflect.get(t, p);
        return typeof v === "function" ? v.bind(t) : v;
      }
    });
  };
  let _exitHandler = null;
  const _fireExit = () => {
    if (_exitHandler) {
      _exitHandler();
      return true;
    }
    return false;
  };
  const appData = createAppData({
    http,
    macros,
    session,
    dataLayerGetCollection: dataLayer.getCollection,
    navStack,
    stateStore,
    storage,
    backend,
    apiBase: effectiveApiBase,
    onExit: _fireExit,
    // Limpiar el overlay del menú al cerrar/cambiar sesión (paridad con el
    // bag de sesión de `user`): un login nuevo no debe heredar marcas. La
    // CLAVE es global por-pestaña, así que se borra del `sessionStorage`
    // AUNQUE esta página no tenga shell (la página de LOGIN normalmente no lo
    // tiene, y su `appData.login()` debe poder limpiar el residuo de la
    // sesión anterior). Si además hay overlay en memoria (páginas con shell,
    // p.ej. el logout desde el header del layout), se limpia también su
    // estado vivo.
    clearShellOverlay: () => {
      shellOverlay?.clear();
      clearShellOverlayStorage(storage);
    }
    // IDS-AUTH: sin revocación server-side todavía (WebCore aún no expone
    // `/connect/revocation`); el punto de enganche es `onLogout` en appData.
  });
  const RELOGIN_HEAL_MS = 8e3;
  const RELOGIN_MAX_RETRIES = 3;
  let reloginAttempts = 0;
  let expiredNoticeShown = false;
  const pageIsDirty = () => {
    try {
      return typeof self?.getDirty === "function" && self.getDirty() === true;
    } catch {
      return false;
    }
  };
  const goToLogin = () => {
    try {
      storage?.setItem(LOGIN_NOTICE_KEY, "expired");
    } catch {
    }
    try {
      appData.exit({ replace: true, programmatic: true });
    } finally {
      reloginAttempts++;
      if (reloginAttempts <= RELOGIN_MAX_RETRIES) {
        setTimeout(() => {
          unauthorizedHandled = false;
        }, RELOGIN_HEAL_MS);
      }
    }
  };
  forceRelogin = () => {
    if (!expiredNoticeShown && pageIsDirty()) {
      expiredNoticeShown = true;
      void showSessionExpiredNotice().then(goToLogin, goToLogin);
      return;
    }
    goToLogin();
  };
  if (backend === "webcore" && typeof window !== "undefined") {
    window.setTimeout(() => {
      try {
        if (storage?.getItem(LOGIN_NOTICE_KEY) !== "expired" || !isOnLoginPage()) return;
        storage.removeItem(LOGIN_NOTICE_KEY);
        showToast(SESSION_EXPIRED_LOGIN_MESSAGE, { durationMs: 8e3 });
      } catch {
      }
    }, 0);
  }
  let restoredObjectState = null;
  if (currentNavEntry?.objectStateId) {
    restoredObjectState = stateStore.loadObjectState(currentNavEntry.objectStateId);
  }
  const restoredCollectionState = currentNavEntry?.collectionStateId ? stateStore.loadCollectionState(currentNavEntry.collectionStateId) : null;
  void restoredCollectionState;
  let self;
  let ThisDataColl;
  const fireFieldOnchange = (field, oldVal, newVal) => {
    if (Object.is(oldVal, newVal)) return;
    if (oldVal instanceof Date && newVal instanceof Date && oldVal.getTime() === newVal.getTime()) return;
    const w = typeof window !== "undefined" ? window : globalThis;
    try {
      w.__xoneDispatchFieldOnchange?.(self, currentCollName, field, newVal);
    } catch {
    }
  };
  const pageContentsList = () => {
    const def = opts.collectionDefinition;
    return def?.contents ?? [];
  };
  const resolvePageContents = (nameOrIndex) => {
    const list = pageContentsList();
    let entry;
    if (typeof nameOrIndex === "number") {
      entry = list[nameOrIndex];
    } else {
      const bare = nameOrIndex.startsWith("@") ? nameOrIndex.slice(1) : nameOrIndex;
      entry = list.find((c) => c.name === nameOrIndex) ?? list.find((c) => c.name === bare || c.name === `@${bare}`) ?? list.find((c) => c.src === bare);
    }
    if (!entry) {
      console.warn(
        `[xone] getContents("${String(nameOrIndex)}"): <contents> no declarado en "${opts.collectionName ?? "?"}". Devolviendo null (paridad Android).`
      );
      return null;
    }
    const instanceName = entry.name.startsWith("@") ? entry.name.slice(1) : entry.name;
    const wrapper = dataLayer.getCollection(instanceName, entry.src);
    wrapper.setOwnerObject?.(self);
    const nodeFilter = entry.attributes?.["filter"] ?? entry.attributes?.["linkfilter"];
    if (nodeFilter && typeof wrapper.setLinkFilter === "function") {
      wrapper.setLinkFilter(nodeFilter);
    }
    const nodeSort = entry.attributes?.["sort"];
    if (nodeSort && typeof wrapper.getSort === "function" && !wrapper.getSort()) {
      wrapper.setSort?.(nodeSort);
    }
    return wrapper;
  };
  let _domSyncRef = null;
  const _propCache = /* @__PURE__ */ new Map();
  if (opts.collectionDefinition) {
    const def = opts.collectionDefinition;
    const isSpecial = def.special === true;
    if (isSpecial) {
      const memStorage = new MemoryStorageAdapter2();
      const coll = createDataCollection2({
        name: opts.collectionName ?? def.name,
        definition: def,
        storage: memStorage
      });
      let restoredInitial;
      if (restoredObjectState) {
        restoredInitial = {};
        for (const [k, v] of Object.entries(restoredObjectState.fields)) {
          restoredInitial[k] = isDehydratedDataObject(v) ? createDetachedParentObject(v, cloneResolver) : v;
        }
      }
      const obj = createDataObject({
        owner: coll,
        isNew: restoredObjectState ? restoredObjectState.isNew : true,
        ...restoredInitial ? { initial: restoredInitial } : {},
        // WEAL-274: restaurar las semillas de tipo sin asignar.
        ...restoredObjectState?.isNew && restoredObjectState.typeSeeded?.length ? { typeSeeded: new Set(restoredObjectState.typeSeeded) } : {}
      });
      const objAny = obj;
      const origSFP = objAny["setFieldPropertyValue"];
      if (typeof origSFP === "function") {
        const bound = origSFP.bind(obj);
        objAny["setFieldPropertyValue"] = (field, attr, value) => {
          const ret = bound(field, attr, value);
          const str = value == null ? null : String(value);
          if (_domSyncRef && str !== null) {
            _domSyncRef.patchPropAttribute(field, attr, str);
          }
          return ret;
        };
      }
      objAny["getContents"] = (nameOrIndex) => resolvePageContents(nameOrIndex);
      objAny["contents"] = (nameOrIndex) => resolvePageContents(nameOrIndex);
      objAny["getContentsCount"] = () => pageContentsList().length;
      objAny["getParent"] = () => resolvePageParent();
      for (const alias of ["executeNode", "ExecuteNode"]) {
        Object.defineProperty(objAny, alias, {
          value: runProjectNodeByName,
          writable: true,
          enumerable: false,
          configurable: true
        });
      }
      let ownerView = null;
      self = new Proxy(obj, {
        get(target, prop) {
          if (prop === "getOwnerCollection" || prop === "ownerCollection") {
            const goc = Reflect.get(target, prop);
            const viewOf = (c) => {
              if (!ownerView || ownerView.coll !== c) ownerView = { coll: c, view: withPageOwner(c) };
              return ownerView.view;
            };
            if (typeof goc === "function") return () => viewOf(goc.call(target));
            if (goc && typeof goc === "object") return viewOf(goc);
          }
          const v = Reflect.get(target, prop);
          return typeof v === "function" ? v.bind(target) : v;
        },
        set(target, prop, value) {
          const ok = Reflect.set(target, prop, value);
          if (ok && typeof prop === "string" && _domSyncRef) {
            _domSyncRef.syncField(prop);
          }
          return ok;
        }
      });
      ThisDataColl = coll;
    } else {
      const wrapper = dataLayer.getCollection(opts.collectionName ?? def.name);
      ThisDataColl = wrapper;
      const selfFields = /* @__PURE__ */ new Map();
      if (restoredObjectState) {
        for (const [k, v] of Object.entries(restoredObjectState.fields)) {
          selfFields.set(k, isDehydratedDataObject(v) ? createDetachedParentObject(v, cloneResolver) : v);
        }
      }
      const originalSnapshot = new Map(selfFields);
      const dirtySet = /* @__PURE__ */ new Set();
      let isNewFlag = restoredObjectState ? restoredObjectState.isNew : true;
      const pendingTypeSeeded = new Set(
        restoredObjectState?.isNew ? restoredObjectState.typeSeeded ?? [] : []
      );
      const sameVal = (a, b) => Object.is(a, b) || a instanceof Date && b instanceof Date && a.getTime() === b.getTime();
      const trackDirty = (field, value) => {
        if (sameVal(originalSnapshot.get(field), value)) dirtySet.delete(field);
        else dirtySet.add(field);
      };
      const resolveRealColl = async () => {
        const wrapper2 = ThisDataColl;
        if (typeof wrapper2.ready !== "function") return null;
        return await wrapper2.ready();
      };
      const commitSnapshot = () => {
        originalSnapshot.clear();
        for (const [k, v] of selfFields) originalSnapshot.set(k, v);
        dirtySet.clear();
      };
      const _ownerCollVars = /* @__PURE__ */ new Map();
      const augmentedOwnerColl = withPageOwner(new Proxy(
        ThisDataColl,
        {
          get(t, p) {
            if (typeof p === "string" && (p === "setVariable" || p === "setVariables") && typeof Reflect.get(t, p) !== "function") {
              return (n, v) => {
                _ownerCollVars.set(String(n), v);
              };
            }
            if (typeof p === "string" && (p === "getVariable" || p === "getVariables") && typeof Reflect.get(t, p) !== "function") {
              return (n) => _ownerCollVars.get(String(n));
            }
            return Reflect.get(t, p);
          }
        }
      ));
      const runNodeByName = runProjectNodeByName;
      self = new Proxy(
        {
          getValue(field) {
            return selfFields.get(field);
          },
          setValue(field, value) {
            if (isPrivateOrReservedProp(field)) {
              selfFields.set(field, value);
              return;
            }
            const old = selfFields.get(field);
            selfFields.set(field, value);
            pendingTypeSeeded.delete(field);
            trackDirty(field, value);
            if (_domSyncRef) _domSyncRef.syncField(field);
            fireFieldOnchange(field, old, value);
          },
          toJson() {
            return Object.fromEntries(selfFields);
          },
          // WEAL-274: misma API interna que `DataObject` para que el
          // snapshot de la ficha (ir a una hija y volver) conserve las
          // semillas de tipo sin asignar.
          _unassignedTypeSeeded: () => isNewFlag ? [...pendingTypeSeeded] : [],
          // Envuelto en trackPendingWrite para que la navegación espere
          // TODA la operación (incl. `resolveRealColl`), no solo el
          // `realObj.save()` interno — así no hay hueco sin registrar
          // entre la preparación y la escritura real.
          save: () => trackPendingWrite((async () => {
            const coll = await resolveRealColl();
            if (!coll) return true;
            if (isNewFlag) {
              const insertFields = Object.fromEntries(
                [...selfFields].filter(([k]) => !isPrivateOrReservedProp(k))
              );
              const realObj = coll._instantiate(
                insertFields,
                true,
                pendingTypeSeeded.size > 0 ? new Set(pendingTypeSeeded) : void 0
              );
              realObj._suppressOnchangeDispatch = true;
              await realObj.save();
              const newRowid = realObj.getValue("ROWID");
              if (newRowid != null) selfFields.set("ROWID", newRowid);
              const idField = def.idFieldName ?? "ID";
              if (idField !== "ROWID") {
                const newId = realObj.getValue(idField);
                if (newId != null && newId !== 0 && newId !== "" && newId !== "0") {
                  selfFields.set(idField, newId);
                }
              }
              isNewFlag = false;
              pendingTypeSeeded.clear();
            } else {
              if (dirtySet.size === 0) return true;
              const realObj = coll._instantiate(Object.fromEntries(originalSnapshot), false);
              realObj._suppressOnchangeDispatch = true;
              for (const f of dirtySet) {
                if (isPrivateOrReservedProp(f)) continue;
                realObj.setValue(f, selfFields.get(f));
              }
              await realObj.save();
            }
            commitSnapshot();
            return true;
          })()),
          deleteObject: async () => {
            const coll = await resolveRealColl();
            if (!coll) return true;
            const realObj = coll._instantiate(Object.fromEntries(originalSnapshot), false);
            await realObj.deleteObject();
            return true;
          },
          refresh: async () => true,
          getDirty: () => dirtySet.size > 0,
          isNew: () => isNewFlag,
          getOldValue: (field) => originalSnapshot.get(field),
          getOwnerCollection: () => augmentedOwnerColl,
          // Padre = objeto que abrió la ficha (entrada previa del nav
          // stack). Paridad Android `getParent()`. Ver resolvePageParent.
          getParent: () => resolvePageParent(),
          ExecuteNode: runNodeByName,
          executeNode: runNodeByName,
          getPropertyNames: () => def.fields.map((f) => f.name),
          // Paridad Android IXoneObject.getObjectIndex(). El self de
          // página no es el DataObjectProxy real, así que exponemos el
          // índice desde el ObjectState restaurado. `ui.refreshContentRow`
          // ignora el índice en el MPA (re-render completo), así que el
          // requisito real es NO lanzar: 0 es correcto si no se conoce.
          getObjectIndex: () => typeof restoredObjectState?.index === "number" ? restoredObjectState.index : 0,
          getAllFields: () => Object.fromEntries(selfFields),
          getFieldPropertyValue: (field, attr) => {
            const cached = _propCache.get(`${field}:${attr}`);
            if (cached !== void 0) return cached;
            const fDef = def.fields.find((f) => f.name === field);
            return fDef ? fDef[attr] : void 0;
          },
          setFieldPropertyValue: (field, attr, v) => {
            const strVal = v == null ? null : String(v);
            if (strVal !== null && !strVal.startsWith("##FLD_")) {
              _propCache.set(`${field}:${attr}`, strVal);
            } else if (strVal === null) {
              _propCache.delete(`${field}:${attr}`);
            }
            if (_domSyncRef && strVal !== null) {
              _domSyncRef.patchPropAttribute(field, attr, strVal);
            }
            return v;
          },
          getVariable: () => void 0,
          setVariable: () => {
          },
          bind: () => {
          },
          unbind: () => {
          },
          contents: (nameOrIndex) => resolvePageContents(nameOrIndex),
          getContents: (nameOrIndex) => resolvePageContents(nameOrIndex),
          getContentsCount: () => pageContentsList().length
        },
        {
          get(target, prop) {
            if (typeof prop === "string" && !(prop in target)) {
              return selfFields.get(prop);
            }
            return target[prop];
          },
          set(_target, prop, value) {
            if (typeof prop === "string") {
              if (isPrivateOrReservedProp(prop)) {
                selfFields.set(prop, value);
                return true;
              }
              const old = selfFields.get(prop);
              selfFields.set(prop, value);
              pendingTypeSeeded.delete(prop);
              trackDirty(prop, value);
              if (_domSyncRef) {
                _domSyncRef.syncField(prop);
              }
              fireFieldOnchange(prop, old, value);
              return true;
            }
            return true;
          },
          has(target, prop) {
            if (typeof prop === "string" && selfFields.has(prop)) return true;
            return prop in target;
          }
        }
      );
    }
  } else {
    const selfFields = /* @__PURE__ */ new Map();
    if (restoredObjectState) {
      for (const [k, v] of Object.entries(restoredObjectState.fields)) {
        selfFields.set(k, isDehydratedDataObject(v) ? createDetachedParentObject(v, cloneResolver) : v);
      }
    }
    self = new Proxy(
      {
        getValue(field) {
          return selfFields.get(field);
        },
        setValue(field, value) {
          const old = selfFields.get(field);
          selfFields.set(field, value);
          if (_domSyncRef) _domSyncRef.syncField(field);
          fireFieldOnchange(field, old, value);
        },
        toJson() {
          return Object.fromEntries(selfFields);
        },
        contents: (nameOrIndex) => resolvePageContents(nameOrIndex),
        getContents: (nameOrIndex) => resolvePageContents(nameOrIndex),
        getContentsCount: () => pageContentsList().length,
        // Paridad Android IXoneObject.getObjectIndex() — ver rama con
        // definición. 0 si no hay índice restaurado.
        getObjectIndex: () => typeof restoredObjectState?.index === "number" ? restoredObjectState.index : 0
      },
      {
        get(target, prop) {
          if (typeof prop === "string" && !(prop in target)) {
            return selfFields.get(prop);
          }
          return target[prop];
        },
        set(_target, prop, value) {
          if (typeof prop === "string") {
            const old = selfFields.get(prop);
            selfFields.set(prop, value);
            if (_domSyncRef) _domSyncRef.syncField(prop);
            fireFieldOnchange(prop, old, value);
            return true;
          }
          return true;
        },
        has(target, prop) {
          if (typeof prop === "string" && selfFields.has(prop)) return true;
          return prop in target;
        }
      }
    );
    ThisDataColl = {
      getName() {
        return "";
      },
      async loadAll() {
      },
      getCurrentItem() {
        return null;
      }
    };
  }
  const domSync = createDomSync({
    getSelf() {
      return self;
    }
  });
  _domSyncRef = domSync;
  try {
    for (const entry of pageContentsList()) {
      const instanceName = entry.name.startsWith("@") ? entry.name.slice(1) : entry.name;
      const wrapper = dataLayer.getCollection(instanceName, entry.src);
      wrapper.setOwnerObject?.(self);
    }
  } catch {
  }
  function snapshotSelf() {
    const json = self.toJson();
    if (!json || Object.keys(json).length === 0) return null;
    const fieldProperties = _propCache.size > 0 ? Object.fromEntries(_propCache) : void 0;
    const seededFn = self._unassignedTypeSeeded;
    const typeSeeded = typeof seededFn === "function" ? seededFn.call(self) : [];
    return {
      collectionName: currentCollName,
      fields: json,
      dirtyFields: typeof self.getDirty === "function" && self.getDirty() ? [] : [],
      isNew: typeof self.isNew === "function" ? self.isNew() : true,
      index: 0,
      ...fieldProperties ? { fieldProperties } : {},
      ...Array.isArray(typeSeeded) && typeSeeded.length > 0 ? { typeSeeded } : {}
    };
  }
  function snapshotCollection() {
    return {
      collectionName: currentCollName,
      filter: typeof ThisDataColl.getFilter === "function" ? ThisDataColl.getFilter() ?? "" : "",
      sort: typeof ThisDataColl.getSort === "function" ? ThisDataColl.getSort() ?? "" : "",
      currentIndex: 0,
      count: typeof ThisDataColl.getCount === "function" ? ThisDataColl.getCount() ?? 0 : 0
    };
  }
  const isLayoutHosted = typeof document !== "undefined" && !!document.querySelector("[data-xone-router-slot]");
  const isSectionRoot = isLayoutHosted && (currentNavEntry?.rootSwitch === true || navStack.size() <= 1);
  const handleRootBack = (exitReplace = false) => {
    try {
      const w = window;
      if (typeof w.__xoneLayoutOnback === "function") {
        w.__xoneLayoutOnback();
        return;
      }
    } catch {
    }
    appData.exit(exitReplace ? { replace: true } : void 0);
  };
  const writeExitMarker = () => {
    try {
      if (restoredObjectState && typeof self.isNew === "function" && currentNavEntry?.collection) {
        const rowid = String(
          self.getValue?.("ROWID") ?? ""
        );
        if (rowid) {
          const stillNew = self.isNew();
          if (restoredObjectState.isNew === true && stillNew) {
            storage?.setItem(
              DISCARD_KEY,
              JSON.stringify({ coll: currentNavEntry.collection, rowid })
            );
          } else {
            const fields = {};
            try {
              const json = self.toJson();
              for (const [k, v] of Object.entries(json)) {
                const t = typeof v;
                if (v === null || t === "string" || t === "number" || t === "boolean") fields[k] = v;
              }
            } catch {
            }
            storage?.setItem(
              UPSERT_KEY,
              JSON.stringify({ coll: currentNavEntry.collection, rowid, fields })
            );
          }
        }
      }
    } catch {
    }
  };
  const ui = createUi({
    getPageSelf: () => self,
    macros,
    routeMap,
    domSync,
    navStack,
    stateStore,
    storage,
    snapshotSelf,
    snapshotCollection,
    getCollectionName: () => currentCollName,
    isSectionRoot: () => isSectionRoot,
    onRootBack: handleRootBack,
    writeExitMarker
  });
  _exitHandler = () => {
    const g = globalThis;
    const inRow = g.__xoneInRowEdit?.pop();
    if (inRow) {
      try {
        inRow.collapse();
      } catch {
      }
      return;
    }
    writeExitMarker();
    ui.back();
  };
  if (isSectionRoot && typeof window !== "undefined" && typeof history !== "undefined") {
    try {
      history.pushState({ __xoneRootTrap: true }, "", window.location.href);
    } catch {
    }
    const onPopState = () => {
      const hasOnback = typeof window.__xoneLayoutOnback === "function";
      if (hasOnback) {
        try {
          history.pushState({ __xoneRootTrap: true }, "", window.location.href);
        } catch {
        }
      }
      handleRootBack(!hasOnback);
    };
    window.addEventListener("popstate", onPopState);
  }
  resetNavGuard();
  if (typeof window !== "undefined") {
    window.addEventListener("pageshow", (e) => {
      if (e.persisted) {
        resetNavGuard();
        const freshMacros = stateStore.loadMacros();
        if (freshMacros) {
          for (const [k, v] of Object.entries(freshMacros)) {
            macros.set(k, v);
          }
        }
        const discardM = takeMarker(DISCARD_KEY);
        if (discardM?.coll && discardM.rowid) {
          dataLayer.discardNewItem(discardM.coll, discardM.rowid);
        }
        const upsertM = takeMarker(UPSERT_KEY);
        if (upsertM?.coll && upsertM.rowid && upsertM.fields) {
          dataLayer.upsertNewItem(upsertM.coll, upsertM.rowid, upsertM.fields);
          void dataLayer.refetchLockedCollections(upsertM.coll);
        }
      }
    });
  }
  if (currentNavEntry && currentNavEntry.scrollY > 0 && typeof window !== "undefined") {
    const targetScroll = currentNavEntry.scrollY;
    const restoreScroll = () => {
      requestAnimationFrame(() => {
        window.scrollTo(0, targetScroll);
      });
    };
    if (document.readyState === "complete") {
      restoreScroll();
    } else {
      window.addEventListener("load", restoreScroll, { once: true });
    }
  }
  let _signalLifecycleReady;
  const lifecycleReady = new Promise((resolve) => {
    _signalLifecycleReady = resolve;
  });
  const interactiveReady = lifecycleReady.then(
    () => whenPendingLoadsSettled()
  );
  let _resumeStateApplied = false;
  const applyResumeStateIfNeeded = () => {
    if (_resumeStateApplied) return;
    _resumeStateApplied = true;
    if (navDirection !== "back" || !restoredObjectState) return;
    for (const [k, v] of Object.entries(restoredObjectState.fields)) {
      try {
        self[k] = isDehydratedDataObject(v) ? createDetachedParentObject(v, cloneResolver) : v;
      } catch {
      }
    }
    const restoredProps = restoredObjectState.fieldProperties;
    if (restoredProps && typeof self.setFieldPropertyValue === "function") {
      for (const [key, value] of Object.entries(restoredProps)) {
        const sep = key.indexOf(":");
        if (sep <= 0) continue;
        const field = key.slice(0, sep);
        const attribute = key.slice(sep + 1);
        try {
          self.setFieldPropertyValue(field, attribute, value);
        } catch {
        }
      }
    }
  };
  return {
    self,
    This: self,
    ThisDataColl,
    ui,
    appData,
    $http: http,
    crypto: cryptoApi,
    clipboard: clipboardApi,
    console,
    Bundle,
    // WEAL-95: expone la dirección de navegación consumida arriba para que
    // el islands-runtime distinga back (restaurar pestaña activa de barras
    // manuales) de forward/entrada directa (no restaurar).
    navDirection,
    /**
     * #367 — rastro de navegación para `viewmode="breadcrumbview"`.
     *
     * Devuelve una FOTO inmutable con lo mínimo que el rastro necesita:
     * a qué coll corresponde cada salto y a qué URL volver. Se expone esto y
     * no el `navStack` entero a propósito — el rastro solo tiene que LEER, y
     * dar la pila mutable invitaría a que el JS de proyecto la manipule y
     * descuadre la navegación, que es estado delicado (ver WEAL-257).
     */
    // #367 — rastro para `viewmode="breadcrumbview"`. Foto inmutable: se
    // devuelven objetos nuevos, nunca la pila mutable.
    //
    // La entrada de LOGIN se descarta, y no es un detalle cosmético: la crea
    // este mismo createAppContext al cargar la página de acceso, queda en
    // `stack[0]`, y `truncateToRoot()` —lo que ejecuta abrir una sección
    // desde el menú— CONSERVA `stack[0]`. Es decir, sobrevive a todo. Sin
    // este filtro el rastro de cualquier pantalla empieza por "Login" y esa
    // miga es NAVEGABLE: un clic devuelve al usuario ya autenticado a la
    // pantalla de acceso.
    getNavTrail: () => {
      const login = (macros.get("LOGIN_COLLECTION") ?? "").trim();
      const visibles = login === "" ? navStack.getStack() : navStack.getStack().filter((e) => e.collection !== login);
      return visibles.map((e) => ({ id: e.id, collection: e.collection, url: e.url }));
    },
    macros,
    session,
    manifestPromise,
    domSync,
    lifecycleReady,
    // WEAL-196: barrera de interactividad (lifecycle + cargas iniciales
    // asentadas). El islands-runtime la espera antes de ejecutar un handler
    // de interacción → evita que un click temprano lea contents del shell
    // (p.ej. `WebMenuPadre`) todavía en carga.
    interactiveReady,
    // WEAL-130: al abrir el gate de render (bootstrap lo llama tras before-edit),
    // reaplica el snapshot restaurado en un 'resume' (back) para que el estado
    // local del `self` gane al reset incondicional del <before-edit> del proyecto.
    signalLifecycleReady: () => {
      applyResumeStateIfNeeded();
      _signalLifecycleReady();
    },
    // Hooks de selección del menú del shell para el islands-runtime: envolver
    // el `selecteditem` de un island del layout con begin/end permite al
    // overlay distinguir una selección del usuario (reset → single-select)
    // de una mutación de lifecycle (merge → carry-forward en drill-down).
    shellOverlay: shellOverlay ? {
      beginSelection: () => shellOverlay.beginSelection(),
      endSelection: () => shellOverlay.endSelection()
    } : void 0
  };
}

// src/lifecycleGate.ts
function shouldRunCreateLifecycle(self) {
  const maybe = self;
  if (maybe != null && typeof maybe.isNew === "function") {
    try {
      return maybe.isNew() !== false;
    } catch {
      return true;
    }
  }
  return true;
}
export {
  LOGIN_NOTICE_KEY,
  SessionLostError,
  WebCoreError,
  apiBaseSessionScope,
  buildRouteMap,
  consumeNavDirection,
  createAppContext,
  createAppData,
  createBundleCtor,
  createClipboardApi,
  createCryptoApi,
  createDataLayer,
  createDomSync,
  createHttp,
  createMacroStore,
  createNavStack,
  createSession,
  createStateStore,
  createUi,
  createWebCoreBackend,
  filterToSql,
  generateId,
  installDownloadDedup,
  isSessionBearerTarget,
  loginCollectionPath,
  resetNavGuard,
  resolveSessionExpiresAt,
  setNavDirection,
  shouldRunCreateLifecycle,
  sqlQuote
};
//# sourceMappingURL=index.js.map